import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { checklistQuestionAPI, checklistTemplateAPI, masterDataAPI } from "../services/api";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "./RaiseComplaint.css";
import "./ChecklistQuestionTreeManagement.css";

function normChecklistLabel(s) {
  return String(s ?? "").trim().toLowerCase();
}

const CHECKLIST_OPTION_BASED_CATEGORIES = [
  "multipleChoice",
  "radioButton",
  "dropdown",
  "checkbox",
  "groupCheckbox",
  "groupRadioButton",
];

const QUESTION_TYPE_CODE_TO_MASTER_NAME = {
  QT001: "Radio Button",
  QT002: "Label",
  QT003: "Check Box",
  QT004: "Dropdowns",
  QT005: "Special Text",
  QT006: "Multiple Choice",
  QT007: "Text Box",
  QT011: "Group Check Box",
  QT012: "Group Radio Button",
  QT013: "Level",
  QT014: "Level Image Upload",
  QT015: "Level Text Box",
};

/** Level Image Upload / Level Text Box — extra level text input above upload/text preview. */
const CHECKLIST_LEVEL_INPUT_TYPE_CODES = new Set(["QT014", "QT015"]);

function isChecklistLevelInputTypeCode(typeCode) {
  return CHECKLIST_LEVEL_INPUT_TYPE_CODES.has(String(typeCode || "").toUpperCase());
}

function isChecklistLevelInputCategory(category) {
  return category === "levelImage" || category === "levelTextBox";
}

function isChecklistLevelInputType(typeCode, questionTypesList = []) {
  if (isChecklistLevelInputTypeCode(typeCode)) return true;
  const codeUpper = String(typeCode || "").toUpperCase();
  const mappedName = QUESTION_TYPE_CODE_TO_MASTER_NAME[codeUpper];
  const name = mappedName || (questionTypesList || []).find((qt) => (qt.code || "").toUpperCase() === codeUpper)?.name || "";
  const category = resolveChecklistQuestionTypeCategory(name, typeCode);
  return isChecklistLevelInputCategory(category);
}

function isChecklistOptionBasedCategory(category) {
  return CHECKLIST_OPTION_BASED_CATEGORIES.includes(category);
}

function isChecklistOptionBasedTypeName(name) {
  const n = String(name || "").toLowerCase();
  if (n.includes("yes") && n.includes("no")) return false;
  if (n.includes("group") && (n.includes("check box") || n.includes("checkbox"))) return true;
  if (n.includes("group") && (n.includes("radio button") || n.includes("radio"))) return true;
  return (
    n.includes("dropdown") ||
    n.includes("multiple") ||
    n.includes("choice") ||
    n.includes("radio") ||
    n.includes("checkbox") ||
    n.includes("check box")
  );
}

function resolveChecklistQuestionTypeCategory(name, typeCode = "") {
  const n = String(name || "").toLowerCase().trim();
  const code = String(typeCode || "").toLowerCase();
  if (!n) return "text";

  if (n.includes("group") && (n.includes("check box") || n.includes("checkbox"))) {
    return "groupCheckbox";
  }
  if (n.includes("group") && (n.includes("radio button") || n.includes("radio"))) {
    return "groupRadioButton";
  }
  if (n === "radio button" || n.includes("radio button")) {
    return "radioButton";
  }
  if (n.includes("radio") && !n.includes("multiple") && !n.includes("choice")) {
    return "radioButton";
  }
  if ((n.includes("multiple") || n.includes("choice")) && !n.includes("radio button") && !n.includes("radio")) {
    if (
      code.includes("material") ||
      code.includes("dropdown") ||
      code.includes("multiple-choice") ||
      n === "multiple choice"
    ) {
      return "dropdown";
    }
    return "multipleChoice";
  }
  if (n.includes("dropdown")) return "dropdown";
  if (n.includes("checkbox") || n.includes("check box")) return "checkbox";
  if (
    n.includes("level") &&
    (n.includes("image") || n.includes("picture") || n.includes("upload"))
  ) {
    return "levelImage";
  }
  if (n.includes("level") && (n.includes("text box") || n.includes("textbox"))) {
    return "levelTextBox";
  }
  if (n === "level" || n.includes("label")) return "label";
  if (n.includes("special text")) return "specialText";
  if (n.includes("text box") || n.includes("textbox")) return "textBox";
  if (n.includes("yes") || n.includes("no")) return "yesNo";
  if (n.includes("number") || n.includes("numeric")) return "number";
  if (n.includes("date") || n.includes("datepicker") || n.includes("date picker")) return "date";
  if (
    n.includes("image") ||
    n.includes("picture") ||
    n.includes("image upload") ||
    n.includes("file upload")
  ) {
    return "image";
  }
  if (n.includes("text")) return "text";
  return "text";
}

function mapChecklistQuestionTypeCodeToName(typeCode, questionTypesList = []) {
  if (!typeCode) return "Text Box";
  const codeUpper = String(typeCode || "").toUpperCase();
  if (QUESTION_TYPE_CODE_TO_MASTER_NAME[codeUpper]) {
    return QUESTION_TYPE_CODE_TO_MASTER_NAME[codeUpper];
  }
  const type = questionTypesList.find((qt) => (qt.code || "").toUpperCase() === codeUpper);
  return (type && type.name && type.name.trim()) || "Text Box";
}

function isChecklistOptionBasedQuestionTypeName(questionType) {
  return (
    questionType === "Dropdowns" ||
    questionType === "Multiple Choice" ||
    questionType === "Radio Button" ||
    questionType === "Check Box" ||
    questionType === "Group Check Box" ||
    questionType === "Group Radio Button"
  );
}

const GENERAL_SR_CATEGORY = {
  code: "0000",
  name: "All",
  description: "All",
};

function mapSrTypesFromApi(data) {
  return (Array.isArray(data) ? data : [])
    .map((st) => {
      const code =
        st.SRTypeCode ||
        st.srTypeCode ||
        st.SRTYPECODE ||
        st.SrTypeCode ||
        "";
      return {
        code,
        name: code === "0000" ? "All" : st.Name || st.name || st.NAME || "",
        description: st.Description || st.description || st.DESCRIPTION || "",
      };
    })
    .filter((t) => t.code && t.name);
}

function withGeneralSrCategoryOption(types) {
  const mapped = Array.isArray(types) ? types : [];
  if (mapped.some((t) => String(t.code || "").trim() === GENERAL_SR_CATEGORY.code)) {
    return mapped;
  }
  return [GENERAL_SR_CATEGORY, ...mapped];
}

function resolveDefaultQuestionTypeCode(questionTypesList = []) {
  const types = Array.isArray(questionTypesList) ? questionTypesList : [];
  const matchName = (target) =>
    types.find((t) => String(t.name || "").trim().toLowerCase() === target)?.code || "";
  return matchName("level") || matchName("label") || types[0]?.code || "QT002";
}

function resolveDefaultOptionTypeCode(questionTypesList = []) {
  const types = Array.isArray(questionTypesList) ? questionTypesList : [];
  const matchName = (target) =>
    types.find((t) => String(t.name || "").trim().toLowerCase() === target)?.code || "";
  return matchName("label") || "QT002";
}

function buildChecklistNewQuestionDraft(questionTypesList = [], overrides = {}) {
  return {
    srTypeCode: GENERAL_SR_CATEGORY.code,
    questionTypeCode: resolveDefaultQuestionTypeCode(questionTypesList),
    isMandatory: false,
    ...overrides,
  };
}

function parseSrTypeCodes(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((c) => String(c).trim()).filter(Boolean);
  }
  return String(raw)
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);
}

function serializeSrTypeCodes(codes) {
  const unique = [...new Set(parseSrTypeCodes(codes))];
  return unique.length ? unique.join(",") : "";
}

function formatSrCategoryLabel(value, srTypes) {
  const selected = parseSrTypeCodes(value);
  if (!selected.length) return "SR Category";
  const labels = selected.map((code) => {
    const match = (srTypes || []).find((t) => String(t.code) === code);
    return match?.name || code;
  });
  return labels.join(", ");
}

function getSrCategoryIcon(name) {
  const n = String(name || "").toLowerCase();
  if (n === "all" || n.includes("general")) return "📌";
  if (n.includes("quality")) return "⭐";
  if (n.includes("packaging") || n.includes("dispatch")) return "📦";
  if (n.includes("installation")) return "🔧";
  if (n.includes("commercial")) return "💼";
  return "🏷️";
}

function parseAnsTextOptionLabels(parentQuestion) {
  const raw = parentQuestion?.questionAnsText ?? parentQuestion?.QuestionAnsText ?? "";
  if (!raw || !String(raw).trim()) return [];
  return String(raw)
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function isChecklistQuestionOptionBased(parentQuestion, questionTypes) {
  const code = parentQuestion?.questionTypeCode || parentQuestion?.QuestionTypeCode || "";
  if (!code || !questionTypes?.length) return false;
  const qt = questionTypes.find(
    (t) => (t.code || "").toLowerCase() === String(code).toLowerCase()
  );
  if (!qt) return false;
  return isChecklistOptionBasedTypeName(qt.name || "");
}

function childMatchesAnsTextOptions(child, optionLabels) {
  if (!optionLabels.length) return false;
  const cond = child?.conditionalAnswerValue ?? child?.ConditionalAnswerValue ?? "";
  const qText = child?.questionText ?? child?.QuestionText ?? "";
  return optionLabels.some((opt) => {
    const nO = normChecklistLabel(opt);
    return nO && (nO === normChecklistLabel(cond) || nO === normChecklistLabel(qText));
  });
}

function filterChecklistNonOptionChildren(children, parentQuestion, questionTypes) {
  if (!children?.length) return [];

  // Multiple Choice / Radio / Dropdown / Checkbox: rows stored as DB children with no
  // ConditionalAnswerValue are the option-builder rows. They must never also render as
  // nested "NEW QUESTION -05-01" blocks. After save, QuestionAnsText often becomes a
  // comma-joined copy of those texts; splitting on commas then fails to match long
  // questions (commas inside the text), so the legacy branch below would incorrectly
  // return all children — duplicate UI. Hide the whole set whenever every direct child
  // has an empty conditional (the normal MC / Radio option layout).
  if (isChecklistQuestionOptionBased(parentQuestion, questionTypes)) {
    const allDirectEmptyConditional = children.every(
      (c) => String(c?.conditionalAnswerValue ?? c?.ConditionalAnswerValue ?? "").trim() === ""
    );
    if (allDirectEmptyConditional) {
      return [];
    }
  }

  const optionLabels = parseAnsTextOptionLabels(parentQuestion);
  if (optionLabels.length > 0) {
    const rawAnsTextNorm = normChecklistLabel(
      parentQuestion?.questionAnsText ?? parentQuestion?.QuestionAnsText ?? ""
    );
    return children.filter((c) => {
      const condNorm = normChecklistLabel(c?.conditionalAnswerValue ?? c?.ConditionalAnswerValue ?? "");
      const qTextNorm = normChecklistLabel(c?.questionText ?? c?.QuestionText ?? "");
      const isRawAnsMatch = Boolean(rawAnsTextNorm) && (condNorm === rawAnsTextNorm || qTextNorm === rawAnsTextNorm);
      return !(childMatchesAnsTextOptions(c, optionLabels) || isRawAnsMatch);
    });
  }
  if (
    isChecklistQuestionOptionBased(parentQuestion, questionTypes) &&
    children.every(
      (c) => String(c?.conditionalAnswerValue ?? c?.ConditionalAnswerValue ?? "").trim() !== ""
    )
  ) {
    return [];
  }
  // If parent is option-based but QuestionAnsText is empty, we may have persisted options
  // as direct child rows (option nodes) instead of comma-separated QuestionAnsText.
  // In that case, hide those option-nodes from the normal nested-children area so the UI
  // renders them inside the options section (same structure as "create" flow).
  if (isChecklistQuestionOptionBased(parentQuestion, questionTypes)) {
    const hasAnsText = parseAnsTextOptionLabels(parentQuestion).length > 0;
    const hasAnyConditional = children.some(
      (c) => String(c?.conditionalAnswerValue ?? c?.ConditionalAnswerValue ?? "").trim() !== ""
    );
    const optionLikeChildren = children.filter(
      (c) => String(c?.conditionalAnswerValue ?? c?.ConditionalAnswerValue ?? "").trim() === ""
    );
    if (!hasAnsText && !hasAnyConditional && optionLikeChildren.length > 0) {
      return children.filter(
        (c) => String(c?.conditionalAnswerValue ?? c?.ConditionalAnswerValue ?? "").trim() !== ""
      );
    }
  }
  return [...children];
}

/**
 * Edit-mode full save deletes all template questions, then recreates the tree via POST /tree.
 * Payload nodes still carry old QuestionCode / opt_saved_* ids from the UI — re-posting those
 * can make sp_CreateChecklistQuestionTreeNS fail (500). Clear codes so the API assigns fresh
 * codes (same as add-mode inserts through save-question).
 */
function stripQuestionCodesForFullTreeRecreate(nodes) {
  if (!Array.isArray(nodes)) return;
  for (const node of nodes) {
    if (!node || typeof node !== "object") continue;
    node.questionCode = null;
    if (Array.isArray(node.children) && node.children.length > 0) {
      stripQuestionCodesForFullTreeRecreate(node.children);
    }
  }
}

/** Flat list → nested tree (for copy-draft hydration). */
function buildFlatQuestionTree(flatQuestions) {
  if (!Array.isArray(flatQuestions) || flatQuestions.length === 0) return [];

  const questionMap = new Map();
  const roots = [];
  const mapKey = (code) => String(code ?? "").trim().toUpperCase();

  flatQuestions.forEach((q) => {
    const rawCode = q.questionCode || q.QuestionCode || q.QuestionCODE;
    const k = mapKey(rawCode);
    if (!k) return;
    questionMap.set(k, { ...q, children: [] });
  });

  const seenParentChild = new Set();
  flatQuestions.forEach((q) => {
    const rawCode = q.questionCode || q.QuestionCode || q.QuestionCODE;
    const selfKey = mapKey(rawCode);
    const question = selfKey ? questionMap.get(selfKey) : null;
    if (!question) return;

    const parentRaw =
      q.parentQuestionCode || q.ParentQuestionCode || q.ParentQuestionCODE;
    const parentKey = mapKey(parentRaw);

    if (parentRaw && parentKey && questionMap.has(parentKey)) {
      const linkKey = `${parentKey}>${selfKey}`;
      if (seenParentChild.has(linkKey)) return;
      seenParentChild.add(linkKey);
      questionMap.get(parentKey).children.push(question);
    } else {
      const rootKey = `__root__>${selfKey}`;
      if (seenParentChild.has(rootKey)) return;
      seenParentChild.add(rootKey);
      roots.push(question);
    }
  });

  const sortByOrder = (nodes) => {
    nodes.sort((a, b) => {
      const orderA = a.displayOrder || a.DisplayOrder || 0;
      const orderB = b.displayOrder || b.DisplayOrder || 0;
      return orderA - orderB;
    });
    nodes.forEach((node) => {
      if (node.children?.length) sortByOrder(node.children);
    });
  };

  sortByOrder(roots);
  return roots;
}

/**
 * Copy flow: load source questions into newQuestions draft (no DB save).
 * Uses same draft keys as add-mode so existing Save uses tree-upsert unchanged.
 */
function buildDraftQuestionsFromSourceTree(nodes, parentDraftKey = null, draftMap = {}, counters = { root: 0 }) {
  if (!Array.isArray(nodes)) return draftMap;

  nodes.forEach((node, index) => {
    const draftKey = parentDraftKey
      ? `child_${parentDraftKey}_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 8)}`
      : `root_${counters.root++}`;

    draftMap[draftKey] = {
      questionCode: null,
      QuestionCode: null,
      questionTypeCode: node.questionTypeCode || node.QuestionTypeCode || node.QuestionTypeCODE || "QT001",
      srTypeCode: node.srTypeCode || node.SRTypeCode || null,
      parentQuestionCode: parentDraftKey || null,
      questionText: node.questionText || node.QuestionText || "",
      questionAnsText: node.questionAnsText ?? node.QuestionAnsText ?? null,
      displayOrder: node.displayOrder ?? node.DisplayOrder ?? index + 1,
      isMandatory: node.isMandatory ?? node.IsMandatory ?? true,
      isConditional: node.isConditional ?? node.IsConditional ?? false,
      conditionalAnswerValue: node.conditionalAnswerValue ?? node.ConditionalAnswerValue ?? null,
      level: parentDraftKey ? 1 : 0,
      index,
    };

    const children = node.children || [];
    if (children.length > 0) {
      buildDraftQuestionsFromSourceTree(children, draftKey, draftMap, counters);
    }
  });

  return draftMap;
}

const SR_CATEGORY_MENU_MAX_HEIGHT = 220;

const SrCategoryMultiSelect = ({ value, srTypes, onChange, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const selected = parseSrTypeCodes(value);

  const updateMenuPosition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < SR_CATEGORY_MENU_MAX_HEIGHT + 8 && rect.top > spaceBelow;
    const top = openUp
      ? Math.max(8, rect.top - SR_CATEGORY_MENU_MAX_HEIGHT - 4)
      : rect.bottom + 4;
    setMenuStyle({
      position: "fixed",
      top,
      left: rect.left,
      width: Math.max(rect.width, 180),
      maxHeight: SR_CATEGORY_MENU_MAX_HEIGHT,
      zIndex: 1060,
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      const target = e.target;
      if (wrapRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggleCode = (code) => {
    const next = selected.includes(code)
      ? selected.filter((c) => c !== code)
      : [...selected, code];
    onChange(serializeSrTypeCodes(next));
  };

  const handleTriggerClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    updateMenuPosition();
    setOpen(true);
  };

  const menu =
    open && menuStyle
      ? createPortal(
          <div
            ref={menuRef}
            className="sr-category-multiselect-menu sr-category-multiselect-menu-portal"
            style={menuStyle}
            role="listbox"
            aria-multiselectable="true"
            onMouseDown={(e) => e.stopPropagation()}
          >
            {(srTypes || []).map((type) => (
              <label key={type.code} className="sr-category-multiselect-option">
                <input
                  type="checkbox"
                  checked={selected.includes(type.code)}
                  onChange={() => toggleCode(type.code)}
                />
                <span>
                  {getSrCategoryIcon(type.name)} {type.name}
                </span>
              </label>
            ))}
          </div>,
          document.body
        )
      : null;

  return (
    <div className={`sr-category-multiselect ${className}`} ref={wrapRef}>
      <button
        type="button"
        className={`form-select form-select-sm sr-category-multiselect-trigger text-start ${
          selected.length ? "has-value" : ""
        }`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleTriggerClick}
        title={formatSrCategoryLabel(value, srTypes)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="sr-category-multiselect-label">
          {formatSrCategoryLabel(value, srTypes)}
        </span>
      </button>
      {menu}
    </div>
  );
};

// Inline Editable Question Component
const EditableQuestion = ({
  question,
  questionTypes,
  srTypes = [],
  level,
  templateCode,
  onSave,
  onDelete,
  onAddChild,
  isNew = false,
  parentIndex = null,
  childIndex = null,
  newQuestions = {}, // Pass newQuestions to access option children
  onNewQuestionsUpdate = null, // Callback to update newQuestions
  renderQuestionTree = null, // Pass renderQuestionTree for n-level nesting
  node = null, // Pass the node for accessing children
  isNodeExpanded = null, // Function to check if node is expanded
  toggleNode = null, // Function to toggle node expansion
  questionKey = null, // Key in newQuestions for this question
  nestedChildren = null, // Same DOM for create & saved: child question-blocks inside parent question-block
  draggable = false,
  onDragStartBlock = null,
  onDragOverBlock = null,
  onDropBlock = null,
  isDragOver = false,
}) => {
  const [questionText, setQuestionText] = useState(
    question?.questionText || question?.QuestionText || ""
  );
  const [questionTypeCode, setQuestionTypeCode] = useState(
    question?.questionTypeCode ||
      question?.QuestionTypeCode ||
      (isNew ? resolveDefaultQuestionTypeCode(questionTypes) : "")
  );
  const [srTypeCode, setSrTypeCode] = useState(
    question?.srTypeCode ||
      question?.SRTypeCode ||
      (isNew ? GENERAL_SR_CATEGORY.code : "")
  );
  const [isMandatory, setIsMandatory] = useState(
    question?.isMandatory !== undefined ? question.isMandatory : (question?.IsMandatory !== undefined ? question.IsMandatory : false)
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isContentExpanded, setIsContentExpanded] = useState(true);
  const [optionDragState, setOptionDragState] = useState({ sourceId: "", overId: "" });
  const [levelInputText, setLevelInputText] = useState(() => {
    const typeCode = question?.questionTypeCode || question?.QuestionTypeCode || "";
    if (!isChecklistLevelInputTypeCode(typeCode)) return "";
    return String(question?.questionAnsText ?? question?.QuestionAnsText ?? "").trim();
  });

  const createOptionNode = (data = {}) => {
    const typeCode =
      data.typeCode ||
      data.questionTypeCode ||
      resolveDefaultOptionTypeCode(questionTypes);
    const srTypeCode =
      data.srTypeCode || data.SRTypeCode || GENERAL_SR_CATEGORY.code;
    return {
      id: data.id || `opt_${Math.random().toString(36).substr(2, 9)}_${Date.now()}`,
      text: data.text || "",
      typeCode,
      srTypeCode,
      isMandatory: data.isMandatory !== undefined ? data.isMandatory : false,
      levelInputText:
        data.levelInputText ??
        (isChecklistLevelInputTypeCode(typeCode)
          ? String(data.questionAnsText ?? data.QuestionAnsText ?? "").trim()
          : ""),
      children: (data.children || []).map((child) => createOptionNode(child)),
    };
  };

  const createOptionNodeFromSavedQuestion = (savedQ) => {
    const code = savedQ?.questionCode || savedQ?.QuestionCode || "";
    const text = savedQ?.questionText || savedQ?.QuestionText || "";
    const typeCode = savedQ?.questionTypeCode || savedQ?.QuestionTypeCode || "";
    const levelInputText = isChecklistLevelInputTypeCode(typeCode)
      ? String(savedQ?.questionAnsText ?? savedQ?.QuestionAnsText ?? "").trim()
      : "";
    const srType = savedQ?.srTypeCode || savedQ?.SRTypeCode || "";
    const isMan =
      savedQ?.isMandatory !== undefined
        ? savedQ.isMandatory
        : savedQ?.IsMandatory !== undefined
          ? savedQ.IsMandatory
          : true;
    const childQs = savedQ?.children || [];
    return createOptionNode({
      id: code ? `opt_saved_${code}` : undefined,
      text,
      typeCode,
      srTypeCode: srType,
      isMandatory: isMan,
      levelInputText,
      children: Array.isArray(childQs) ? childQs.map((c) => createOptionNodeFromSavedQuestion(c)) : [],
    });
  };

  // Generate a unique identifier for this question instance (memoized to stay consistent)
  const questionId = useMemo(() => {
    return question?.questionCode || question?.QuestionCode || `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, [question?.questionCode, question?.QuestionCode]);
  // Prefer explicit draft key (root_*/child_*); else persist edits under saved QuestionCODE so SAVE always merges.
  const draftStorageKey = useMemo(() => {
    if (questionKey) return questionKey;
    const c = String(question?.questionCode || question?.QuestionCode || "").trim();
    return c || null;
  }, [questionKey, question?.questionCode, question?.QuestionCode]);
  const hasHydratedSavedOptionsRef = useRef(false);
  const lastOptionChildrenSyncKeyRef = useRef("");

  useEffect(() => {
    hasHydratedSavedOptionsRef.current = false;
    lastOptionChildrenSyncKeyRef.current = "";
  }, [questionId]);

  // Helper function to update newQuestions when question data changes
  const updateNewQuestions = (updates) => {
    console.log(`[updateNewQuestions] Called with:`, {
      isNew,
      hasOnNewQuestionsUpdate: !!onNewQuestionsUpdate,
      questionKey,
      draftStorageKey,
      updates,
      currentQuestion: newQuestions[draftStorageKey],
    });
    if (onNewQuestionsUpdate && draftStorageKey) {
      const updated = { ...newQuestions };
      if (!updated[draftStorageKey]) {
        // In edit-mode, saved questions are not pre-seeded into newQuestions.
        // Create a lightweight draft so option/child additions are tracked for SAVE.
        updated[draftStorageKey] = {
          __existingParentDraft: true,
          questionCode: question?.questionCode || question?.QuestionCode || questionKey || null,
          parentQuestionCode:
            question?.parentQuestionCode ||
            question?.ParentQuestionCode ||
            question?.ParentQuestionCODE ||
            null,
          questionText: questionText || question?.questionText || question?.QuestionText || "",
          questionTypeCode: questionTypeCode || question?.questionTypeCode || question?.QuestionTypeCode || "",
          srTypeCode: srTypeCode || question?.srTypeCode || question?.SRTypeCode || "",
          isMandatory:
            isMandatory !== undefined
              ? isMandatory
              : question?.isMandatory !== undefined
                ? question.isMandatory
                : question?.IsMandatory !== undefined
                  ? question.IsMandatory
                  : true,
        };
      }

      const shouldMarkDirty = [
        "questionText",
        "QuestionText",
        "questionTypeCode",
        "QuestionTypeCode",
        "srTypeCode",
        "SRTypeCode",
        "isMandatory",
        "IsMandatory",
        "questionAnsText",
        "QuestionAnsText",
        "conditionalAnswerValue",
        "ConditionalAnswerValue",
        "optionsData",
        "__deletedOptionCodes",
      ].some((k) => Object.prototype.hasOwnProperty.call(updates || {}, k));
      const trackedDirtyKeys = [
        "questionText",
        "QuestionText",
        "questionTypeCode",
        "QuestionTypeCode",
        "srTypeCode",
        "SRTypeCode",
        "isMandatory",
        "IsMandatory",
        "questionAnsText",
        "QuestionAnsText",
        "conditionalAnswerValue",
        "ConditionalAnswerValue",
        "optionsData",
        "__deletedOptionCodes",
      ];
      const changedKeys = Object.keys(updates || {}).filter((k) => trackedDirtyKeys.includes(k));

      updated[draftStorageKey] = {
        ...updated[draftStorageKey],
        ...updates,
        __dirty: Boolean(updated[draftStorageKey]?.__dirty || shouldMarkDirty),
        __dirtyFields: [
          ...new Set([...(updated[draftStorageKey]?.__dirtyFields || []), ...changedKeys]),
        ],
      };
      console.log(`[updateNewQuestions] Updating question ${draftStorageKey}:`, updated[draftStorageKey]);
      onNewQuestionsUpdate(updated);
    } else {
      console.warn(`[updateNewQuestions] Conditions not met:`, {
        hasOnNewQuestionsUpdate: !!onNewQuestionsUpdate,
        questionKey,
        draftStorageKey,
      });
    }
  };

  const questionType = questionTypes.find(
    (qt) => qt.code === questionTypeCode
  );

  // Initialize options based on question type
  const initializeOptions = () => {
    if (question?.questionAnsText || question?.QuestionAnsText) {
      const ansText = question.questionAnsText || question.QuestionAnsText;
      return ansText.split(",").map((opt) => opt.trim()).filter((opt) => opt);
    }
    // Initialize with Yes/No if question type is Yes/No
    if (questionType?.name?.toLowerCase().includes("yes") ||
      questionType?.name?.toLowerCase().includes("no")) {
      return ["Yes", "No"];
    }
    return [];
  };

  const [options, setOptions] = useState(() => {
    const opts = initializeOptions();
    return opts.map((opt) =>
      createOptionNode(typeof opt === "string" ? { text: opt } : opt)
    );
  });

  // Track expanded/collapsed state for option-children
  const [expandedOptionChildren, setExpandedOptionChildren] = useState(new Set());

  const toggleOptionChildren = (optionId) => {
    setExpandedOptionChildren(prev => {
      const newSet = new Set(prev);
      if (newSet.has(optionId)) {
        newSet.delete(optionId);
      } else {
        newSet.add(optionId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    setSrTypeCode(
      question?.srTypeCode ||
        question?.SRTypeCode ||
        (isNew ? GENERAL_SR_CATEGORY.code : "")
    );
  }, [question?.srTypeCode, question?.SRTypeCode, questionId, isNew]);

  useEffect(() => {
    if (!isNew || !onNewQuestionsUpdate || !draftStorageKey || !questionTypes?.length) return;

    const nextSr = parseSrTypeCodes(srTypeCode).length ? srTypeCode : GENERAL_SR_CATEGORY.code;
    const nextType = questionTypeCode || resolveDefaultQuestionTypeCode(questionTypes);
    const updates = {};

    if (!parseSrTypeCodes(srTypeCode).length) {
      setSrTypeCode(nextSr);
      updates.srTypeCode = nextSr;
    }
    if (!questionTypeCode) {
      setQuestionTypeCode(nextType);
      updates.questionTypeCode = nextType;
    }
    if (Object.keys(updates).length > 0) {
      updateNewQuestions(updates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, questionTypes, draftStorageKey]);

  // Update options when question type changes
  useEffect(() => {
    if (questionType) {
      const name = questionType.name?.toLowerCase() || "";
      if ((name.includes("yes") || name.includes("no")) && options.length === 0) {
        setOptions([createOptionNode({ text: "Yes" }), createOptionNode({ text: "No" })]);
      } else if (!isChecklistOptionBasedTypeName(name) && options.length > 0 && !name.includes("yes") && !name.includes("no")) {
        setOptions([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionTypeCode]);

  useEffect(() => {
    if (!isChecklistLevelInputType(questionTypeCode, questionTypes)) return;
    const fromQuestion = String(question?.questionAnsText ?? question?.QuestionAnsText ?? "").trim();
    if (fromQuestion && fromQuestion !== levelInputText) {
      setLevelInputText(fromQuestion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.questionAnsText, question?.QuestionAnsText, questionTypeCode]);

  useEffect(() => {
    if (!onNewQuestionsUpdate || !draftStorageKey) return;
    if (!isChecklistLevelInputType(questionTypeCode, questionTypes)) return;
    updateNewQuestions({ questionAnsText: levelInputText.trim() || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelInputText, questionTypeCode, draftStorageKey]);

  const renderLevelInputTextbox = (value, onChange, keySuffix = "main") => (
    <input
      key={`level_input_${keySuffix}`}
      type="text"
      className="form-control form-control-sm mb-2"
      placeholder="Enter level text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );

  // Determine question type category
  const getQuestionTypeCategory = () => {
    let name = "";
    let typeCode = "";

    if (questionType) {
      name = questionType.name || "";
      typeCode = questionType.code || "";
    } else {
      name = question?.questionTypeName || question?.QuestionTypeName || "";
      typeCode = question?.questionTypeCode || question?.QuestionTypeCode || "";

      if (!name && questionTypeCode) {
        const foundType = questionTypes.find((qt) => qt.code === questionTypeCode);
        if (foundType) {
          name = foundType.name || "";
          typeCode = foundType.code || "";
        }
      }
    }

    return resolveChecklistQuestionTypeCategory(name, typeCode);
  };

  // Recalculate category when questionTypeCode or questionType changes
  const questionTypeCategory = useMemo(() => {
    return getQuestionTypeCategory();
  }, [questionTypeCode, questionType, questionTypes, question]);
  
  const isOptionBased = isChecklistOptionBasedCategory(questionTypeCategory);

  const persistedQuestionCode = question?.questionCode || question?.QuestionCode || "";

  const optionChildrenSyncKey = useMemo(() => {
    if (!node?.children?.length) return "";
    const childSig = (c) => {
      const code = c?.questionCode ?? c?.QuestionCode ?? "";
      const kids = Array.isArray(c?.children)
        ? [...c.children]
            .sort(
              (a, b) =>
                (a.displayOrder ?? a.DisplayOrder ?? 0) - (b.displayOrder ?? b.DisplayOrder ?? 0)
            )
            .map(childSig)
            .join("\u003e")
        : "";
      return [
        code,
        c?.conditionalAnswerValue ?? c?.ConditionalAnswerValue ?? "",
        c?.questionText ?? c?.QuestionText ?? "",
        c?.questionTypeCode ?? c?.QuestionTypeCode ?? "",
        c?.displayOrder ?? c?.DisplayOrder ?? 0,
        kids,
      ].join("\t");
    };
    return [...node.children]
      .sort(
        (a, b) =>
          (a.displayOrder ?? a.DisplayOrder ?? 0) - (b.displayOrder ?? b.DisplayOrder ?? 0)
      )
      .map(childSig)
      .join("|");
  }, [node]);

  useEffect(() => {
    if (!isOptionBased) return;
    if (!(persistedQuestionCode || !isNew)) return;
    if (!isNew) {
      if (lastOptionChildrenSyncKeyRef.current !== optionChildrenSyncKey) {
        lastOptionChildrenSyncKeyRef.current = optionChildrenSyncKey;
        hasHydratedSavedOptionsRef.current = false;
      }
      if (hasHydratedSavedOptionsRef.current) return;
    }

    const orderedChildren = [...(node?.children || [])].sort(
      (a, b) => (a.displayOrder ?? a.DisplayOrder ?? 0) - (b.displayOrder ?? b.DisplayOrder ?? 0)
    );

    // If QuestionAnsText is empty and children have no ConditionalAnswerValue,
    // treat direct children as persisted option-nodes (saved via recursive QuestionDto flow).
    const directOptionChildren = orderedChildren.filter(
      (c) => String(c?.conditionalAnswerValue ?? c?.ConditionalAnswerValue ?? "").trim() === ""
    );
    const labelsFromAns = parseAnsTextOptionLabels(question);
    let labels = [];
    // Prefer persisted children as source of truth when available.
    // This avoids breaking option labels that contain commas.
    if (
      orderedChildren.length > 0 &&
      orderedChildren.every(
        (c) => String(c?.conditionalAnswerValue ?? c?.ConditionalAnswerValue ?? "").trim() !== ""
      )
    ) {
      labels = orderedChildren.map((c) =>
        String(c.conditionalAnswerValue || c.ConditionalAnswerValue || "").trim()
      );
    } else if (orderedChildren.length === 0) {
      labels = [...labelsFromAns];
    }
    if (labels.length === 0 && directOptionChildren.length > 0) {
      setOptions((prev) => {
        const next = directOptionChildren.map((c) => {
          const opt = createOptionNodeFromSavedQuestion(c);
          const prevMatch = prev.find(
            (o) => normChecklistLabel(o.text) === normChecklistLabel(opt.text)
          );
          return prevMatch ? { ...prevMatch, ...opt } : opt;
        });
        return next;
      });
      if (!isNew) hasHydratedSavedOptionsRef.current = true;
      return;
    }
    if (labels.length === 0) return;

    const optionTreeSig = (o) => {
      if (!o) return "";
      const kids = (o.children || []).map(optionTreeSig).join(",");
      return `${normChecklistLabel(o.text)}|${String(o.typeCode || "")}|${String(o.srTypeCode || "")}|${kids}`;
    };

    setOptions((prev) => {
      const next = labels.map((text) => {
        const nt = normChecklistLabel(text);
        const child = orderedChildren.find((c) => {
          const nc = normChecklistLabel(c.conditionalAnswerValue ?? c.ConditionalAnswerValue);
          const nqt = normChecklistLabel(c.questionText ?? c.QuestionText);
          return nt && (nc === nt || nqt === nt);
        });
        const displayText = (child && (child.questionText || child.QuestionText)) || text || "";
        const prevMatch =
          prev.find((o) => normChecklistLabel(o.text) === nt) ||
          (child
            ? prev.find(
                (o) =>
                  normChecklistLabel(o.text) ===
                  normChecklistLabel(child.questionText ?? child.QuestionText ?? "")
              )
            : null);
        const typeCode =
          child?.questionTypeCode ||
          child?.QuestionTypeCode ||
          prevMatch?.typeCode ||
          resolveDefaultOptionTypeCode(questionTypes);
        const srType =
          child?.srTypeCode ||
          child?.SRTypeCode ||
          prevMatch?.srTypeCode ||
          GENERAL_SR_CATEGORY.code;
        const isMan =
          child?.isMandatory !== undefined
            ? child.isMandatory
            : child?.IsMandatory !== undefined
              ? child.IsMandatory
              : prevMatch?.isMandatory !== undefined
                ? prevMatch.isMandatory
                : true;
        // When QuestionAnsText drives the option list, still attach nested rows saved under
        // each option (e.g. Dropdown under MCQ → Labels). The branch above without labels used
        // createOptionNodeFromSavedQuestion; this branch previously dropped children entirely.
        if (child) {
          const fromSaved = createOptionNodeFromSavedQuestion(child);
          const merged = {
            ...fromSaved,
            text: displayText || fromSaved.text || text,
            typeCode:
              typeCode ||
              fromSaved.typeCode ||
              resolveDefaultOptionTypeCode(questionTypes),
            srTypeCode: srType || fromSaved.srTypeCode || GENERAL_SR_CATEGORY.code,
            isMandatory: isMan,
          };
          if (prevMatch) {
            return { ...merged, id: prevMatch.id };
          }
          return merged;
        }
        if (prevMatch) {
          return {
            ...prevMatch,
            text: displayText || prevMatch.text || text,
            typeCode: typeCode || prevMatch.typeCode || resolveDefaultOptionTypeCode(questionTypes),
            srTypeCode: srType || prevMatch.srTypeCode || GENERAL_SR_CATEGORY.code,
            isMandatory: isMan,
          };
        }
        return createOptionNode({
          text: displayText || text,
          typeCode,
          srTypeCode: srType,
          isMandatory: isMan,
        });
      });
      const same =
        prev.length === next.length &&
        prev.every((p, i) => {
          const q = next[i];
          return (
            normChecklistLabel(p.text) === normChecklistLabel(q.text) &&
            (p.typeCode || "") === (q.typeCode || "") &&
            !!p.isMandatory === !!q.isMandatory &&
            optionTreeSig(p) === optionTreeSig(q)
          );
        });
      return same ? prev : next;
    });
    if (!isNew) hasHydratedSavedOptionsRef.current = true;
  }, [
    isNew,
    isOptionBased,
    persistedQuestionCode,
    question?.questionAnsText,
    question?.QuestionAnsText,
    optionChildrenSyncKey,
  ]);

  useEffect(() => {
    if (!isOptionBased) return;
    setExpandedOptionChildren((prev) => {
      const n = new Set(prev);
      let changed = false;
      options.forEach((o) => {
        if (o.typeCode && !n.has(o.id)) {
          n.add(o.id);
          changed = true;
        }
      });
      return changed ? n : prev;
    });
  }, [options, isOptionBased]);

  // Helper function to get the question indicator icon/component based on category and type name
  // This function works for both parent and child questions
  const getQuestionIndicator = (category, typeName = null, selectedType = null) => {
    // Get type name from parameter, selectedType, or questionType
    let name = "";
    let typeCode = "";
    
    if (typeName) {
      name = typeName.toLowerCase().trim();
    } else if (selectedType) {
      name = (selectedType.name || "").toLowerCase().trim();
      typeCode = (selectedType.code || "").toLowerCase();
    } else {
      name = (questionType?.name || "").toLowerCase().trim();
      typeCode = (questionType?.code || "").toLowerCase();
    }
    
    switch (category) {
      case "radioButton":
      case "groupRadioButton":
        return <input type="radio" className="form-check-input" checked={false} disabled />;
      case "yesNo":
        return <input type="radio" className="form-check-input" checked={false} disabled />;
      case "checkbox":
      case "groupCheckbox":
        return <input type="checkbox" className="form-check-input" checked={false} disabled />;
      case "multipleChoice":
        return <input type="checkbox" className="form-check-input" checked={false} disabled />;
      case "dropdown":
        // IMPORTANT: Check for "Multiple Choice" FIRST before checking for "dropdown"
        // Check if it's "Multiple Choice" type - check name first, then type code
        const isMultipleChoiceType = name === "multiple choice" || 
                                     name === "multiplechoice" ||
                                     (name.includes("multiple") && name.includes("choice") && !name.includes("radio") && !name.includes("dropdown")) ||
                                     typeCode.includes("multiple-choice") ||
                                     typeCode.includes("material");
        
        if (isMultipleChoiceType) {
          return <span style={{ fontSize: "12px", fontWeight: "bold", color: "#333" }}>a) b)</span>;
        }
        
        // Regular dropdown (Dropdown, Dropdowns, etc.) - check name explicitly
        // This should catch "dropdown", "dropdowns", "dropdown list", etc.
        // Only show dropdown icon if name explicitly contains "dropdown" and NOT "multiple choice"
        const isDropdownType = name.includes("dropdown") && 
                              !name.includes("multiple") && 
                              !name.includes("choice");
        
        if (isDropdownType) {
          return <span style={{ fontSize: "16px" }}>📋</span>;
        }
        
        // Default for dropdown category - if name is empty or doesn't match, default to dropdown icon
        // This handles edge cases where the type might not be properly identified
        return <span style={{ fontSize: "16px" }}>📋</span>;
      case "label":
        return <span style={{ fontSize: "16px" }}>🏷️</span>;
      case "specialText":
        return <span style={{ fontSize: "16px" }}>📄</span>;
      case "textBox":
        return <span style={{ fontSize: "16px" }}>📄</span>;
      case "number":
        return <span style={{ fontSize: "16px" }}>🔢</span>;
      case "date":
        return <span style={{ fontSize: "16px" }}>📅</span>;
      case "image":
      case "levelImage":
        return <span style={{ fontSize: "16px" }}>🖼️</span>;
      case "levelTextBox":
        return <span style={{ fontSize: "16px" }}>📄</span>;
      case "text":
      default:
        return <span style={{ fontSize: "16px" }}>📝</span>;
    }
  };

  // Update newQuestions with questionAnsText and optionsData whenever options change
  // (needed for both add mode and edit mode snapshot save).
  useEffect(() => {
    if (!onNewQuestionsUpdate || !draftStorageKey || !isOptionBased) return undefined;

    const timer = window.setTimeout(() => {
      const optionsText = options
        .filter((opt) => opt.text && opt.text.trim())
        .map((opt) => opt.text)
        .join(",");

      const questionAnsText = optionsText || null;
      const optionsData = options.filter((opt) => opt.text && opt.text.trim());

      updateNewQuestions({
        questionAnsText,
        optionsData,
      });
    }, 120);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, isOptionBased, questionTypeCode, draftStorageKey, onNewQuestionsUpdate]);

  const getTypeIcon = (typeName) => {
    const name = typeName?.toLowerCase() || "";
    if (name === "level") return "🏷️";
    if (name.includes("level") && (name.includes("image") || name.includes("upload") || name.includes("picture"))) {
      return "🖼️";
    }
    if (name.includes("level") && (name.includes("text box") || name.includes("textbox"))) {
      return "📄";
    }
    if (name.includes("group") && (name.includes("check box") || name.includes("checkbox"))) return "☑";
    if (name.includes("group") && name.includes("radio")) return "🔘";
    // Check for "Radio Button" first and return radio button icon
    if (name.includes("radio button") || name === "radio button") return "🔘";
    // Check for "radio" (but not if it contains "multiple" or "choice")
    if (name.includes("radio") && !name.includes("multiple") && !name.includes("choice")) return "🔘";
    // Check for "Multiple Choice" (dropdown-like) and return "a) b)" style icon
    // This must come before the general "multiple" or "choice" check
    if (name === "multiple choice") return "a)";
    // Check for "Multiple Choice" (original name that was renamed to Radio Button) - shouldn't reach here
    if (name.includes("multiple") || name.includes("choice")) return "☑️";
    if (name.includes("dropdown")) return "📋";
    if (name.includes("checkbox") || name.includes("check box")) return "☐";
    if (name.includes("label")) return "🏷️";
    if (name.includes("special text")) return "📄";
    if (name.includes("text box") || name.includes("textbox")) return "📄";
    if (name.includes("yes") || name.includes("no")) return "✅";
    if (name.includes("text")) return "📝";
    if (name.includes("number") || name.includes("numeric")) return "🔢";
    if (name.includes("date") || name.includes("datepicker") || name.includes("date picker")) return "📅";
    if (
      name.includes("image") ||
      name.includes("picture") ||
      name.includes("image upload") ||
      name.includes("file upload")
    )
      return "🖼️";
    return "🖼️"; // Default icon like in the image
  };

  const handleSave = async (showSuccess = false) => {
    if (!questionText.trim()) {
      if (showSuccess) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "Question text is required",
        });
      }
      return;
    }

    if (!questionTypeCode) {
      if (showSuccess) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "Please select a question type",
        });
      }
      return;
    }

    if (parseSrTypeCodes(srTypeCode).length === 0) {
      if (showSuccess) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "Please select at least one SR Category",
        });
      }
      return;
    }

    setIsSaving(true);
    try {
      const questionData = {
        templateCode: templateCode,
        questionTypeCode: questionTypeCode,
        srTypeCode: srTypeCode || null,
        parentQuestionCode: question?.parentQuestionCode || question?.ParentQuestionCode || null,
        questionText: questionText,
        questionAnsText: isOptionBased
          ? options.filter((opt) => opt.text && opt.text.trim()).map(opt => opt.text).join(",")
          : isChecklistLevelInputType(questionTypeCode, questionTypes)
            ? (levelInputText.trim() || null)
          : questionTypeCategory === "yesNo"
            ? "Yes,No"
            : null,
        displayOrder: question?.displayOrder || question?.DisplayOrder || 0,
        isMandatory: isMandatory,
        isConditional: question?.isConditional ?? (level > 0 || question?.optionText),
        conditionalAnswerValue: question?.conditionalAnswerValue || question?.ConditionalAnswerValue || question?.optionText || null,
      };

      let response;
      if (isNew || !question?.questionCode) {
        response = await checklistQuestionAPI.createQuestion(questionData);
      } else {
        response = await checklistQuestionAPI.updateQuestion(
          question.questionCode || question.QuestionCode,
          questionData
        );
      }

      if (response?.success) {
        if (isNew && showSuccess) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Question created successfully",
            timer: 1500,
            showConfirmButton: false,
          });
        }
        onSave();
      } else {
        throw new Error(response?.message || "Failed to save question");
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to save question",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Don't auto-save on blur - wait for final save button
  const handleBlur = () => {
    // Auto-save disabled - save only on final save button
  };

  const removeDraftQuestionCascade = () => {
    if (!onNewQuestionsUpdate) return;
    const selfCode =
      question?.questionCode || question?.QuestionCode || question?.QuestionCODE || null;
    const selfText = String(question?.questionText || question?.QuestionText || "").trim();
    const selfType = String(question?.questionTypeCode || question?.QuestionTypeCode || "").trim();
    const selfParent = String(
      question?.parentQuestionCode || question?.ParentQuestionCode || question?.ParentQuestionCODE || ""
    ).trim();
    const removeRecursively = (source, targetKeys) => {
      const next = { ...source };
      const queue = [...targetKeys];
      const removed = new Set();

      while (queue.length > 0) {
        const currentKey = queue.shift();
        if (!currentKey || removed.has(currentKey)) continue;
        removed.add(currentKey);
        delete next[currentKey];

        Object.keys(next).forEach((k) => {
          const q = next[k] || {};
          const parentCode =
            q.parentQuestionCode || q.ParentQuestionCode || q.ParentQuestionCODE || null;
          if (parentCode && String(parentCode) === String(currentKey)) {
            queue.push(k);
          }
          if (
            k.startsWith(`child_${currentKey}`) ||
            k.startsWith(`option_child_${currentKey}_`)
          ) {
            queue.push(k);
          }
        });
      }
      return next;
    };

    // Primary target key + robust fallback match by payload (for cases where questionKey is missing/mismatched).
    const fallbackMatchedKeys = Object.keys(newQuestions).filter((k) => {
      const q = newQuestions[k];
      if (!q) return false;
      if (q === question) return true;
      const qText = String(q.questionText || q.QuestionText || "").trim();
      const qType = String(q.questionTypeCode || q.QuestionTypeCode || "").trim();
      const qParent = String(q.parentQuestionCode || q.ParentQuestionCode || q.ParentQuestionCODE || "").trim();
      return (
        selfText &&
        qText === selfText &&
        qType === selfType &&
        qParent === selfParent
      );
    });
    const primaryTargets = [...new Set([draftStorageKey, questionKey, ...fallbackMatchedKeys].filter(Boolean))];
    if (primaryTargets.length === 0) return;

    let updated = removeRecursively(newQuestions, primaryTargets);
    if (selfCode) {
      // Also remove any draft rows keyed by/linked to the persisted code.
      updated = removeRecursively(updated, [String(selfCode)]);
    }
    onNewQuestionsUpdate(updated);
  };

  const handleDelete = async () => {
    const hasChildQuestionsForDeleteGuard = () => {
      const persistedSelfCode =
        question?.questionCode || question?.QuestionCode || question?.QuestionCODE || null;
      const currentKeys = new Set(
        [
          draftStorageKey,
          questionKey,
          persistedSelfCode ? String(persistedSelfCode) : null,
          question?.questionCode || question?.QuestionCode || null,
          node?.questionCode || node?.QuestionCode || null,
        ]
          .filter(Boolean)
          .map((v) => String(v).trim())
      );

      // Consider only non-option saved children as blockers.
      // Option labels under Dropdown/Radio/etc. should not prevent parent delete.
      const nodeChildren = Array.isArray(node?.children) ? node.children : [];
      const questionChildren = Array.isArray(question?.children) ? question.children : [];
      const nodeNonOptionChildren = filterChecklistNonOptionChildren(
        nodeChildren,
        node || question || {},
        questionTypes
      );
      const questionNonOptionChildren = filterChecklistNonOptionChildren(
        questionChildren,
        question || node || {},
        questionTypes
      );
      const hasSavedTreeChildren =
        nodeNonOptionChildren.length > 0 || questionNonOptionChildren.length > 0;

      const hasDraftChildren = Object.entries(newQuestions || {}).some(([k, q]) => {
        const key = String(k || "").trim();
        const parentCode = String(
          q?.parentQuestionCode || q?.ParentQuestionCode || q?.ParentQuestionCODE || ""
        ).trim();
        if (parentCode && currentKeys.has(parentCode)) return true;
        for (const baseKey of currentKeys) {
          if (
            key === `child_${baseKey}` ||
            key.startsWith(`child_${baseKey}_`) ||
            key.startsWith(`option_child_${baseKey}_`)
          ) {
            return true;
          }
        }
        return false;
      });
      return hasSavedTreeChildren || hasDraftChildren;
    };

    if (hasChildQuestionsForDeleteGuard()) {
      await Swal.fire({
        icon: "warning",
        title: "Delete Not Allowed",
        text: "Parent question cannot be deleted while child questions/options exist. Please delete all children first.",
        confirmButtonText: "OK",
      });
      return;
    }

    if (isNew) {
      removeDraftQuestionCascade();
      return;
    }

    const result = await Swal.fire({
      title: "Delete Question?",
      text: "This will also delete all child questions. Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        // Existing question delete is deferred to SAVE.
        // Parent component handles marking codes for deletion.
        onDelete?.();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Failed to delete question",
        });
      }
    }
  };

  const updateOptionNode = (list, targetId, updater) =>
    list.map((opt) => {
      if (opt.id === targetId) {
        return updater(opt);
      }
      if (opt.children && opt.children.length > 0) {
        return { ...opt, children: updateOptionNode(opt.children, targetId, updater) };
      }
      return opt;
    });

  const removeOptionNode = (list, targetId) =>
    list
      .map((opt) => ({
        ...opt,
        children: removeOptionNode(opt.children || [], targetId),
      }))
      .filter((opt) => opt.id !== targetId);

  const handleAddOption = () => {
    // Validate that the question has a type selected before allowing options
    if (!questionTypeCode || questionTypeCode.trim() === "") {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please select a question type before adding options",
        confirmButtonText: "OK"
      });
      return;
    }

    if (parseSrTypeCodes(srTypeCode).length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please select at least one SR Category before adding options",
        confirmButtonText: "OK"
      });
      return;
    }
    
    // Validate that the question has mandatory dropdown selected
    if (isMandatory === undefined) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please select mandatory option before adding options",
        confirmButtonText: "OK"
      });
      return;
    }
    
    // Check if there are existing options without types
    if (options.length > 0) {
      const optionsWithoutType = options.some((opt) => {
        return !opt.typeCode || opt.typeCode.trim() === "";
      });
      
      if (optionsWithoutType) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "Please select a question type for all existing options before adding new options",
          confirmButtonText: "OK"
        });
        return;
      }
      
      // Check if there are existing options without mandatory dropdown
      const optionsWithoutMandatory = options.some((opt) => {
        return opt.isMandatory === undefined;
      });
      
      if (optionsWithoutMandatory) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "Please select mandatory option for all existing options before adding new options",
          confirmButtonText: "OK"
        });
        return;
      }

      // Check if there are existing options without text (except for Label and Special Text)
      const optionsWithoutText = options.some((opt) => {
        const optionText = opt.text || "";
        const hasText = optionText.trim().length > 0;
        
        // If option has no text, check if it's Label, Special Text, or Text Box type
        if (!hasText && opt.typeCode) {
          const optionTypeCategory = getOptionTypeCategory(opt.typeCode);
          // Allow Label, Special Text, and Text Box to have no text
          if (optionTypeCategory === "label" || optionTypeCategory === "specialText" || optionTypeCategory === "textBox") {
            return false; // This option is exempt from text requirement
          }
        }
        
        // For all other types, text is required
        return !hasText;
      });
      
      if (optionsWithoutText) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "Please enter text for all existing options before adding new options (Label, Special Text, and Text Box are exempt)",
          confirmButtonText: "OK"
        });
        return;
      }
    }
    
    setOptions((prev) => [...prev, createOptionNode()]);
  };

  const findOptionNode = (list, targetId) => {
    for (const opt of list) {
      if (opt.id === targetId) {
        return opt;
      }
      if (opt.children && opt.children.length > 0) {
        const found = findOptionNode(opt.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const findOptionSubtreeById = (list, targetId) => {
    for (const opt of list) {
      if (opt.id === targetId) return opt;
      if (opt.children && opt.children.length > 0) {
        const found = findOptionSubtreeById(opt.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const collectSavedOptionCodes = (optNode) => {
    const codes = [];
    const walk = (node) => {
      if (!node) return;
      const id = String(node.id || "");
      if (id.startsWith("opt_saved_")) {
        const code = id.replace("opt_saved_", "").trim();
        if (code) codes.push(code);
      }
      const kids = Array.isArray(node.children) ? node.children : [];
      kids.forEach(walk);
    };
    walk(optNode);
    return [...new Set(codes)];
  };

  const handleAddSubOption = (parentId) => {
    // Find the parent option
    const parentOption = findOptionNode(options, parentId);
    
    if (!parentOption) {
      console.error(`Parent option with id ${parentId} not found`);
      return;
    }
    
    // Validate parent option has a type
    if (!parentOption.typeCode || parentOption.typeCode.trim() === "") {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please select a question type for the parent option before adding sub-options",
        confirmButtonText: "OK"
      });
      return;
    }
    
    // Validate parent option has mandatory dropdown selected
    if (parentOption.isMandatory === undefined) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please select mandatory option for the parent option before adding sub-options",
        confirmButtonText: "OK"
      });
      return;
    }
    
    // Check if there are existing sub-options without types
    if (parentOption.children && parentOption.children.length > 0) {
      const subOptionsWithoutType = parentOption.children.some((child) => {
        return !child.typeCode || child.typeCode.trim() === "";
      });
      
      if (subOptionsWithoutType) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "Please select a question type for all existing sub-options before adding new sub-options",
          confirmButtonText: "OK"
        });
        return;
      }
      
      // Check if there are existing sub-options without mandatory dropdown
      const subOptionsWithoutMandatory = parentOption.children.some((child) => {
        return child.isMandatory === undefined;
      });
      
      if (subOptionsWithoutMandatory) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "Please select mandatory option for all existing sub-options before adding new sub-options",
          confirmButtonText: "OK"
        });
        return;
      }

      // Check if there are existing sub-options without text (except for Label and Special Text)
      const subOptionsWithoutText = parentOption.children.some((child) => {
        const optionText = child.text || "";
        const hasText = optionText.trim().length > 0;
        
        // If sub-option has no text, check if it's Label, Special Text, or Text Box type
        if (!hasText && child.typeCode) {
          const optionTypeCategory = getOptionTypeCategory(child.typeCode);
          // Allow Label, Special Text, and Text Box to have no text
          if (
            optionTypeCategory === "label" ||
            optionTypeCategory === "specialText" ||
            optionTypeCategory === "textBox" ||
            optionTypeCategory === "levelImage" ||
            optionTypeCategory === "levelTextBox"
          ) {
            return false; // This sub-option is exempt from text requirement
          }
        }
        
        // For all other types, text is required
        return !hasText;
      });
      
      if (subOptionsWithoutText) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "Please enter text for all existing sub-options before adding new sub-options (Label, Special Text, and Text Box are exempt)",
          confirmButtonText: "OK"
        });
        return;
      }
    }
    
    setOptions((prev) =>
      updateOptionNode(prev, parentId, (opt) => ({
        ...opt,
        children: [...(opt.children || []), createOptionNode()],
      }))
    );
    // Automatically expand the parent when adding a child
    setExpandedOptionChildren(prev => new Set([...prev, parentId]));
  };

  const handleUpdateOption = (optionId, value) => {
    setOptions((prev) =>
      updateOptionNode(prev, optionId, (opt) => ({ ...opt, text: value }))
    );
  };

  const handleUpdateOptionLevelInput = (optionId, value) => {
    setOptions((prev) =>
      updateOptionNode(prev, optionId, (opt) => ({ ...opt, levelInputText: value }))
    );
  };

  const handleOptionTypeChange = (optionId, typeCode) => {
    setOptions((prev) =>
      updateOptionNode(prev, optionId, (opt) => {
        const updated = { ...opt, typeCode };

        // Find the question type by code
        const selectedType = questionTypes.find((qt) => qt.code === typeCode);

        if (selectedType) {
          const name = selectedType.name?.toLowerCase() || "";

          // If Yes/No type and no children exist, initialize with "Yes" and "No"
          if ((name.includes("yes") || name.includes("no")) && (!opt.children || opt.children.length === 0)) {
            updated.children = [
              createOptionNode({ text: "Yes" }),
              createOptionNode({ text: "No" })
            ];
            // Automatically expand when Yes/No children are initialized
            setExpandedOptionChildren(prev => new Set([...prev, optionId]));
          } else {
            // For non-option-based types, auto-expand to show the input UI
            const isOptionBased = isChecklistOptionBasedTypeName(name);
            const isLevelInputType = isChecklistLevelInputCategory(
              resolveChecklistQuestionTypeCategory(name, typeCode)
            );
            if (
              (!isOptionBased && !name.includes("yes") && !name.includes("no")) ||
              isLevelInputType
            ) {
              setExpandedOptionChildren(prev => new Set([...prev, optionId]));
            }
          }
          // For all other types, keep existing children (don't clear them)
          // This allows any type to have children of any type, just like the main question-block
        }

        return updated;
      })
    );
  };

  const handleOptionMandatoryChange = (optionId, isMandatory) => {
    setOptions((prev) =>
      updateOptionNode(prev, optionId, (opt) => ({ ...opt, isMandatory }))
    );
  };

  const handleOptionSrTypeChange = (optionId, nextSrTypeCode) => {
    setOptions((prev) =>
      updateOptionNode(prev, optionId, (opt) => ({ ...opt, srTypeCode: nextSrTypeCode }))
    );
  };

  const handleDeleteOption = (optionId) => {
    // Track removed saved options so final save can persist deletions in DB.
    const targetNode = findOptionSubtreeById(options, optionId);
    const deletedSavedCodes = collectSavedOptionCodes(targetNode);
    if (deletedSavedCodes.length > 0) {
      const existingDeleted = Array.isArray(newQuestions?.[draftStorageKey]?.__deletedOptionCodes)
        ? newQuestions[draftStorageKey].__deletedOptionCodes
        : [];
      updateNewQuestions({
        __deletedOptionCodes: [...new Set([...existingDeleted, ...deletedSavedCodes])],
      });
    }
    setOptions((prev) => removeOptionNode(prev, optionId));
  };

  const moveInArray = (arr, from, to) => {
    if (!Array.isArray(arr)) return arr;
    const next = [...arr];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
  };

  const reorderOptionSiblings = (list, sourceId, targetId) => {
    if (!Array.isArray(list) || !sourceId || !targetId || sourceId === targetId) {
      return { next: list, changed: false, savedSiblingCodes: [] };
    }

    const sourceIndex = list.findIndex((o) => o?.id === sourceId);
    const targetIndex = list.findIndex((o) => o?.id === targetId);
    if (sourceIndex >= 0 && targetIndex >= 0) {
      const reorderedList = moveInArray(list, sourceIndex, targetIndex);
      const savedSiblingCodes = reorderedList
        .map((o) => String(o?.id || ""))
        .filter((id) => id.startsWith("opt_saved_"))
        .map((id) => id.replace("opt_saved_", "").trim())
        .filter(Boolean);
      return {
        next: reorderedList,
        changed: sourceIndex !== targetIndex,
        savedSiblingCodes:
          savedSiblingCodes.length === reorderedList.length && savedSiblingCodes.length > 1
            ? savedSiblingCodes
            : [],
      };
    }

    let changed = false;
    let savedSiblingCodes = [];
    const next = list.map((opt) => {
      if (!Array.isArray(opt?.children) || opt.children.length === 0) return opt;
      const result = reorderOptionSiblings(opt.children, sourceId, targetId);
      if (!result.changed) return opt;
      changed = true;
      if (result.savedSiblingCodes.length > 1) {
        savedSiblingCodes = result.savedSiblingCodes;
      }
      return { ...opt, children: result.next };
    });

    return { next, changed, savedSiblingCodes };
  };

  const handleOptionDragStart = (e, optionId) => {
    e.stopPropagation();
    setOptionDragState({ sourceId: optionId, overId: "" });
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", optionId);
    } catch (_) {
      // no-op: some browsers may restrict dataTransfer in synthetic drags
    }
  };

  const handleOptionDragOver = (e, optionId) => {
    if (!optionDragState.sourceId || optionDragState.sourceId === optionId) return;
    e.preventDefault();
    e.stopPropagation();
    setOptionDragState((prev) =>
      prev.overId === optionId ? prev : { ...prev, overId: optionId }
    );
  };

  const handleOptionDrop = async (e, targetId) => {
    e.preventDefault();
    e.stopPropagation();
    const sourceId = optionDragState.sourceId;
    if (!sourceId || sourceId === targetId) {
      setOptionDragState({ sourceId: "", overId: "" });
      return;
    }
    let reorderedSavedSiblingCodes = [];
    setOptions((prev) => {
      const result = reorderOptionSiblings(prev, sourceId, targetId);
      reorderedSavedSiblingCodes = result.savedSiblingCodes || [];
      return result.changed ? result.next : prev;
    });
    if (reorderedSavedSiblingCodes.length > 1) {
      try {
        await checklistQuestionAPI.reorder(templateCode, reorderedSavedSiblingCodes);
      } catch (error) {
        console.error("Failed to persist option reorder:", error);
      }
    }
    setOptionDragState({ sourceId: "", overId: "" });
  };

  const handleOptionDragEnd = (e) => {
    e.stopPropagation();
    setOptionDragState({ sourceId: "", overId: "" });
  };

  // Helper function to get type category for an option's selected type
  const getOptionTypeCategory = (typeCode) => {
    if (!typeCode) return null;
    const selectedType = questionTypes.find((qt) => qt.code === typeCode);
    if (!selectedType) return null;
    return resolveChecklistQuestionTypeCategory(selectedType.name || "", selectedType.code || "");
  };

  // Render input UI for option-children based on selected type
  const renderOptionChildrenInput = (option) => {
    if (!option.typeCode) return null;

    const typeCategory = getOptionTypeCategory(option.typeCode);
    if (!typeCategory) return null;
    
    // Get the selected type to check if it's "Multiple Choice"
    const selectedType = questionTypes.find((qt) => qt.code === option.typeCode);
    const isMultipleChoice = selectedType && (selectedType.name || "").toLowerCase().trim() === "multiple choice";

    const isOptionBased = isChecklistOptionBasedCategory(typeCategory);

    // For option-based types, render the option tree for this option's children
    if (isOptionBased) {
      const optionChildren = option.children || [];

      return (
        <div className="option-children-input ms-3 mt-2">
          <div className="question-input-group">
            <div className="question-indicator">
              {getQuestionIndicator(typeCategory, selectedType?.name, selectedType)}
            </div>
            <div className="question-input-wrapper">
              <div className="ms-2 mt-2">
                {optionChildren.map((child, index) => renderOptionNode(child, 0, [index + 1], typeCategory))}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // For other types, show the appropriate input UI
    return (
      <div className="option-children-input ms-3 mt-2">
        <div className="question-input-group">
          <div className="question-indicator">
            {getQuestionIndicator(typeCategory, selectedType?.name, selectedType)}
          </div>
          <div className="question-input-wrapper">
            {typeCategory === "yesNo" && (
              <div className="ms-2 mt-2 d-flex gap-3">
                <div className="form-check">
                  <input type="radio" className="form-check-input" checked={false} disabled />
                  <label className="form-check-label">Yes</label>
                </div>
                <div className="form-check">
                  <input type="radio" className="form-check-input" checked={false} disabled />
                  <label className="form-check-label">No</label>
                </div>
              </div>
            )}

            {typeCategory === "number" && (
              <input
                type="number"
                className="form-control form-control-sm ms-2 mt-2"
                placeholder="Enter number"
                style={{ maxWidth: 200 }}
                disabled
              />
            )}

            {typeCategory === "date" && (
              <input
                type="date"
                className="form-control form-control-sm ms-2 mt-2"
                style={{ maxWidth: 200 }}
                disabled
              />
            )}

            {typeCategory === "specialText" && (
              <textarea
                className="form-control form-control-sm ms-2 mt-2"
                rows={3}
                placeholder="Enter special text"
                disabled
              />
            )}

            {typeCategory === "levelImage" &&
              renderLevelInputTextbox(
                option.levelInputText || "",
                (val) => handleUpdateOptionLevelInput(option.id, val),
                option.id
              )}

            {typeCategory === "levelTextBox" &&
              renderLevelInputTextbox(
                option.levelInputText || "",
                (val) => handleUpdateOptionLevelInput(option.id, val),
                option.id
              )}

            {typeCategory === "image" && (
              <div className="ms-2 mt-2">
                <label className="btn btn-outline-primary btn-sm" style={{ pointerEvents: 'none', opacity: 0.6 }}>
                  Upload Image
                  <input type="file" hidden accept="image/*" disabled />
                </label>
              </div>
            )}

            {typeCategory === "levelImage" && (
              <div className="ms-2 mt-2">
                <label className="btn btn-outline-primary btn-sm" style={{ pointerEvents: 'none', opacity: 0.6 }}>
                  Upload Image
                  <input type="file" hidden accept="image/*" disabled />
                </label>
              </div>
            )}

            {typeCategory === "textBox" && (
              <textarea
                className="form-control form-control-sm ms-2 mt-2"
                rows={3}
                placeholder="Enter text"
                disabled
              />
            )}

            {typeCategory === "levelTextBox" && (
              <textarea
                className="form-control form-control-sm ms-2 mt-2"
                rows={3}
                placeholder="Enter text"
                disabled
              />
            )}

            {typeCategory === "label" && (
              <p className="small ms-2 mt-2 text-muted fst-italic mb-0">
                (Label - No input required)
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderOptionNode = (option, depth = 0, indexPath = [], parentTypeCategory = null) => {
    const displayNumber = indexPath.join(".");

    const hasChildren = option.children && option.children.length > 0;
    const canHaveChildren = option.typeCode || hasChildren;
    const isExpanded = expandedOptionChildren.has(option.id);

    // Use parent option's type category if available, otherwise use question's type category
    const currentTypeCategory = parentTypeCategory || questionTypeCategory;
    
    // Check if this is "Multiple Choice" type (dropdown category with Multiple Choice name)
    // Check if the question type is "Multiple Choice" - this applies to all options under a Multiple Choice question
    const isMultipleChoiceType = currentTypeCategory === "dropdown" && questionType && 
      (questionType.name || "").toLowerCase().trim() === "multiple choice";
    
    // Convert index to alphabetical label (a, b, c, etc.)
    const getAlphabeticalLabel = (index) => {
      if (index < 1) return String.fromCharCode(97); // 'a' if index is 0 or less
      return String.fromCharCode(96 + index); // 'a' = 97, 'b' = 98, etc.
    };
    
    // Get the first index for alphabetical conversion (use the option's position in its parent's children)
    const firstIndex = indexPath[0] || 1;
    const alphabeticalLabel = isMultipleChoiceType ? `${getAlphabeticalLabel(firstIndex)})` : null;

    return (
      <div key={option.id} className="option-item" style={{ marginLeft: depth * 20 }}>
        <div
          className={`option-row ${optionDragState.overId === option.id ? "question-block-drag-over" : ""}`}
          draggable
          onDragStart={(e) => handleOptionDragStart(e, option.id)}
          onDragOver={(e) => handleOptionDragOver(e, option.id)}
          onDrop={(e) => handleOptionDrop(e, option.id)}
          onDragEnd={handleOptionDragEnd}
        >
          <div className="d-flex align-items-center gap-1">
            {canHaveChildren && (
              <button
                type="button"
                className="btn btn-toggle-option-children p-0 border-0 bg-transparent"
                onClick={() => toggleOptionChildren(option.id)}
                title={isExpanded ? "Collapse children" : "Expand children"}
                style={{ fontSize: '16px', lineHeight: '1', cursor: 'pointer', color: '#666', width: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                {isExpanded ? '−' : '+'}
              </button>
            )}
            {currentTypeCategory === "dropdown" && isMultipleChoiceType && alphabeticalLabel ? (
              <div className="option-number" title={alphabeticalLabel} style={{ 
                backgroundColor: '#007bff', 
                color: 'white', 
                borderRadius: '50%', 
                width: '24px', 
                height: '24px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: '12px', 
                fontWeight: 'bold' 
              }}>{alphabeticalLabel}</div>
            ) : currentTypeCategory === "dropdown" ? (
              <div className="option-number" title={displayNumber}>{displayNumber}</div>
            ) : null}
            {(currentTypeCategory === "multipleChoice" ||
              currentTypeCategory === "checkbox" ||
              currentTypeCategory === "groupCheckbox") && (
                <input type="checkbox" className="form-check-input" checked={true} disabled />
              )}
            {(currentTypeCategory === "radioButton" ||
              currentTypeCategory === "groupRadioButton") && (
                <input type="radio" className="form-check-input" checked={true} disabled />
              )}
          </div>
          <input
            type="text"
            className="form-control form-control-sm option-input"
            placeholder={isMultipleChoiceType && alphabeticalLabel ? `Option ${alphabeticalLabel}` : `Option ${displayNumber}`}
            value={option.text}
            onChange={(e) => handleUpdateOption(option.id, e.target.value)}
          />
          <button
            type="button"
            className="btn btn-cancel-user funbtns btn-fun-icon"
            onClick={() => handleDeleteOption(option.id)}
            title="Delete option"
          >
            ×
          </button>

          <div className="d-flex align-items-center gap-2 ms-2">
            <div className="question-type-select">
              <SrCategoryMultiSelect
                value={option.srTypeCode || ""}
                srTypes={srTypes}
                onChange={(next) => handleOptionSrTypeChange(option.id, next)}
              />
            </div>

            <div className="question-type-select">
              <select
                className="form-select form-select-sm"
                value={option.typeCode || ""}
                onChange={(e) => handleOptionTypeChange(option.id, e.target.value)}
              >
                <option value="">Select Type</option>
                {questionTypes.map((type) => (
                  <option key={type.code} value={type.code}>
                    {getTypeIcon(type.name)} {type.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Mandatory Dropdown for Options/Child Level */}
            <div className="question-type-select">
              <select
                className="form-select form-select-sm"
                value={option.isMandatory !== undefined ? (option.isMandatory ? "mandatory" : "notMandatory") : "notMandatory"}
                onChange={(e) => handleOptionMandatoryChange(option.id, e.target.value === "mandatory")}
              >
                <option value="mandatory">Mandatory</option>
                <option value="notMandatory">Not Mandatory</option>
              </select>
            </div>
          </div>
        </div>

        {/* Show input UI when type is selected and expanded */}
        {isExpanded && option.typeCode && renderOptionChildrenInput(option)}

        {/* Only MC/Radio/Dropdown/Checkbox option rows can nest further options (Yes/No, etc.). Leaf types
            (Text Box, Date, …) must not show "add option" — that created extra empty rows after save/reload. */}
        {option.typeCode &&
          isChecklistOptionBasedCategory(getOptionTypeCategory(option.typeCode) || "") && (
          <div className="ms-3 mt-2">
            <button
              type="button"
              className={`btn btn-click-to-add-option btn-sm btn-option-${(option.children?.length || 0) % 6}`}
              onClick={() => handleAddSubOption(option.id)}
            >
              <span className="btn-icon">+</span>
              <span className="btn-text">CLICK TO ADD OPTION</span>
            </button>
          </div>
        )}

        {/* Only render children directly if type is NOT option-based (option-based types are handled by renderOptionChildrenInput) */}
        {isExpanded && option.children && option.children.length > 0 && (() => {
          const optionTypeCategory = getOptionTypeCategory(option.typeCode);
          const isOptionBased = optionTypeCategory && isChecklistOptionBasedCategory(optionTypeCategory);

          // Don't render children directly if it's an option-based type (already rendered in renderOptionChildrenInput)
          if (isOptionBased) return null;

          return (
            <div className="option-children ms-3 mt-2">
              {option.children.map((child, childIdx) => {
                // Use parent option's type category for children
                const childParentTypeCategory = getOptionTypeCategory(option.typeCode) || currentTypeCategory;
                return renderOptionNode(child, depth + 1, [...indexPath, childIdx + 1], childParentTypeCategory);
              })}
            </div>
          );
        })()}
      </div>
    );
  };

  const renderOptionTree = () => {
    if (!isOptionBased) return null;
    return (
      <div className="ms-2 mt-2 checklist-option-tree-root">
        <div className="checklist-option-tree-list">
          {options.map((option, index) => renderOptionNode(option, 0, [index + 1]))}
        </div>
      </div>
    );
  };

  /** Root-level add: after all option rows and after nested child questions for this block */
  const renderRootAddOptionButton = () => {
    if (!isOptionBased) return null;
    return (
      <div className="ms-2 mt-2 checklist-option-add-footer">
        <button
          type="button"
          className={`btn btn-click-to-add-option btn-sm mt-1 w-100 btn-option-${options.length % 6}`}
          onClick={handleAddOption}
        >
          <span className="btn-icon">+</span>
          <span className="btn-text">CLICK TO ADD OPTION</span>
        </button>
      </div>
    );
  };

  return (
    <div
      className={`question-block ${level > 0 ? 'question-block-child' : ''} ${draggable ? "question-block-draggable" : ""} ${isDragOver ? "question-block-drag-over" : ""}`}
      style={{ '--level': level }}
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable || !onDragStartBlock) return;
        onDragStartBlock(e, question);
      }}
      onDragOver={(e) => {
        if (!draggable || !onDragOverBlock) return;
        onDragOverBlock(e, question);
      }}
      onDrop={(e) => {
        if (!draggable || !onDropBlock) return;
        onDropBlock(e, question);
      }}
      onDragEnd={() => {
        if (!draggable) return;
        if (onDropBlock) onDropBlock(null, null, { cancelOnly: true });
      }}
    >

      <div className="question-card">
        {/* Question Header Bar */}
        <div className={`question-header ${isNew ? 'question-header-new' : 'question-header-saved'}`}>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-toggle-content p-0 border-0 bg-transparent"
              onClick={() => setIsContentExpanded(!isContentExpanded)}
              title={isContentExpanded ? "Collapse" : "Expand"}
              style={{ fontSize: '18px', lineHeight: '1', cursor: 'pointer', color: '#666' }}
            >
              {isContentExpanded ? '−' : '+'}
            </button>
            <h6 className="question-title mb-0">
              {level === 0
                ? `New Question -${String(parentIndex || 1).padStart(2, "0")}`
                : `New Question -${String(parentIndex || 1).padStart(2, "0")}-${String(childIndex || 1).padStart(2, "0")}`}
            </h6>
          </div>

          <div className="question-actions">
            <button
              type="button"
              className="btn btn-cancel-user funbtns btn-fun-icon"
              onClick={handleDelete}
              title="Delete"
            >
              ×
            </button>

            {/* SR Category + Question Type Selector - Always visible, same design everywhere */}
            <div className="d-flex align-items-center gap-2 ms-2">
              <div className="question-type-select">
                <SrCategoryMultiSelect
                  value={srTypeCode}
                  srTypes={srTypes}
                  onChange={(newSrTypeCode) => {
                    setSrTypeCode(newSrTypeCode);
                    updateNewQuestions({ srTypeCode: newSrTypeCode });
                  }}
                />
              </div>

              <div className="question-type-select">
                <select
                  className="form-select form-select-sm"
                  name="questionTypeCode"
                  value={questionTypeCode}
                  onChange={(e) => {
                    const newTypeCode = e.target.value;
                    setQuestionTypeCode(newTypeCode);
                    updateNewQuestions({ questionTypeCode: newTypeCode });

                    // Update options based on new question type
                    const newType = questionTypes.find((qt) => qt.code === newTypeCode);
                    if (newType) {
                      const name = newType.name?.toLowerCase() || "";
                      if (name.includes("yes") || name.includes("no")) {
                        setOptions([createOptionNode({ text: "Yes" }), createOptionNode({ text: "No" })]);
                      } else if (!isChecklistOptionBasedTypeName(name)) {
                        setOptions([]);
                      }
                    }
                  }}
                >
                  <option value="">Select Type</option>
                  {questionTypes.map((type) => (
                    <option key={type.code} value={type.code}>
                      {getTypeIcon(type.name)} {type.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Mandatory Dropdown - Always visible after type selector */}
              <div className="question-type-select">
                <select
                  className="form-select form-select-sm"
                  name="isMandatory"
                  value={isMandatory ? "mandatory" : "notMandatory"}
                  onChange={(e) => {
                    const newIsMandatory = e.target.value === "mandatory";
                    setIsMandatory(newIsMandatory);
                    updateNewQuestions({ isMandatory: newIsMandatory });
                  }}
                >
                  <option value="mandatory">Mandatory</option>
                  <option value="notMandatory">Not Mandatory</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Question Content */}
        {isContentExpanded && (
          <div className="question-content">
            <div className="question-input-group">
              {/* Radio/Checkbox Indicator */}
              <div className="question-indicator">
                {getQuestionIndicator(questionTypeCategory, questionType?.name)}
              </div>

              {/* Question Input and Options */}
              <div className="question-input-wrapper">
                <input
                  type="text"
                  className="form-control form-control-sm mb-2"
                  placeholder="Enter question text"
                  value={questionText}
                  onChange={(e) => {
                    const newText = e.target.value;
                    setQuestionText(newText);
                    updateNewQuestions({ questionText: newText });
                  }}
                  onBlur={handleBlur}
                />

                {/* Different input fields based on question type */}
                {isOptionBased && renderOptionTree()}

                {questionTypeCategory === "yesNo" && (
                  <div className="ms-2 mt-2 d-flex gap-3">
                    <div className="form-check">
                      <input type="radio" className="form-check-input" checked={false} disabled />
                      <label className="form-check-label">Yes</label>
                    </div>
                    <div className="form-check">
                      <input type="radio" className="form-check-input" checked={false} disabled />
                      <label className="form-check-label">No</label>
                    </div>
                  </div>
                )}

                {questionTypeCategory === "number" && (
                  <input
                    type="number"
                    className="form-control form-control-sm ms-2 mt-2"
                    placeholder="Enter number"
                    style={{ maxWidth: 200 }}
                    disabled
                  />
                )}

                {questionTypeCategory === "date" && (
                  <input
                    type="date"
                    className="form-control form-control-sm ms-2 mt-2"
                    style={{ maxWidth: 200 }}
                    disabled
                  />
                )}

                {questionTypeCategory === "specialText" && (
                  <textarea
                    className="form-control form-control-sm ms-2 mt-2"
                    rows={3}
                    placeholder="Enter special text"
                    disabled
                  />
                )}

                {questionTypeCategory === "levelImage" &&
                  renderLevelInputTextbox(levelInputText, (val) => {
                    setLevelInputText(val);
                    updateNewQuestions({ questionAnsText: val.trim() || null });
                  })}

                {questionTypeCategory === "levelTextBox" &&
                  renderLevelInputTextbox(levelInputText, (val) => {
                    setLevelInputText(val);
                    updateNewQuestions({ questionAnsText: val.trim() || null });
                  })}

                {questionTypeCategory === "image" && (
                  <div className="ms-2 mt-2">
                    <label className="btn btn-outline-primary btn-sm" style={{ pointerEvents: 'none', opacity: 0.6 }}>
                      Upload Image
                      <input type="file" hidden accept="image/*" disabled />
                    </label>
                  </div>
                )}

                {questionTypeCategory === "levelImage" && (
                  <div className="ms-2 mt-2">
                    <label className="btn btn-outline-primary btn-sm" style={{ pointerEvents: 'none', opacity: 0.6 }}>
                      Upload Image
                      <input type="file" hidden accept="image/*" disabled />
                    </label>
                  </div>
                )}

                {questionTypeCategory === "textBox" && (
                  <textarea
                    className="form-control form-control-sm ms-2 mt-2"
                    rows={3}
                    placeholder="Enter text"
                    disabled
                  />
                )}

                {questionTypeCategory === "levelTextBox" && (
                  <textarea
                    className="form-control form-control-sm ms-2 mt-2"
                    rows={3}
                    placeholder="Enter text"
                    disabled
                  />
                )}

                {questionTypeCategory === "label" && (
                  <p className="small ms-2 mt-2 text-muted fst-italic mb-0">
                    (Label - No input required)
                  </p>
                )}
              </div>
            </div>

            {/* Inside same white card as options: keeps DOM + visual hierarchy for create & saved */}
            {nestedChildren ? (
              <div className="checklist-nested-question-blocks">{nestedChildren}</div>
            ) : null}

            {isOptionBased && renderRootAddOptionButton()}
          </div>
        )}
      </div>
    </div>
  );
};

const ChecklistQuestionTreeManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const templateCode = searchParams.get("templateCode") || "";
  const copyFromTemplateCode = location.state?.copyFromTemplateCode || "";
  const isCopyDraft = Boolean(location.state?.isCopyDraft && copyFromTemplateCode);

  const [template, setTemplate] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionTree, setQuestionTree] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [srTypes, setSrTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newQuestions, setNewQuestions] = useState({});
  const [deletedQuestionCodes, setDeletedQuestionCodes] = useState(new Set());
  const [dragState, setDragState] = useState(null); // { sourceCode, parentCode }
  const [dragOverCode, setDragOverCode] = useState("");
  const [isReordering, setIsReordering] = useState(false);
  const [hasRecentReorderSaved, setHasRecentReorderSaved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Track expanded/collapsed state for each question node
  // Initialize all nodes as expanded by default
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  // Auto-expand all nodes when questions are loaded
  useEffect(() => {
    if (questionTree.length > 0) {
      const allNodeKeys = new Set();
      const collectNodeKeys = (nodes) => {
        nodes.forEach(node => {
          const nodeKey = node.questionCode || node.QuestionCode;
          if (nodeKey) {
            allNodeKeys.add(nodeKey);
            if (node.children && node.children.length > 0) {
              collectNodeKeys(node.children);
            }
          }
        });
      };
      collectNodeKeys(questionTree);
      // Merge with existing expanded nodes to preserve user's manual expand/collapse actions
      setExpandedNodes(prev => {
        const merged = new Set(prev);
        allNodeKeys.forEach(key => merged.add(key));
        return merged;
      });
    }
  }, [questionTree]);

  useEffect(() => {
    if (templateCode) {
      loadData();
    }
    // No auto-redirect when templateCode is missing — user stays on this route and can use Back / breadcrumb.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateCode]);

  const loadCopyDraftQuestions = async (sourceCode) => {
    let flatQuestions = [];
    const hierarchyResponse = await checklistQuestionAPI.getQuestionsHierarchy(sourceCode);
    if (hierarchyResponse?.success && Array.isArray(hierarchyResponse.data)) {
      flatQuestions = hierarchyResponse.data;
    } else {
      const listResponse = await checklistQuestionAPI.getByTemplate(sourceCode, {
        pageNumber: 1,
        pageSize: 5000,
      });
      flatQuestions = listResponse?.data?.data || listResponse?.data || [];
    }

    setQuestions([]);
    setQuestionTree([]);
    setDeletedQuestionCodes(new Set());

    if (!Array.isArray(flatQuestions) || flatQuestions.length === 0) {
      setNewQuestions({});
      return;
    }

    const sourceTree = buildFlatQuestionTree(flatQuestions);
    const draftMap = buildDraftQuestionsFromSourceTree(sourceTree);
    setNewQuestions(draftMap);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadTemplate(), loadQuestionTypes()]);

      if (isCopyDraft && copyFromTemplateCode) {
        await loadCopyDraftQuestions(copyFromTemplateCode);
        Swal.fire({
          icon: "info",
          title: "Copy draft",
          html:
            "Template header is saved with the new template code.<br/>Questions are loaded for review — click <b>Save</b> to persist them (same as normal edit).",
          confirmButtonText: "OK",
        });
      } else {
        await loadQuestions();
      }
    } catch (error) {
      console.error("Error loading data:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load data",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = async () => {
    try {
      const response = await checklistTemplateAPI.getByCode(templateCode);
      if (response?.success && response.data) {
        setTemplate(response.data);
        const marketCode =
          response.data.marketCode || response.data.MarketCode || response.data.MARKETCODE || "";
        if (marketCode) {
          await loadSrTypes(marketCode);
        } else {
          setSrTypes([]);
        }
      }
    } catch (error) {
      console.error("Error loading template:", error);
    }
  };

  const loadSrTypes = async (marketCode) => {
    if (!marketCode) {
      setSrTypes([]);
      return;
    }
    try {
      const response = await masterDataAPI.getSRTypes(marketCode, true);
      let srTypesData = [];
      if (response?.success && response.data) {
        srTypesData = mapSrTypesFromApi(response.data);
      } else if (Array.isArray(response?.data)) {
        srTypesData = mapSrTypesFromApi(response.data);
      } else if (Array.isArray(response)) {
        srTypesData = mapSrTypesFromApi(response);
      }
      setSrTypes(withGeneralSrCategoryOption(srTypesData));
    } catch (error) {
      console.error("Error loading SR types:", error);
      setSrTypes(withGeneralSrCategoryOption([]));
    }
  };

  // Helper function to check if a question type is Label, Special Text, or Text Box
  const isLabelOrSpecialText = (typeCode) => {
    if (!typeCode) return false;
    const questionType = questionTypes.find((qt) => qt.code === typeCode);
    if (!questionType) return false;
    const name = questionType.name?.toLowerCase() || "";
    return (
      name === "level" ||
      name.includes("label") ||
      name.includes("special text") ||
      name.includes("text box") ||
      name.includes("textbox")
    );
  };

  // Helper function to check if a parent question is Label, Special Text, or Text Box type
  const isParentLabelOrSpecialText = (parentQuestion, parentTypeCode) => {
    // First check if we have a type code and it's Label, Special Text, or Text Box
    if (parentTypeCode && isLabelOrSpecialText(parentTypeCode)) {
      return true;
    }
    // Also check the question type name directly from the question object (for saved questions)
    const questionTypeName = parentQuestion?.questionTypeName || parentQuestion?.QuestionTypeName || "";
    if (questionTypeName) {
      const name = questionTypeName.toLowerCase();
      if (
        name === "level" ||
        name.includes("label") ||
        name.includes("special text") ||
        name.includes("text box") ||
        name.includes("textbox")
      ) {
        return true;
      }
    }
    // Also check if parent question has a questionType object with name
    if (parentQuestion?.questionType?.name) {
      const name = parentQuestion.questionType.name.toLowerCase();
      if (
        name === "level" ||
        name.includes("label") ||
        name.includes("special text") ||
        name.includes("text box") ||
        name.includes("textbox")
      ) {
        return true;
      }
    }
    return false;
  };

  const loadQuestionTypes = async () => {
    try {
      const response = await checklistQuestionAPI.getQuestionTypes();
      if (response?.success && response.data) {
        // Use Greenlam.QuestionTypeMASTER as-is: QT001 Radio Button, QT002 Label, QT003 Check Box,
        // QT004 Dropdowns, QT005 Special Text, QT006 Multiple Choice, QT007 Text Box
        const apiTypes = response.data.map((qt) => ({
          code: qt.questionTypeCode || qt.QuestionTypeCode || "",
          name: qt.questionTypeName || qt.QuestionTypeName || "",
        })).filter((t) => t.code && t.name);

        // Remove duplicates by code (keep first occurrence)
        const uniqueByCode = [];
        const seenCodes = new Set();
        apiTypes.forEach((type) => {
          const code = (type.code || "").toUpperCase();
          if (code && !seenCodes.has(code)) {
            seenCodes.add(code);
            uniqueByCode.push(type);
          }
        });

        setQuestionTypes(uniqueByCode);
      } else {
        setQuestionTypes([]);
      }
    } catch (error) {
      console.error("Error loading question types:", error);
      setQuestionTypes([]);
    }
  };

  const loadQuestions = async () => {
    try {
      const response = await checklistQuestionAPI.getQuestionsHierarchy(templateCode);
      if (response?.success && response.data) {
        const flatQuestions = response.data;
        setQuestions(flatQuestions);
        const tree = mergeDuplicateTemplateRoots(buildTree(flatQuestions));
        setQuestionTree(tree);
      } else {
        const regularResponse = await checklistQuestionAPI.getByTemplate(templateCode);
        if (regularResponse?.success && regularResponse.data?.data) {
          const flatQuestions = regularResponse.data.data;
          setQuestions(flatQuestions);
          const tree = mergeDuplicateTemplateRoots(buildTree(flatQuestions));
          setQuestionTree(tree);
        }
      }
    } catch (error) {
      console.error("Error loading questions:", error);
    }
  };

  const buildTree = (flatQuestions) => {
    const questionMap = new Map();
    const roots = [];
    const mapKey = (code) => String(code ?? "").trim().toUpperCase();

    flatQuestions.forEach((q) => {
      const rawCode = q.questionCode || q.QuestionCode;
      const k = mapKey(rawCode);
      if (!k) return;
      questionMap.set(k, {
        ...q,
        children: [],
      });
    });

    const seenParentChild = new Set();
    flatQuestions.forEach((q) => {
      const rawCode = q.questionCode || q.QuestionCode;
      const selfKey = mapKey(rawCode);
      const question = selfKey ? questionMap.get(selfKey) : null;
      if (!question) return;
      const parentRaw =
        q.parentQuestionCode ||
        q.ParentQuestionCode ||
        q.ParentQuestionCODE;
      const parentKey = mapKey(parentRaw);

      if (parentRaw && parentKey && questionMap.has(parentKey)) {
        const linkKey = `${parentKey}>${selfKey}`;
        if (seenParentChild.has(linkKey)) return;
        seenParentChild.add(linkKey);
        const parent = questionMap.get(parentKey);
        parent.children.push(question);
      } else {
        const rootKey = `__root__>${selfKey}`;
        if (seenParentChild.has(rootKey)) return;
        seenParentChild.add(rootKey);
        roots.push(question);
      }
    });

    const sortByOrder = (nodes) => {
      nodes.sort((a, b) => {
        const orderA = a.displayOrder || a.DisplayOrder || 0;
        const orderB = b.displayOrder || b.DisplayOrder || 0;
        return orderA - orderB;
      });
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          sortByOrder(node.children);
        }
      });
    };

    sortByOrder(roots);
    return roots;
  };

  const getQuestionCodeValue = (q) =>
    String(q?.questionCode || q?.QuestionCode || q?.QuestionCODE || "").trim();

  const getParentCodeValue = (q) =>
    String(q?.parentQuestionCode || q?.ParentQuestionCode || q?.ParentQuestionCODE || "").trim();

  /**
   * Earlier bugs could insert a second root with the same title + type (e.g. two "Product Application"
   * Multiple Choice blocks). Merge duplicates in-memory: keep the richest tree, move unique children
   * onto the survivor, drop the extra root. Next full-tree save then deletes the orphan from the DB.
   */
  const mergeDuplicateTemplateRoots = (roots) => {
    if (!Array.isArray(roots) || roots.length < 2) return roots;
    const mapKey = (c) => String(c ?? "").trim().toUpperCase();
    const textKey = (node) =>
      String(node?.questionText ?? node?.QuestionText ?? "").trim().toLowerCase();
    const typeKey = (node) =>
      String(node?.questionTypeCode ?? node?.QuestionTypeCode ?? "").trim().toUpperCase();
    const compositeKey = (node) => `${textKey(node)}||${typeKey(node)}`;
    const descendantCount = (node) => {
      const ch = node?.children;
      if (!Array.isArray(ch) || ch.length === 0) return 1;
      return 1 + ch.reduce((sum, c) => sum + descendantCount(c), 0);
    };
    const groups = new Map();
    roots.forEach((r) => {
      const tk = textKey(r);
      if (!tk) return;
      const ck = compositeKey(r);
      if (!groups.has(ck)) groups.set(ck, []);
      groups.get(ck).push(r);
    });
    const toRemove = new Set();
    groups.forEach((nodes) => {
      if (nodes.length < 2) return;
      nodes.sort((a, b) => {
        const da = descendantCount(a);
        const db = descendantCount(b);
        if (db !== da) return db - da;
        const oa = Number(a.displayOrder ?? a.DisplayOrder ?? 0);
        const ob = Number(b.displayOrder ?? b.DisplayOrder ?? 0);
        return oa - ob;
      });
      const primary = nodes[0];
      const seenChildCodes = new Set(
        (primary.children || [])
          .map((c) => mapKey(getQuestionCodeValue(c)))
          .filter(Boolean)
      );
      for (let i = 1; i < nodes.length; i += 1) {
        const dup = nodes[i];
        toRemove.add(dup);
        const kids = Array.isArray(dup.children) ? dup.children : [];
        for (const c of kids) {
          const cc = mapKey(getQuestionCodeValue(c));
          if (cc && seenChildCodes.has(cc)) continue;
          if (cc) seenChildCodes.add(cc);
          if (!Array.isArray(primary.children)) primary.children = [];
          primary.children.push(c);
        }
      }
      const sortRec = (arr) => {
        if (!Array.isArray(arr) || arr.length === 0) return;
        arr.sort(
          (a, b) =>
            Number(a.displayOrder ?? a.DisplayOrder ?? 0) -
            Number(b.displayOrder ?? b.DisplayOrder ?? 0)
        );
        arr.forEach((n) => sortRec(n.children));
      };
      sortRec(primary.children);
    });
    return roots.filter((r) => !toRemove.has(r));
  };

  const collectQuestionCodesRecursively = (node) => {
    if (!node) return [];
    const selfCode = getQuestionCodeValue(node);
    const childCodes = Array.isArray(node.children)
      ? node.children.flatMap((child) => collectQuestionCodesRecursively(child))
      : [];
    return [...(selfCode ? [selfCode] : []), ...childCodes];
  };

  const normalizeDeleteCodes = (codes = []) => {
    const uniqueCodes = [...new Set((codes || []).map((c) => String(c || "").trim()).filter(Boolean))];
    if (uniqueCodes.length === 0) return [];

    // Remove descendants when ancestor is already present in delete list.
    const parentByCode = new Map(
      (Array.isArray(questions) ? questions : []).map((q) => [
        getQuestionCodeValue(q),
        String(q?.parentQuestionCode || q?.ParentQuestionCode || q?.ParentQuestionCODE || "").trim(),
      ])
    );
    const codeSet = new Set(uniqueCodes);
    return uniqueCodes.filter((code) => {
      let parent = parentByCode.get(code);
      while (parent) {
        if (codeSet.has(parent)) return false;
        parent = parentByCode.get(parent);
      }
      return true;
    });
  };

  const safeDeleteQuestionByCode = async (code) => {
    try {
      await checklistQuestionAPI.deleteQuestion(code);
      return true;
    } catch (error) {
      const status = Number(error?.response?.status || 0);
      const message = String(error?.response?.data?.message || error?.message || "").toLowerCase();
      const isBenign =
        status === 404 ||
        message.includes("not found") ||
        message.includes("does not exist") ||
        message.includes("already deleted");
      if (isBenign) return false;
      throw error;
    }
  };

  const toggleNode = (nodeKey) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeKey)) {
        newSet.delete(nodeKey);
      } else {
        newSet.add(nodeKey);
      }
      return newSet;
    });
  };

  const moveInArray = (arr, from, to) => {
    const out = [...arr];
    const [item] = out.splice(from, 1);
    out.splice(to, 0, item);
    return out;
  };

  const buildUpdatePayloadFromQuestion = (q, displayOrder) => ({
    templateCode,
    questionTypeCode: q?.questionTypeCode || q?.QuestionTypeCode || "",
    srTypeCode: q?.srTypeCode || q?.SRTypeCode || null,
    parentQuestionCode: (q?.parentQuestionCode || q?.ParentQuestionCode || q?.ParentQuestionCODE || null),
    questionText: q?.questionText || q?.QuestionText || "",
    questionAnsText: q?.questionAnsText || q?.QuestionAnsText || null,
    displayOrder,
    isMandatory:
      q?.isMandatory !== undefined
        ? q.isMandatory
        : q?.IsMandatory !== undefined
          ? q.IsMandatory
          : true,
    isConditional: q?.isConditional ?? q?.IsConditional ?? false,
    conditionalAnswerValue: q?.conditionalAnswerValue || q?.ConditionalAnswerValue || null,
  });

  const handleDragStartQuestion = (e, question) => {
    const sourceCode = getQuestionCodeValue(question);
    if (!sourceCode || sourceCode.startsWith("root_") || sourceCode.startsWith("child_")) return;
    if (!sourceCode) return;
    const parentCode = getParentCodeValue(question);
    setDragState({ sourceCode, parentCode });
    if (e?.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", sourceCode);
    }
  };

  const handleDragOverQuestion = (e, question) => {
    if (!dragState || !question) return;
    const targetCode = getQuestionCodeValue(question);
    const targetParentCode = getParentCodeValue(question);
    if (!targetCode || targetCode === dragState.sourceCode) return;
    if (targetParentCode !== dragState.parentCode) return; // only same-sibling group
    e.preventDefault();
    setDragOverCode(targetCode);
  };

  const handleDropQuestion = async (e, question, meta = {}) => {
    if (meta.cancelOnly) {
      setDragOverCode("");
      setDragState(null);
      return;
    }
    if (!dragState || !question || isReordering) return;
    e?.preventDefault?.();

    const targetCode = getQuestionCodeValue(question);
    const targetParentCode = getParentCodeValue(question);
    if (!targetCode || targetCode === dragState.sourceCode) {
      setDragOverCode("");
      setDragState(null);
      return;
    }
    if (targetParentCode !== dragState.parentCode) {
      setDragOverCode("");
      setDragState(null);
      return;
    }

    const parentCode = dragState.parentCode;
    const siblings = [...(questions || [])]
      .filter((q) => getParentCodeValue(q) === parentCode)
      .sort(
        (a, b) =>
          (a?.displayOrder ?? a?.DisplayOrder ?? 0) - (b?.displayOrder ?? b?.DisplayOrder ?? 0)
      );

    const from = siblings.findIndex((q) => getQuestionCodeValue(q) === dragState.sourceCode);
    const to = siblings.findIndex((q) => getQuestionCodeValue(q) === targetCode);
    if (from < 0 || to < 0 || from === to) {
      setDragOverCode("");
      setDragState(null);
      return;
    }

    const reordered = moveInArray(siblings, from, to).map((q, idx) => ({
      ...q,
      displayOrder: idx + 1,
      DisplayOrder: idx + 1,
    }));
    const orderedCodes = reordered
      .map((q) => getQuestionCodeValue(q))
      .filter((code) => code && !code.startsWith("root_") && !code.startsWith("child_"));
    const reorderedMap = new Map(reordered.map((q) => [getQuestionCodeValue(q), q]));

    setIsReordering(true);
    try {
      if (orderedCodes.length < 2) {
        setDragOverCode("");
        setDragState(null);
        setIsReordering(false);
        return;
      }

      // Use backend reorder endpoint designed for displayOrder updates.
      await checklistQuestionAPI.reorder(templateCode, orderedCodes);

      setQuestions((prev) =>
        (prev || []).map((q) => {
          const code = getQuestionCodeValue(q);
          if (!reorderedMap.has(code)) return q;
          return { ...q, displayOrder: reorderedMap.get(code).displayOrder, DisplayOrder: reorderedMap.get(code).DisplayOrder };
        })
      );
      await loadQuestions();
      setHasRecentReorderSaved(true);
    } catch (error) {
      console.error("Failed to reorder questions:", error);
      // Fallback path for environments where reorder endpoint may not be deployed.
      try {
        await Promise.all(
          reordered
            .filter((q) => {
              const code = getQuestionCodeValue(q);
              return code && !code.startsWith("root_") && !code.startsWith("child_");
            })
            .map((q, idx) =>
              checklistQuestionAPI.updateQuestion(
                getQuestionCodeValue(q),
                buildUpdatePayloadFromQuestion(q, idx + 1)
              )
            )
        );
        await loadQuestions();
        setHasRecentReorderSaved(true);
      } catch (fallbackError) {
        Swal.fire({
          icon: "error",
          title: "Reorder Failed",
          text:
            fallbackError?.response?.data?.message ||
            error?.response?.data?.message ||
            fallbackError?.message ||
            error?.message ||
            "Could not save question order.",
        });
      }
      await loadQuestions();
    } finally {
      setIsReordering(false);
      setDragOverCode("");
      setDragState(null);
    }
  };

  const isNodeExpanded = (nodeKey) => {
    // If nodeKey is not provided, default to expanded
    if (!nodeKey) return true;
    // Since we default to expanded, we need to check if the node was explicitly collapsed
    // If expandedNodes is empty, all nodes are expanded by default
    // If expandedNodes has items, nodes in the set are expanded, nodes not in the set are collapsed
    // But for initial load, we want all nodes expanded, so we default to true
    // After user interaction, we track the state explicitly
    if (expandedNodes.size === 0) {
      // No explicit state set, default to expanded
      return true;
    }
    // If node is in the set, it's expanded; if not, it's collapsed
    return expandedNodes.has(nodeKey);
  };

  const hasChildren = (node) => {
    const nodeKey = node.questionCode || node.QuestionCode;
    const hasSavedChildren = node.children && node.children.length > 0;
    const hasNewChildren = Object.keys(newQuestions).some(key => {
      if (key === `child_${nodeKey}`) {
        const question = newQuestions[key];
        const hasType = !!(question?.questionTypeCode || question?.QuestionTypeCode);
        return hasType;
      }
      if (key.startsWith(`child_${nodeKey}_`)) {
        const question = newQuestions[key];
        const questionParentCode = question?.parentQuestionCode || question?.ParentQuestionCode;
        if (questionParentCode !== nodeKey) return false;
        // Only count as child if it has a type selected
        const hasType = !!(question.questionTypeCode || question.QuestionTypeCode);
        return hasType;
      }
      if (key.startsWith(`option_child_${nodeKey}_`)) {
        const question = newQuestions[key];
        const hasType = !!(question?.questionTypeCode || question?.QuestionTypeCode);
        return hasType;
      }
      return false;
    });
    return hasSavedChildren || hasNewChildren;
  };

  const renderQuestionTree = (node, level = 0, parentIndex = null, childIndex = null, nodeKeyOverride = null) => {
    // For new questions, use the key from newQuestions; for saved questions, use questionCode.
    // nodeKeyOverride must be a string key (e.g. draft root_1 or saved QuestionCODE). Ignore non-primitive
    // values so a mistaken caller cannot pass a parent object and poison nodeKey.
    const overrideStr =
      nodeKeyOverride != null &&
      (typeof nodeKeyOverride === "string" || typeof nodeKeyOverride === "number")
        ? String(nodeKeyOverride).trim()
        : "";
    const nodeKey =
      overrideStr ||
      node.questionCode ||
      node.QuestionCode ||
      Object.keys(newQuestions).find((key) => {
        const q = newQuestions[key];
        const nodeText = node.questionText || node.QuestionText || "";
        const nodeType = node.questionTypeCode || node.QuestionTypeCode || "";
        const nodeParent = node.parentQuestionCode || node.ParentQuestionCode || null;
        const qText = q.questionText || q.QuestionText || "";
        const qType = q.questionTypeCode || q.QuestionTypeCode || "";
        const qParent = q.parentQuestionCode || q.ParentQuestionCode || null;
        return qText === nodeText && qType === nodeType && qParent === nodeParent;
      }) ||
      null;
    
    const isExpanded = isNodeExpanded(nodeKey);
    const hasChildNodes = hasChildren(node);

    // Find all new child questions for this node (both old format and new format)
    // For new questions, nodeKey might be the key from newQuestions (e.g., "root_1")
    const hasNewChild = nodeKey ? newQuestions[`child_${nodeKey}`] : null;
    const newChildQuestions = Object.keys(newQuestions)
      .filter(key => {
        // Check if it's a child question for this node
        if (!nodeKey) return false; // Can't match if we don't have a nodeKey
        if (key === `child_${nodeKey}`) return false; // Skip old format, handled separately
        if (!key.startsWith(`child_${nodeKey}_`)) return false;
        // Verify parentQuestionCode matches to ensure it's a direct child
        const question = newQuestions[key];
        const questionParentCode = question?.parentQuestionCode || question?.ParentQuestionCode;
        if (questionParentCode !== nodeKey) return false;
        // Only show child questions that have a type selected
        const hasType = !!(question.questionTypeCode || question.QuestionTypeCode);
        return hasType;
      })
      .map(key => ({ key, question: newQuestions[key] }));

    return (
      <div key={nodeKey} className="question-tree-wrapper">
        <div className="tree-node-header">
          {hasChildNodes && (
            <button
              type="button"
              className="tree-toggle-btn"
              onClick={() => toggleNode(nodeKey)}
              aria-label={isExpanded ? "Collapse" : "Expand"}
              title={isExpanded ? "Collapse" : "Expand"}
            >
              <span className={`tree-toggle-icon ${isExpanded ? 'expanded' : 'collapsed'}`}>
                {isExpanded ? '−' : '+'}
              </span>
            </button>
          )}
          {!hasChildNodes && <span className="tree-spacer"></span>}
          <span className="tree-icon">
            {hasChildNodes ? '📁' : '📄'}
          </span>
        </div>
        <EditableQuestion
          question={node}
          questionTypes={questionTypes}
          srTypes={srTypes}
          level={level}
          templateCode={templateCode}
          onSave={loadQuestions}
          onDelete={loadQuestions}
          renderQuestionTree={renderQuestionTree}
          node={node}
          isNodeExpanded={isNodeExpanded}
          toggleNode={toggleNode}
          questionKey={nodeKey}
          newQuestions={newQuestions}
          onNewQuestionsUpdate={setNewQuestions}
          onAddChild={(parent) => {
            // Handle adding child questions at any level with unique keys
            // For new questions, nodeKey is the key from newQuestions (e.g., "root_1")
            // For saved questions, nodeKey is the questionCode
            const parentKey = nodeKey || parent.questionCode || parent.QuestionCode || `temp_${Date.now()}`;
            
            // Validate parent has a type selected before allowing child questions (except for Label and Special Text)
            const parentQuestion = newQuestions[parentKey] || parent;
            const parentTypeCode = parentQuestion?.questionTypeCode || parentQuestion?.QuestionTypeCode || parent?.questionTypeCode || parent?.QuestionTypeCode;
            
            // Allow child questions for Label and Special Text even without question type
            if (!parentTypeCode && !isParentLabelOrSpecialText(parentQuestion, parentTypeCode)) {
              Swal.fire({
                icon: "warning",
                title: "Validation Error",
                text: "Please select a question type for the parent question before adding child questions",
                confirmButtonText: "OK"
              });
              return;
            }
            
            // Validate parent has mandatory dropdown selected
            const parentIsMandatory = parentQuestion?.isMandatory !== undefined ? parentQuestion.isMandatory : (parent?.isMandatory !== undefined ? parent.isMandatory : undefined);
            if (parentIsMandatory === undefined) {
              Swal.fire({
                icon: "warning",
                title: "Validation Error",
                text: "Please select mandatory option for the parent question before adding child questions",
                confirmButtonText: "OK"
              });
              return;
            }
            
            const timestamp = Date.now();
            const randomId = Math.random().toString(36).substr(2, 9);

            // Auto-expand parent node when adding child
            if (!isNodeExpanded(parentKey)) {
              toggleNode(parentKey);
            }

            if (parent.optionIndex !== undefined) {
              // Adding child to an option
              const optionChildKey = `option_child_${parentKey}_${parent.optionIndex}_${timestamp}_${randomId}`;

              setNewQuestions(prev => ({
                ...prev,
                [optionChildKey]: buildChecklistNewQuestionDraft(questionTypes, {
                  parentQuestionCode: parentKey,
                  conditionalAnswerValue: parent.optionText || "",
                  level: level + 1,
                }),
              }));
            } else {
              // Adding regular child question with unique key
              const childKey = `child_${parentKey}_${timestamp}_${randomId}`;
              console.log(`[onAddChild - Tree] Adding child question:`, {
                parentKey,
                childKey,
                parent,
                nodeKey,
                level: level + 1,
                timestamp,
                randomId
              });
              setNewQuestions(prev => {
                const updated = {
                  ...prev,
                  [childKey]: buildChecklistNewQuestionDraft(questionTypes, {
                    parentQuestionCode: parentKey,
                    level: level + 1,
                  }),
                };
                console.log(`[onAddChild - Tree] Updated newQuestions keys:`, Object.keys(updated));
                console.log(`[onAddChild - Tree] Child question added:`, updated[childKey]);
                return updated;
              });
            }
          }}
          parentIndex={parentIndex}
          childIndex={childIndex}
        />

        {/* New Child Question Form - Old format */}
        {isExpanded && hasNewChild && (() => {
          const newChildKey = hasNewChild.questionCode || hasNewChild.QuestionCode || `child_${nodeKey}`;
          const newChildHasChildren = hasChildren({ ...hasNewChild, questionCode: newChildKey });

          return (
            <div className="question-tree-wrapper">
              {newChildHasChildren && (
                <div className="tree-node-header">
                  <button
                    type="button"
                    className="tree-toggle-btn"
                    onClick={() => toggleNode(newChildKey)}
                    aria-label={isNodeExpanded(newChildKey) ? "Collapse" : "Expand"}
                    title={isNodeExpanded(newChildKey) ? "Collapse" : "Expand"}
                  >
                    <span className={`tree-toggle-icon ${isNodeExpanded(newChildKey) ? 'expanded' : 'collapsed'}`}>
                      {isNodeExpanded(newChildKey) ? '−' : '+'}
                    </span>
                  </button>
                  <span className="tree-icon">📁</span>
                </div>
              )}
              {!newChildHasChildren && (
                <div className="tree-node-header">
                  <span className="tree-spacer"></span>
                  <span className="tree-icon">📄</span>
                </div>
              )}
              <EditableQuestion
                question={hasNewChild}
                questionTypes={questionTypes}
                srTypes={srTypes}
                level={level + 1}
                templateCode={templateCode}
                isNew={true}
                questionKey={`child_${nodeKey}`}
                newQuestions={newQuestions}
                onNewQuestionsUpdate={setNewQuestions}
                renderQuestionTree={renderQuestionTree}
                node={hasNewChild}
                onSave={() => {
                  const updated = { ...newQuestions };
                  delete updated[`child_${nodeKey}`];
                  setNewQuestions(updated);
                  loadQuestions();
                }}
                onDelete={() => {
                  const updated = { ...newQuestions };
                  delete updated[`child_${nodeKey}`];
                  setNewQuestions(updated);
                }}
                onAddChild={(parent) => {
                  // Allow adding child questions to any level with unique keys
                  const parentKey = parent.questionCode || parent.QuestionCode || `child_${nodeKey}`;
                  
                  // Validate parent has a type selected before allowing child questions (except for Label and Special Text)
                  const parentQuestion = newQuestions[parentKey] || parent;
                  const parentTypeCode = parentQuestion?.questionTypeCode || parentQuestion?.QuestionTypeCode || parent?.questionTypeCode || parent?.QuestionTypeCode;
                  
                  // Allow child questions for Label and Special Text even without question type
                  if (!parentTypeCode && !isLabelOrSpecialText(parentTypeCode)) {
                    Swal.fire({
                      icon: "warning",
                      title: "Validation Error",
                      text: "Please select a question type for the parent question before adding child questions",
                      confirmButtonText: "OK"
                    });
                    return;
                  }
                  
                  const timestamp = Date.now();
                  const randomId = Math.random().toString(36).substr(2, 9);

                  // Auto-expand parent node when adding child
                  if (!isNodeExpanded(parentKey)) {
                    toggleNode(parentKey);
                  }

                  if (parent.optionIndex !== undefined) {
                    const optionChildKey = `option_child_${parentKey}_${parent.optionIndex}_${timestamp}_${randomId}`;
                    setNewQuestions(prev => ({
                      ...prev,
                      [optionChildKey]: buildChecklistNewQuestionDraft(questionTypes, {
                        parentQuestionCode: parentKey,
                        conditionalAnswerValue: parent.optionText || "",
                        level: level + 2,
                      }),
                    }));
                  } else {
                    const childKey = `child_${parentKey}_${timestamp}_${randomId}`;
                    setNewQuestions(prev => ({
                      ...prev,
                      [childKey]: buildChecklistNewQuestionDraft(questionTypes, {
                        parentQuestionCode: parentKey,
                        level: level + 2,
                      }),
                    }));
                  }
                }}
                parentIndex={parentIndex}
                childIndex={node.children?.length || 0}
              />
            </div>
          );
        })()}

        {/* New Child Question Forms - New format (with timestamp) */}
        {isExpanded && newChildQuestions.map(({ key, question: newChild }) => {
          const newChildKey = newChild.questionCode || newChild.QuestionCode || key;
          const newChildHasChildren = hasChildren({ ...newChild, questionCode: newChildKey });

          return (
            <div key={key} className="question-tree-wrapper">
              {newChildHasChildren && (
                <div className="tree-node-header">
                  <button
                    type="button"
                    className="tree-toggle-btn"
                    onClick={() => toggleNode(newChildKey)}
                    aria-label={isNodeExpanded(newChildKey) ? "Collapse" : "Expand"}
                    title={isNodeExpanded(newChildKey) ? "Collapse" : "Expand"}
                  >
                    <span className={`tree-toggle-icon ${isNodeExpanded(newChildKey) ? 'expanded' : 'collapsed'}`}>
                      {isNodeExpanded(newChildKey) ? '−' : '+'}
                    </span>
                  </button>
                  <span className="tree-icon">📁</span>
                </div>
              )}
              {!newChildHasChildren && (
                <div className="tree-node-header">
                  <span className="tree-spacer"></span>
                  <span className="tree-icon">📄</span>
                </div>
              )}
              <EditableQuestion
                question={newChild}
                questionTypes={questionTypes}
                srTypes={srTypes}
                level={level + 1}
                templateCode={templateCode}
                isNew={true}
                questionKey={key}
                newQuestions={newQuestions}
                onNewQuestionsUpdate={setNewQuestions}
                renderQuestionTree={renderQuestionTree}
                node={newChild}
                onSave={() => {
                  const updated = { ...newQuestions };
                  delete updated[key];
                  setNewQuestions(updated);
                  loadQuestions();
                }}
                onDelete={() => {
                  const updated = { ...newQuestions };
                  delete updated[key];
                  setNewQuestions(updated);
                }}
                onAddChild={(parent) => {
                  // Allow adding child questions to any level with unique keys
                  const parentKey = parent.questionCode || parent.QuestionCode || key;
                  
                  // Validate parent has a type selected before allowing child questions (except for Label and Special Text)
                  const parentQuestion = newQuestions[parentKey] || parent;
                  const parentTypeCode = parentQuestion?.questionTypeCode || parentQuestion?.QuestionTypeCode || parent?.questionTypeCode || parent?.QuestionTypeCode;
                  
                  // Allow child questions for Label and Special Text even without question type
                  if (!parentTypeCode && !isLabelOrSpecialText(parentTypeCode)) {
                    Swal.fire({
                      icon: "warning",
                      title: "Validation Error",
                      text: "Please select a question type for the parent question before adding child questions",
                      confirmButtonText: "OK"
                    });
                    return;
                  }
                  
                  // Validate parent has mandatory dropdown selected
                  const parentIsMandatory = parentQuestion?.isMandatory !== undefined ? parentQuestion.isMandatory : (parent?.isMandatory !== undefined ? parent.isMandatory : undefined);
                  if (parentIsMandatory === undefined) {
                    Swal.fire({
                      icon: "warning",
                      title: "Validation Error",
                      text: "Please select mandatory option for the parent question before adding child questions",
                      confirmButtonText: "OK"
                    });
                    return;
                  }
                  
                  const timestamp = Date.now();
                  const randomId = Math.random().toString(36).substr(2, 9);

                  // Auto-expand parent node when adding child
                  if (!isNodeExpanded(parentKey)) {
                    toggleNode(parentKey);
                  }

                  if (parent.optionIndex !== undefined) {
                    const optionChildKey = `option_child_${parentKey}_${parent.optionIndex}_${timestamp}_${randomId}`;
                    setNewQuestions(prev => ({
                      ...prev,
                      [optionChildKey]: buildChecklistNewQuestionDraft(questionTypes, {
                        parentQuestionCode: parentKey,
                        conditionalAnswerValue: parent.optionText || "",
                        level: level + 2,
                      }),
                    }));
                  } else {
                    const childKey = `child_${parentKey}_${timestamp}_${randomId}`;
                    setNewQuestions(prev => ({
                      ...prev,
                      [childKey]: buildChecklistNewQuestionDraft(questionTypes, {
                        parentQuestionCode: parentKey,
                        level: level + 2,
                      }),
                    }));
                  }
                }}
                parentIndex={parentIndex}
                childIndex={(node.children?.length || 0) + newChildQuestions.findIndex(c => c.key === key) + 1}
                isNodeExpanded={isNodeExpanded}
                toggleNode={toggleNode}
              />
            </div>
          );
        })}

        {/* Render Option-based Child Questions */}
        {isExpanded && Object.keys(newQuestions)
          .filter((key) => {
            if (!key.startsWith(`option_child_${nodeKey}_`)) return false;
            // Only show option children that have a type selected
            const question = newQuestions[key];
            const hasType = !!(question.questionTypeCode || question.QuestionTypeCode);
            return hasType;
          })
          .map((key) => {
            const optionChild = newQuestions[key];
            const optionIndex = parseInt(key.split('_').pop());
            const optionChildKey = optionChild.questionCode || optionChild.QuestionCode || key;
            const hasOptionChildChildren = newQuestions[`child_${optionChildKey}`];

            const optionChildHasChildren = hasOptionChildChildren ||
              (optionChild.questionCode && node.children && node.children.some(c =>
                (c.questionCode || c.QuestionCode) === optionChild.questionCode &&
                c.children && c.children.length > 0
              ));

            return (
              <div key={key} className="question-tree-wrapper">
                {optionChildHasChildren && (
                  <div className="tree-node-header">
                    <button
                      type="button"
                      className="tree-toggle-btn"
                      onClick={() => toggleNode(optionChildKey)}
                      aria-label={isNodeExpanded(optionChildKey) ? "Collapse" : "Expand"}
                      title={isNodeExpanded(optionChildKey) ? "Collapse" : "Expand"}
                    >
                      <span className={`tree-toggle-icon ${isNodeExpanded(optionChildKey) ? 'expanded' : 'collapsed'}`}>
                        {isNodeExpanded(optionChildKey) ? '−' : '+'}
                      </span>
                    </button>
                    <span className="tree-icon">📁</span>
                  </div>
                )}
                {!optionChildHasChildren && (
                  <div className="tree-node-header">
                    <span className="tree-spacer"></span>
                    <span className="tree-icon">📄</span>
                  </div>
                )}
                <EditableQuestion
                  question={optionChild}
                  questionTypes={questionTypes}
                  srTypes={srTypes}
                  level={level + 1}
                  templateCode={templateCode}
                  isNew={true}
                  questionKey={key}
                  newQuestions={newQuestions}
                  onNewQuestionsUpdate={setNewQuestions}
                  renderQuestionTree={renderQuestionTree}
                  node={optionChild}
                  onSave={() => {
                    const updated = { ...newQuestions };
                    delete updated[key];
                    setNewQuestions(updated);
                    loadQuestions();
                  }}
                  onDelete={() => {
                    const updated = { ...newQuestions };
                    delete updated[key];
                    // Also remove any children of this question
                    Object.keys(updated).forEach(k => {
                      if (k.startsWith(`child_${optionChildKey}`) || k.startsWith(`option_child_${optionChildKey}_`)) {
                        delete updated[k];
                      }
                    });
                    setNewQuestions(updated);
                  }}
                  onAddChild={(parent) => {
                    // Allow adding child questions to option-based children with unique keys
                    const parentKey = parent.questionCode || parent.QuestionCode || optionChildKey;
                    
                    // Validate parent has a type selected before allowing child questions (except for Label and Special Text)
                    const parentQuestion = newQuestions[parentKey] || parent;
                    const parentTypeCode = parentQuestion?.questionTypeCode || parentQuestion?.QuestionTypeCode || parent?.questionTypeCode || parent?.QuestionTypeCode;
                    
                    // Allow child questions for Label and Special Text even without question type
                    if (!parentTypeCode && !isLabelOrSpecialText(parentTypeCode)) {
                      Swal.fire({
                        icon: "warning",
                        title: "Validation Error",
                        text: "Please select a question type for the parent question before adding child questions",
                        confirmButtonText: "OK"
                      });
                      return;
                    }
                    
                    // Validate parent has mandatory dropdown selected
                    const parentIsMandatory = parentQuestion?.isMandatory !== undefined ? parentQuestion.isMandatory : (parent?.isMandatory !== undefined ? parent.isMandatory : undefined);
                    if (parentIsMandatory === undefined) {
                      Swal.fire({
                        icon: "warning",
                        title: "Validation Error",
                        text: "Please select mandatory option for the parent question before adding child questions",
                        confirmButtonText: "OK"
                      });
                      return;
                    }
                    
                    const timestamp = Date.now();
                    const randomId = Math.random().toString(36).substr(2, 9);

                    if (parent.optionIndex !== undefined) {
                      const optionChildKey = `option_child_${parentKey}_${parent.optionIndex}_${timestamp}_${randomId}`;
                      setNewQuestions(prev => ({
                        ...prev,
                        [optionChildKey]: buildChecklistNewQuestionDraft(questionTypes, {
                          parentQuestionCode: parentKey,
                          conditionalAnswerValue: parent.optionText || "",
                          level: level + 2,
                        }),
                      }));
                    } else {
                      const childKey = `child_${parentKey}_${timestamp}_${randomId}`;
                      setNewQuestions(prev => ({
                        ...prev,
                        [childKey]: buildChecklistNewQuestionDraft(questionTypes, {
                          parentQuestionCode: parentKey,
                          level: level + 2,
                        }),
                      }));
                    }
                  }}
                  parentIndex={parentIndex}
                  childIndex={optionIndex}
                />

                {/* Render children of option-based child questions recursively */}
                {isNodeExpanded(optionChildKey) && hasOptionChildChildren && (
                  <div className="ms-3 mt-2">
                    {renderQuestionTree(
                      { ...hasOptionChildChildren, questionCode: optionChildKey },
                      level + 2,
                      parentIndex,
                      optionIndex
                    )}
                  </div>
                )}

                {/* Also render saved children if this option child is saved and has children */}
                {isNodeExpanded(optionChildKey) && optionChild.questionCode && node.children && node.children.some(c =>
                  (c.questionCode || c.QuestionCode) === optionChild.questionCode &&
                  c.children && c.children.length > 0
                ) && (
                    <div className="ms-3 mt-2">
                      {node.children
                        .find(c => (c.questionCode || c.QuestionCode) === optionChild.questionCode)
                        ?.children
                        ?.map((grandChild, gcIndex) =>
                          renderQuestionTree(
                            grandChild,
                            level + 2,
                            parentIndex,
                            gcIndex + 1
                          )
                        )}
                    </div>
                  )}
              </div>
            );
          })}

        {/* Render Children with numbering - Only if renderQuestionTree is provided (for new questions) */}
        {isExpanded && node && node.children && node.children.length > 0 && renderQuestionTree && (
          <div className={`children-container ${level === 0 ? 'children-container-level-0' : ''}`}>
            {node.children
              .filter((child) => {
                // Filter out option-based children (they're shown separately above)
                // But only if they're new questions - saved option-based children should be rendered here
                const isNewOptionChild = Object.keys(newQuestions).some(key =>
                  key.startsWith(`option_child_${nodeKey}_`) &&
                  newQuestions[key].conditionalAnswerValue === (child.conditionalAnswerValue || child.ConditionalAnswerValue)
                );
                // Also check if this child has a conditionalAnswerValue that matches an option
                // Extract options from node's questionAnsText
                const questionOptions = node.questionAnsText || node.QuestionAnsText
                  ? (node.questionAnsText || node.QuestionAnsText).split(",").map(opt => opt.trim()).filter(opt => opt)
                  : [];
                const hasMatchingOption = questionOptions.length > 0 && questionOptions.some(opt => {
                  return opt === (child.conditionalAnswerValue || child.ConditionalAnswerValue);
                });
                // If it's a saved question with conditionalAnswerValue matching an option, show it in option section
                // Otherwise, show it in regular children section
                return !isNewOptionChild && (!hasMatchingOption || child.questionCode || child.QuestionCode);
              })
              .map((child, index) => {
                  // Recursively render child questions, allowing unlimited nesting (n-level)
                  // For saved children, use their questionCode; for new children, find their key
                  const childNodeKey = child.questionCode || child.QuestionCode || 
                    (Object.keys(newQuestions).find(k => {
                      const q = newQuestions[k];
                      return (q.questionText || q.QuestionText) === (child.questionText || child.QuestionText) &&
                             (q.parentQuestionCode || q.ParentQuestionCode) === nodeKey;
                    }) || null);
                  return renderQuestionTree(
                    child,
                    level + 1,
                    parentIndex || (level === 0 ? questionTree.findIndex(n => (n.questionCode || n.QuestionCode) === nodeKey) + 1 : null),
                    index + 1,
                    childNodeKey
                  );
              })}
          </div>
        )}

        {/* Click To Add button - Always show when expanded, regardless of whether children exist */}
        {isExpanded && (
          <div className={`add-child-btn-container ${level === 0 ? 'add-child-btn-container-level-0' : ''}`}>
            <button
              type="button"
              className="btn btn-save-user funbtns btn-fun-sm checklist-tree-add-btn"
              onClick={() => {
                // For saved questions, add directly to newQuestions
                // Generate unique key to avoid conflicts with existing children
                const timestamp = Date.now();
                const randomId = Math.random().toString(36).substr(2, 9);
                const childKey = `child_${nodeKey}_${timestamp}_${randomId}`;
                console.log(`[Click To Add Button] Adding child directly:`, {
                  nodeKey,
                  childKey,
                  level: level + 1
                });
                setNewQuestions(prev => ({
                  ...prev,
                  [childKey]: buildChecklistNewQuestionDraft(questionTypes, {
                    parentQuestionCode: nodeKey,
                    level: level + 1,
                  }),
                }));
                // Auto-expand when adding a child
                if (!isExpanded) {
                  toggleNode(nodeKey);
                }
              }}
            >
              <AddIcon className="checklist-tree-btn-icon" fontSize="small" />
              CLICK TO ADD
            </button>
          </div>
        )}
      </div>
    );
  };

  const handleAddRootQuestion = () => {
    // Allow first question without validation, but validate subsequent questions
    const existingRootQuestions = Object.keys(newQuestions).filter((key) => key.startsWith("root_"));
    
    // Check if this is the very first question (tree is empty AND no new questions yet)
    const isFirstQuestion = questionTree.length === 0 && existingRootQuestions.length === 0;
    
    // Validate that all existing questions have types selected (except for the first question)
    if (!isFirstQuestion && existingRootQuestions.length > 0) {
      const rootQuestionsWithoutType = existingRootQuestions.some((key) => {
        const question = newQuestions[key];
        const hasType = !!(question.questionTypeCode || question.QuestionTypeCode);
        return !hasType;
      });

      if (rootQuestionsWithoutType) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "Please select a question type for all existing questions before adding new questions",
          confirmButtonText: "OK"
        });
        return;
      }
      
      // Validate that all existing questions have mandatory dropdown selected
      const rootQuestionsWithoutMandatory = existingRootQuestions.some((key) => {
        const question = newQuestions[key];
        const questionIsMandatory = question.isMandatory !== undefined ? question.isMandatory : (question.IsMandatory !== undefined ? question.IsMandatory : undefined);
        return questionIsMandatory === undefined;
      });

      if (rootQuestionsWithoutMandatory) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: "Please select mandatory option for all existing questions before adding new questions",
          confirmButtonText: "OK"
        });
        return;
      }
    }

    setNewQuestions((prev) => {
      const roots = Object.keys(prev).filter((k) => k.startsWith("root_"));
      const maxSuffix = roots.reduce((m, k) => {
        const n = parseInt(String(k).replace(/^root_/, ""), 10);
        return Number.isFinite(n) && n > m ? n : m;
      }, 0);
      const nextIndex = Math.max(maxSuffix, questionTree.length) + 1;
      return {
        ...prev,
        [`root_${nextIndex}`]: buildChecklistNewQuestionDraft(questionTypes, {
          level: 0,
          index: nextIndex,
        }),
      };
    });
  };

  // Build tree structure from newQuestions
  const buildQuestionTree = (questionsMap, parentKey = null) => {
    const tree = [];

    console.log(`[buildQuestionTree] Building tree for parentKey: ${parentKey}`);
    console.log(`[buildQuestionTree] Total questions in map: ${Object.keys(questionsMap).length}`);
    console.log(`[buildQuestionTree] Question keys:`, Object.keys(questionsMap));

    // Find all questions with the given parent
    const relevantQuestions = Object.entries(questionsMap)
      .filter(([key, question]) => {
        const questionParent = question.parentQuestionCode || question.ParentQuestionCode;

        // For root questions (no parent)
        if (parentKey === null) {
          // Root questions: start with 'root_' or have no parentQuestionCode
          if (key.startsWith('root_')) {
            console.log(`[buildQuestionTree] Found root question: ${key}`);
            return true;
          }
          // Also check if it's a regular question without parent
          if (!questionParent && !key.startsWith('child_') && !key.startsWith('option_child_')) {
            console.log(`[buildQuestionTree] Found root question (no parent): ${key}`);
            return true;
          }
          return false;
        }

        // For child questions, check if parent matches
        // Primary check: parentQuestionCode matches parentKey
        if (questionParent === parentKey) {
          console.log(`[buildQuestionTree] Found child by parentQuestionCode match: ${key} (parent: ${questionParent})`);
          return true;
        }

        // Secondary check: key pattern matches
        // Format: child_${parentKey}_${timestamp}_${randomId}
        if (key.startsWith(`child_${parentKey}_`)) {
          console.log(`[buildQuestionTree] Found child by key pattern: ${key} (matches child_${parentKey}_)`);
          return true;
        }

        // Check for option-based children
        // Format: option_child_${parentKey}_${optionIndex}_${timestamp}_${randomId}
        if (key.startsWith(`option_child_${parentKey}_`)) {
          console.log(`[buildQuestionTree] Found option child by key pattern: ${key}`);
          return true;
        }

        return false;
      })
      .map(([key, question]) => {
        // Get the question key (code) - use existing code or the key itself as identifier
        const questionKey = question.questionCode || question.QuestionCode || key;
        const questionParent = question.parentQuestionCode || question.ParentQuestionCode;

        return {
          key,
          questionKey,
          questionParent,
          question,
        };
      });

    console.log(`[buildQuestionTree] Found ${relevantQuestions.length} relevant questions for parentKey: ${parentKey}`);

    // Sort by display order or index
    relevantQuestions.sort((a, b) => {
      const orderA = a.question.displayOrder || a.question.DisplayOrder || a.question.index || 0;
      const orderB = b.question.displayOrder || b.question.DisplayOrder || b.question.index || 0;
      return orderA - orderB;
    });

    // Build tree nodes
    relevantQuestions.forEach(({ key, questionKey, questionParent, question }) => {
      // Only include questions that have required fields
      if (!question.questionText && !question.QuestionText) {
        console.log(`[buildQuestionTree] Skipping incomplete question: ${key} (no questionText)`);
        return; // Skip incomplete questions
      }

      if (!question.questionTypeCode && !question.QuestionTypeCode) {
        console.log(`[buildQuestionTree] Skipping question without type: ${key}`);
        return; // Skip questions without type
      }

      // Use the key as the identifier for finding children (since children reference parent by key)
      const nodeKey = key; // Use the actual key from newQuestions

      console.log(`[buildQuestionTree] Processing question: ${key}, nodeKey: ${nodeKey}, questionText: ${question.questionText || question.QuestionText}`);

      // Recursively build children BEFORE creating the node
      const children = buildQuestionTree(questionsMap, nodeKey);

      console.log(`[buildQuestionTree] Question ${key} has ${children.length} children`);

      const node = {
        questionCode: question.questionCode || question.QuestionCode || null,
        questionTypeCode: question.questionTypeCode || question.QuestionTypeCode || '',
        parentQuestionCode: questionParent || null,
        questionText: question.questionText || question.QuestionText || '',
        questionAnsText: question.questionAnsText || question.QuestionAnsText || null,
        optionsData: question.optionsData || null, // Store full options structure with nested children
        displayOrder: question.displayOrder || question.DisplayOrder || question.index || 0,
        isMandatory: question.isMandatory !== undefined ? question.isMandatory : false,
        isConditional: question.isConditional !== undefined ? question.isConditional : false,
        conditionalAnswerValue: question.conditionalAnswerValue || question.ConditionalAnswerValue || null,
        children: children, // Use the pre-built children array
      };

      tree.push(node);
    });

    console.log(`[buildQuestionTree] Returning ${tree.length} nodes for parentKey: ${parentKey}`);
    return tree;
  };

  // Generate JSON structure in the format required for Greenlam.Questions table
  const generateQuestionJsonStructure = (node, level = 1) => {
    const mapQuestionType = (typeCode) =>
      mapChecklistQuestionTypeCodeToName(typeCode, questionTypes);

    const questionType = mapQuestionType(node.questionTypeCode || node.QuestionTypeCode);
    const result = {
      questionText: node.questionText || node.QuestionText || "",
      questionType: questionType,
      levelNo: level,
      isMandatory: node.isMandatory !== undefined ? node.isMandatory : (node.IsMandatory !== undefined ? node.IsMandatory : false),
      children: []
    };

    // Process ALL children recursively - this is the key fix
    if (node.children && node.children.length > 0) {
      // Recursively process all children
      result.children = node.children.map(child => generateQuestionJsonStructure(child, level + 1));
    }

    // For option-based types (including Group Check Box / Group Radio Button), extract options
    if (isChecklistOptionBasedQuestionTypeName(questionType)) {
      // Helper function to convert option object to JSON structure recursively
      const convertOptionToJson = (option, optLevel) => {
        console.log(`[convertOptionToJson] Converting option:`, {
          text: option.text,
          typeCode: option.typeCode,
          hasChildren: !!(option.children && option.children.length > 0),
          childrenCount: option.children?.length || 0
        });
        
        const optionType = option.typeCode 
          ? mapQuestionType(option.typeCode)
          : "Label";
        
        console.log(`[convertOptionToJson] Mapped type: ${option.typeCode} -> ${optionType}`);
        
        const optionResult = {
          questionText: option.text || "",
          questionType: optionType,
          levelNo: optLevel,
          isMandatory: option.isMandatory !== undefined ? option.isMandatory : false,
          children: []
        };

        // Recursively process nested children (sub-options or child questions)
        if (option.children && option.children.length > 0) {
          console.log(`[convertOptionToJson] Processing ${option.children.length} nested children for option: ${option.text}`);
          optionResult.children = option.children.map(child => convertOptionToJson(child, optLevel + 1));
          console.log(`[convertOptionToJson] Generated ${optionResult.children.length} children for option: ${option.text}`);
        }

        return optionResult;
      };

      // Try to use optionsData first (full structure with nested children)
      if (node.optionsData && Array.isArray(node.optionsData) && node.optionsData.length > 0) {
        console.log(`[generateQuestionJsonStructure] Using optionsData for nested children:`, node.optionsData);
        const optionChildren = node.optionsData.map(option => convertOptionToJson(option, level + 1));
        // Merge options with existing children (options first, then regular children)
        result.children = [...optionChildren, ...result.children];
      }
      // Fallback to questionAnsText (comma-separated text only)
      else if (node.questionAnsText || node.QuestionAnsText) {
        const optionsText = node.questionAnsText || node.QuestionAnsText;
        const options = optionsText.split(",").map(opt => opt.trim()).filter(opt => opt);

        if (options.length > 0) {
          const optionChildren = options.map(optionText => ({
            questionText: optionText,
            questionType: "Label",
            levelNo: level + 1,
            isMandatory: false, // Default to Non Mandatory for options from questionAnsText
            children: []
          }));

          // Merge options with existing children (options first, then regular children)
          result.children = [...optionChildren, ...result.children];
        }
      }
    }

    return result;
  };

  // Convert question tree to the JSON format for Greenlam.Questions
  // Returns an array when there are multiple root questions, or a single object for one root question
  const convertToQuestionDtoFormat = (questionTree) => {
    if (!questionTree || questionTree.length === 0) return null;

    // Process ALL root questions, not just the first one
    console.log(`[convertToQuestionDtoFormat] Processing ${questionTree.length} root question(s)`);
    
    // Generate JSON for all root questions
    const allRootQuestions = questionTree.map((rootQuestion, index) => {
      console.log(`[convertToQuestionDtoFormat] Processing root question ${index + 1}:`, rootQuestion);
      console.log(`[convertToQuestionDtoFormat] Root question ${index + 1} children:`, rootQuestion.children);
      console.log(`[convertToQuestionDtoFormat] Root question ${index + 1} children count:`, rootQuestion.children?.length || 0);
      const json = generateQuestionJsonStructure(rootQuestion, 1);
      console.log(`[convertToQuestionDtoFormat] Generated JSON for root question ${index + 1}:`, json);
      return json;
    });

    // Return array if multiple root questions, single object if one
    if (allRootQuestions.length === 1) {
      console.log("[convertToQuestionDtoFormat] Returning single root question JSON");
      return allRootQuestions[0];
    } else {
      console.log(`[convertToQuestionDtoFormat] Returning array of ${allRootQuestions.length} root questions`);
      return allRootQuestions;
    }
  };

  /** Children array from API / local tree (supports PascalCase). */
  const getQuestionTreeChildNodes = (node) => {
    if (!node || typeof node !== "object") return [];
    const c = node.children ?? node.Children;
    return Array.isArray(c) ? c : [];
  };

  /**
   * Full export JSON: every node includes codes, parent link, conditional fields, and nested children.
   * Separate from convertToQuestionDtoFormat (used for save) so API payloads stay unchanged.
   */
  const generateQuestionJsonExportStructure = (node, level = 1, parentQuestionCodeFallback = null) => {
    const mapQuestionType = (typeCode) =>
      mapChecklistQuestionTypeCodeToName(typeCode, questionTypes);

    const qc =
      node.questionCode ?? node.QuestionCode ?? node.QuestionCODE ?? null;
    const pqc =
      node.parentQuestionCode ??
      node.ParentQuestionCode ??
      node.ParentQuestionCODE ??
      parentQuestionCodeFallback;
    const typeCodeRaw = node.questionTypeCode || node.QuestionTypeCode || "";
    const questionType = mapQuestionType(typeCodeRaw);

    const result = {
      questionCode: qc,
      parentQuestionCode: pqc || null,
      questionText: node.questionText || node.QuestionText || "",
      questionTypeCode: typeCodeRaw || null,
      srTypeCode: node.srTypeCode || node.SRTypeCode || null,
      questionType,
      questionAnsText: node.questionAnsText || node.QuestionAnsText || null,
      displayOrder: node.displayOrder ?? node.DisplayOrder ?? null,
      isMandatory:
        node.isMandatory !== undefined
          ? node.isMandatory
          : node.IsMandatory !== undefined
            ? node.IsMandatory
            : true,
      isConditional:
        node.isConditional !== undefined
          ? node.isConditional
          : node.IsConditional !== undefined
            ? node.IsConditional
            : false,
      conditionalAnswerValue:
        node.conditionalAnswerValue ?? node.ConditionalAnswerValue ?? null,
      levelNo: level,
      children: [],
    };

    const kids = getQuestionTreeChildNodes(node);
    if (kids.length > 0) {
      result.children = kids.map((child) =>
        generateQuestionJsonExportStructure(child, level + 1, qc || pqc || null)
      );
    }

    if (isChecklistOptionBasedQuestionTypeName(questionType)) {
      const convertOptionToJsonExport = (option, optLevel, branchParentCode) => {
        const optionType = option.typeCode ? mapQuestionType(option.typeCode) : "Label";
        const optRes = {
          optionId: option.id || null,
          parentQuestionCode: branchParentCode || null,
          questionText: option.text || "",
          questionTypeCode: option.typeCode || null,
          srTypeCode: option.srTypeCode || null,
          questionType: optionType,
          levelNo: optLevel,
          isMandatory: option.isMandatory !== undefined ? option.isMandatory : false,
          children: [],
        };
        if (option.children && option.children.length > 0) {
          optRes.children = option.children.map((child) =>
            convertOptionToJsonExport(child, optLevel + 1, branchParentCode)
          );
        }
        return optRes;
      };

      if (node.optionsData && Array.isArray(node.optionsData) && node.optionsData.length > 0) {
        const optionChildren = node.optionsData.map((option) =>
          convertOptionToJsonExport(option, level + 1, qc)
        );
        result.children = [...optionChildren, ...result.children];
      } else if (node.questionAnsText || node.QuestionAnsText) {
        const optionsText = node.questionAnsText || node.QuestionAnsText;
        const options = optionsText.split(",").map((opt) => opt.trim()).filter(Boolean);
        if (options.length > 0) {
          const optionChildren = options.map((optionText) => ({
            parentQuestionCode: qc,
            questionText: optionText,
            questionTypeCode: null,
            questionType: "Label",
            levelNo: level + 1,
            isMandatory: false,
            children: [],
          }));
          result.children = [...optionChildren, ...result.children];
        }
      }
    }

    return result;
  };

  const buildChecklistQuestionTreeFullExport = (roots) => ({
    exportKind: "checklistQuestionTreeFull",
    templateCode: templateCode || null,
    templateName: template?.templateName || template?.TemplateName || null,
    questions: (roots || []).map((r) => generateQuestionJsonExportStructure(r, 1, null)),
  });

  const escapeHtml = (str) =>
    String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  // Debug function to log current newQuestions state
  const logNewQuestionsState = () => {
    console.log("=== CURRENT newQuestions STATE ===");
    console.log("Total keys:", Object.keys(newQuestions).length);
    console.log("All keys:", Object.keys(newQuestions));
    Object.entries(newQuestions).forEach(([key, question]) => {
      console.log(`Key: ${key}`, {
        questionText: question.questionText || question.QuestionText || "MISSING",
        questionTypeCode: question.questionTypeCode || question.QuestionTypeCode || "MISSING",
        parentQuestionCode: question.parentQuestionCode || question.ParentQuestionCode || "NULL",
        level: question.level || "N/A",
        hasQuestionText: !!(question.questionText || question.QuestionText),
        hasQuestionTypeCode: !!(question.questionTypeCode || question.QuestionTypeCode),
        isComplete: !!(question.questionText || question.QuestionText) && !!(question.questionTypeCode || question.QuestionTypeCode)
      });
    });
    console.log("=== END newQuestions STATE ===");
  };

  // Generate and display JSON structure
  const handleGenerateJson = () => {
    // Log current state before processing
    logNewQuestionsState();



    try {
      // Collect all new questions that are complete
      const completeNewQuestions = {};
      console.log(`[handleGenerateJson] All newQuestions before filtering:`, newQuestions);
      console.log(`[handleGenerateJson] All newQuestions keys:`, Object.keys(newQuestions));
      Object.entries(newQuestions).forEach(([key, question]) => {
        console.log(`[handleGenerateJson] Checking question ${key}:`, {
          hasQuestionText: !!(question.questionText || question.QuestionText),
          hasQuestionTypeCode: !!(question.questionTypeCode || question.QuestionTypeCode),
          questionText: question.questionText || question.QuestionText,
          questionTypeCode: question.questionTypeCode || question.QuestionTypeCode,
          parentQuestionCode: question.parentQuestionCode || question.ParentQuestionCode
        });
        if (question.questionText || question.QuestionText) {
          if (question.questionTypeCode || question.QuestionTypeCode) {
            completeNewQuestions[key] = question;
            console.log(`[handleGenerateJson] Added ${key} to completeNewQuestions`);
          } else {
            console.log(`[handleGenerateJson] Skipped ${key} - missing questionTypeCode`);
          }
        } else {
          console.log(`[handleGenerateJson] Skipped ${key} - missing questionText`);
        }
      });

      console.log("Complete new questions:", completeNewQuestions);
      console.log("Complete new questions count:", Object.keys(completeNewQuestions).length);
      console.log("All newQuestions keys:", Object.keys(newQuestions));

      // Debug: Log all questions and their parentQuestionCode
      console.log("=== DEBUGGING QUESTION STRUCTURE ===");
      Object.entries(newQuestions).forEach(([key, question]) => {
        console.log(`Question Key: ${key}`);
        console.log(`  - questionText: ${question.questionText || question.QuestionText || 'MISSING'}`);
        console.log(`  - questionTypeCode: ${question.questionTypeCode || question.QuestionTypeCode || 'MISSING'}`);
        console.log(`  - parentQuestionCode: ${question.parentQuestionCode || question.ParentQuestionCode || 'NULL'}`);
        console.log(`  - level: ${question.level || 'N/A'}`);
      });
      console.log("=== END DEBUGGING ===");

      // Prefer saved hierarchy (full parent/child tree from API). Draft-only when nothing saved yet.
      let questionTreeToUse = [];
      if (questionTree.length > 0) {
        console.log("Using saved questionTree (full hierarchy) for JSON export...");
        questionTreeToUse = questionTree;
      } else if (Object.keys(completeNewQuestions).length > 0) {
        console.log("Building tree from newQuestions (no saved tree yet)...");
        Object.entries(completeNewQuestions).forEach(([key, question]) => {
          console.log(`Complete Question Key: ${key}`);
          console.log(`  - questionText: ${question.questionText || question.QuestionText}`);
          console.log(`  - questionTypeCode: ${question.questionTypeCode || question.QuestionTypeCode}`);
          console.log(`  - parentQuestionCode: ${question.parentQuestionCode || question.ParentQuestionCode || 'NULL'}`);
        });
        questionTreeToUse = buildQuestionTree(completeNewQuestions);
        console.log("Built tree:", questionTreeToUse);
      } else {
        Swal.fire({
          icon: "warning",
          title: "No Questions",
          text: "Please create at least one question before generating JSON",
        });
        return;
      }

      if (questionTreeToUse.length > 0) {
        const exportPayload = buildChecklistQuestionTreeFullExport(questionTreeToUse);
        const jsonString = JSON.stringify(exportPayload, null, 2);

        if (exportPayload.questions?.length) {
          const jsonStringSafe = escapeHtml(jsonString);

          Swal.fire({
            title: "Full checklist JSON (all roots, parents & children)",
            html: `
              <div style="text-align: left; max-height: 500px; overflow-y: auto;">
                <pre style="background: #f5f5f5; padding: 15px; border-radius: 5px; font-size: 12px; white-space: pre-wrap; word-wrap: break-word;">${jsonStringSafe}</pre>
              </div>
            `,
            width: "800px",
            showCancelButton: true,
            confirmButtonText: "Copy to Clipboard",
            cancelButtonText: "Close",
            customClass: {
              popup: "json-modal",
            },
          }).then((result) => {
            if (result.isConfirmed) {
              navigator.clipboard.writeText(jsonString).then(() => {
                Swal.fire({
                  icon: "success",
                  title: "Copied!",
                  text: "JSON structure copied to clipboard",
                  timer: 1500,
                  showConfirmButton: false,
                });
              }).catch(() => {
                Swal.fire({
                  icon: "error",
                  title: "Error",
                  text: "Failed to copy to clipboard",
                });
              });
            }
          });

          console.log("Generated full export JSON:", exportPayload);
        } else {
          Swal.fire({
            icon: "warning",
            title: "No Data",
            text: "Could not generate JSON structure from questions",
          });
        }
      } else {
        Swal.fire({
          icon: "warning",
          title: "No Questions",
          text: "Please create at least one question before generating JSON",
        });
      }
    } catch (error) {
      console.error("Error generating JSON:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to generate JSON structure",
      });
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    const tcTrim = String(templateCode || "").trim();
    if (!tcTrim) {
      await Swal.fire({
        icon: "warning",
        title: "Missing template",
        text: "Cannot save without a template. Open this page from Checklist Templates with ?templateCode=... in the URL.",
        confirmButtonText: "OK",
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const hasUnsavedOptionDraftWork = (q) => {
        const opts = Array.isArray(q?.optionsData) ? q.optionsData : [];
        if (opts.length === 0) return false;
        const hasNewOption = opts.some((o) => !String(o?.id || "").startsWith("opt_saved_"));
        const hasNewNested = opts.some((o) => {
          const saved = String(o?.id || "").startsWith("opt_saved_");
          if (!saved) return false;
          const kids = Array.isArray(o.children) ? o.children : [];
          const containsNew = (arr) =>
            arr.some((c) => {
              const id = String(c?.id || "");
              if (!id.startsWith("opt_saved_")) return true;
              return Array.isArray(c.children) && containsNew(c.children);
            });
          return kids.length > 0 && containsNew(kids);
        });
        const hasDeletedSavedOptions =
          Array.isArray(q?.__deletedOptionCodes) && q.__deletedOptionCodes.length > 0;
        return hasNewOption || hasNewNested || hasDeletedSavedOptions;
      };

      const hasDraftEntries = Object.values(newQuestions || {}).some((q) => {
        if (!q || typeof q !== "object") return false;
        const isExistingDraft = Boolean(q?.__existingParentDraft);
        const isDirtyExistingDraft = isExistingDraft && Boolean(q?.__dirty);
        if (isExistingDraft && isDirtyExistingDraft && hasUnsavedOptionDraftWork(q)) return true;
        const qText = String(
          q?.questionText || q?.QuestionText || q?.conditionalAnswerValue || q?.ConditionalAnswerValue || ""
        ).trim();
        const qType = String(q?.questionTypeCode || q?.QuestionTypeCode || "").trim();
        if (qText && qType && (!isExistingDraft || isDirtyExistingDraft)) return true;
        if (Array.isArray(q?.__deletedOptionCodes) && q.__deletedOptionCodes.length > 0) return true;
        if (Array.isArray(q?.optionsData)) {
          return q.optionsData.some((opt) => {
            const id = String(opt?.id || "").trim();
            if (!id || !id.startsWith("opt_saved_")) return true;
            const kids = Array.isArray(opt?.children) ? opt.children : [];
            return kids.some((child) => {
              const childId = String(child?.id || "").trim();
              return !childId || !childId.startsWith("opt_saved_");
            });
          });
        }
        return false;
      });
      const hasPendingDeleteMarks = (deletedQuestionCodes?.size || 0) > 0;
      const hasAnyDirtyDraft = Object.values(newQuestions || {}).some(
        (q) => q && typeof q === "object" && Boolean(q.__dirty)
      );
      if (
        hasRecentReorderSaved &&
        !hasDraftEntries &&
        !hasPendingDeleteMarks &&
        !hasAnyDirtyDraft
      ) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Question reordering has been saved successfully",
          timer: 1800,
          showConfirmButton: false,
        });
        setHasRecentReorderSaved(false);
        return;
      }

      // First, validate all questions have required fields
      const validationErrors = [];
      const questionsWithoutType = [];
      const questionsWithoutSrCategory = [];
      const questionsWithoutText = [];
      const questionsWithoutMandatory = [];
      const optionsWithoutType = [];
      const optionsWithoutSrCategory = [];
      
      const optionBasedTypeCodes = new Set(["QT001", "QT003", "QT004", "QT006", "QT011", "QT012"]);

      // Helper function to recursively check options for types
      const checkOptionsForTypes = (options, questionText, questionKey, parentQuestionTypeCode = "", path = "") => {
        if (!options || !Array.isArray(options)) return;
        
        options.forEach((option, index) => {
          const optionText = option.text || "";
          const optionTypeCode = option.typeCode || "";
          const optionSrTypeCode = option.srTypeCode || "";
          const optionPath = path ? `${path} > Option ${index + 1}` : `Option ${index + 1}`;
          const parentTypeCodeNorm = String(parentQuestionTypeCode || "").trim().toUpperCase();
          const isOptionBasedParent = optionBasedTypeCodes.has(parentTypeCodeNorm);
          const hasChildren = Array.isArray(option.children) && option.children.length > 0;
          
          if (optionText.trim()) {
            // For option-based parents (Dropdown/Radio/Checkbox/Multiple Choice),
            // plain leaf option labels are valid even when typeCode is missing.
            // We treat them as Label-like option rows and avoid false validation errors.
            if (!optionTypeCode || optionTypeCode.trim() === "") {
              if (isOptionBasedParent && !hasChildren) {
                return;
              }
              optionsWithoutType.push({
                questionKey,
                questionText,
                optionText: optionText.trim(),
                path: optionPath
              });
            }
            if (parseSrTypeCodes(optionSrTypeCode).length === 0) {
              if (isOptionBasedParent && !hasChildren) {
                return;
              }
              optionsWithoutSrCategory.push({
                questionKey,
                questionText,
                optionText: optionText.trim(),
                path: optionPath
              });
            }
          }
          
          // Recursively check children
          if (option.children && option.children.length > 0) {
            checkOptionsForTypes(
              option.children,
              questionText,
              questionKey,
              optionTypeCode || parentQuestionTypeCode,
              optionPath
            );
          }
        });
      };
      
      Object.entries(newQuestions).forEach(([key, question]) => {
        const questionText = question.questionText || question.QuestionText || "";
        const questionTypeCode = question.questionTypeCode || question.QuestionTypeCode || "";
        const questionSrTypeCode = question.srTypeCode || question.SRTypeCode || "";
        
        if (questionText.trim()) {
          // Question has text, so it should have a type
          if (!questionTypeCode || questionTypeCode.trim() === "") {
            questionsWithoutType.push({
              key,
              questionText: questionText.trim()
            });
          }

          if (parseSrTypeCodes(questionSrTypeCode).length === 0) {
            questionsWithoutSrCategory.push({
              key,
              questionText: questionText.trim()
            });
          }
          
          // Question has text, so it should have mandatory dropdown selected
          const questionIsMandatory = question.isMandatory !== undefined ? question.isMandatory : (question.IsMandatory !== undefined ? question.IsMandatory : undefined);
          if (questionIsMandatory === undefined) {
            questionsWithoutMandatory.push({
              key,
              questionText: questionText.trim()
            });
          }
          
          // Check options if they exist
          if (question.optionsData && Array.isArray(question.optionsData)) {
            checkOptionsForTypes(question.optionsData, questionText.trim(), key, questionTypeCode);
          }
        } else if (questionTypeCode) {
          // Question has type but no text
          questionsWithoutText.push({
            key,
            questionTypeCode
          });
        }
      });

      // Show validation errors if any
      if (questionsWithoutType.length > 0) {
        const questionList = questionsWithoutType
          .map(q => `"${q.questionText.substring(0, 50)}${q.questionText.length > 50 ? '...' : ''}"`)
          .join('\n');
        
        await Swal.fire({
          icon: "error",
          title: "Validation Error",
          html: `
            <div style="text-align: left;">
              <p><strong>Please select a question type for the following question(s):</strong></p>
              <pre style="background: #f5f5f5; padding: 10px; border-radius: 5px; max-height: 200px; overflow-y: auto; font-size: 12px;">${escapeHtml(questionList)}</pre>
            </div>
          `,
          confirmButtonText: "OK"
        });
        return;
      }

      if (questionsWithoutSrCategory.length > 0) {
        const questionList = questionsWithoutSrCategory
          .map(q => `"${q.questionText.substring(0, 50)}${q.questionText.length > 50 ? '...' : ''}"`)
          .join('\n');

        await Swal.fire({
          icon: "error",
          title: "Validation Error",
          html: `
            <div style="text-align: left;">
              <p><strong>Please select at least one SR Category for the following question(s):</strong></p>
              <pre style="background: #f5f5f5; padding: 10px; border-radius: 5px; max-height: 200px; overflow-y: auto; font-size: 12px;">${escapeHtml(questionList)}</pre>
            </div>
          `,
          confirmButtonText: "OK"
        });
        return;
      }

      if (questionsWithoutText.length > 0) {
        await Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Please enter question text for all questions with selected types",
          confirmButtonText: "OK"
        });
        return;
      }
      
      // Check for questions without mandatory dropdown
      if (questionsWithoutMandatory.length > 0) {
        const questionList = questionsWithoutMandatory
          .map(q => `"${q.questionText.substring(0, 50)}${q.questionText.length > 50 ? '...' : ''}"`)
          .join('\n');
        
        await Swal.fire({
          icon: "error",
          title: "Validation Error",
          html: `
            <div style="text-align: left;">
              <p><strong>Please select mandatory option for the following question(s):</strong></p>
              <pre style="background: #f5f5f5; padding: 10px; border-radius: 5px; max-height: 200px; overflow-y: auto; font-size: 12px;">${escapeHtml(questionList)}</pre>
            </div>
          `,
          confirmButtonText: "OK"
        });
        return;
      }
      
      // Check for options without types
      if (optionsWithoutType.length > 0) {
        const optionList = optionsWithoutType
          .map(opt => `Question: "${opt.questionText.substring(0, 30)}${opt.questionText.length > 30 ? '...' : ''}"\n  ${opt.path}: "${opt.optionText.substring(0, 40)}${opt.optionText.length > 40 ? '...' : ''}"`)
          .join('\n\n');
        
        await Swal.fire({
          icon: "error",
          title: "Validation Error",
          html: `
            <div style="text-align: left;">
              <p><strong>Please select a question type for the following option(s):</strong></p>
              <pre style="background: #f5f5f5; padding: 10px; border-radius: 5px; max-height: 200px; overflow-y: auto; font-size: 12px;">${escapeHtml(optionList)}</pre>
            </div>
          `,
          confirmButtonText: "OK"
        });
        return;
      }

      if (optionsWithoutSrCategory.length > 0) {
        const optionList = optionsWithoutSrCategory
          .map(opt => `Question: "${opt.questionText.substring(0, 30)}${opt.questionText.length > 30 ? '...' : ''}"\n  ${opt.path}: "${opt.optionText.substring(0, 40)}${opt.optionText.length > 40 ? '...' : ''}"`)
          .join('\n\n');

        await Swal.fire({
          icon: "error",
          title: "Validation Error",
          html: `
            <div style="text-align: left;">
              <p><strong>Please select at least one SR Category for the following option(s):</strong></p>
              <pre style="background: #f5f5f5; padding: 10px; border-radius: 5px; max-height: 200px; overflow-y: auto; font-size: 12px;">${escapeHtml(optionList)}</pre>
            </div>
          `,
          confirmButtonText: "OK"
        });
        return;
      }

      // Collect all new questions that are complete (have questionText and questionTypeCode)
      const completeNewQuestions = {};
      Object.entries(newQuestions).forEach(([key, question]) => {
        // Drafts for already-saved parents are handled separately (append-only behavior)
        if (question?.__existingParentDraft) return;
        const qText =
          (question.questionText || question.QuestionText || "").trim() ||
          // Some edit-mode "option_child_*" drafts may carry the label in ConditionalAnswerValue first
          String(question.conditionalAnswerValue || question.ConditionalAnswerValue || "").trim();
        const qType = (question.questionTypeCode || question.QuestionTypeCode || "").trim();
        if (qText && qType) {
          // Ensure we persist questionText for downstream save logic
          completeNewQuestions[key] = {
            ...question,
            questionText: (question.questionText || question.QuestionText || qText),
          };
        }
      });

      // Exact edit-mode snapshot save:
      // Persist exactly what is currently in UI (existing tree minus deleted + current draft additions)
      // in a single replace-style flow.
      const snapshotDraftQuestions = {};
      Object.entries(newQuestions).forEach(([key, q]) => {
        if (!q || typeof q !== "object") return;
        let qText =
          (q?.questionText || q?.QuestionText || "").trim() ||
          String(q?.conditionalAnswerValue || q?.ConditionalAnswerValue || "").trim();
        let qType = String(q?.questionTypeCode || q?.QuestionTypeCode || "").trim();
        const hasOptionData = Array.isArray(q?.optionsData) && q.optionsData.length > 0;
        if ((!qText || !qType) && q.__dirty && q.__existingParentDraft) {
          const code = String(q.questionCode || q.QuestionCode || key || "").trim();
          if (code) {
            const fromApi = (Array.isArray(questions) ? questions : []).find(
              (row) => getQuestionCodeValue(row) === code
            );
            if (fromApi) {
              if (!qText) qText = String(fromApi.questionText || fromApi.QuestionText || "").trim();
              if (!qType) qType = String(fromApi.questionTypeCode || fromApi.QuestionTypeCode || "").trim();
            }
          }
        }
        const include =
          (qText && qType) ||
          hasOptionData ||
          (Boolean(q.__existingParentDraft && q.__dirty) && (hasOptionData || Boolean(qText)));
        if (!include) return;
        snapshotDraftQuestions[key] = {
          ...q,
          questionText: q?.questionText ?? q?.QuestionText ?? qText,
          questionTypeCode: q?.questionTypeCode ?? q?.QuestionTypeCode ?? qType,
          srTypeCode: q?.srTypeCode ?? q?.SRTypeCode ?? null,
        };
      });

      const hasExistingTree = Array.isArray(questionTree) && questionTree.length > 0;
      const editModeHasStructuralWork =
        hasDraftEntries ||
        hasPendingDeleteMarks ||
        hasRecentReorderSaved ||
        hasAnyDirtyDraft ||
        Object.keys(snapshotDraftQuestions).length > 0;
      // Edit mode (template already has saved questions): one full tree payload via
      // createQuestionTree — same shape as add-mode — after deleting existing rows.
      // Supports add/remove/edit at any depth and any question type the UI can show.
      // Add mode (no existing tree): unchanged — still uses save-question / draft flow below.
      // Primary snapshot save via backend tree-upsert (add/edit/delete):
      // existing QuestionCode rows are updated, new rows inserted under parent, removed rows deleted.
      // This keeps one authoritative save path and avoids mixing with legacy saveQuestion flow.
      const useEditModeFullTreeSave = editModeHasStructuralWork;
      if (useEditModeFullTreeSave) {
        const deletedSet = new Set([...deletedQuestionCodes].map((c) => String(c || "").trim()).filter(Boolean));

        const cloneVisibleTree = (nodes = []) =>
          (nodes || [])
            .filter((n) => !deletedSet.has(getQuestionCodeValue(n)))
            .map((n) => ({
              ...n,
              children: cloneVisibleTree(Array.isArray(n.children) ? n.children : []),
            }));

        const visibleRoots = cloneVisibleTree(questionTree);
        const nodeByKey = new Map();
        const treeNodeKey = (c) => String(c ?? "").trim().toUpperCase();

        const indexNode = (node) => {
          if (!node) return;
          const code = getQuestionCodeValue(node);
          if (code) nodeByKey.set(treeNodeKey(code), node);
          const kids = Array.isArray(node.children) ? node.children : [];
          kids.forEach(indexNode);
        };
        visibleRoots.forEach(indexNode);

        const getFlatRowByQuestionCode = (qc) => {
          const k = treeNodeKey(qc);
          if (!k) return null;
          const list = Array.isArray(questions) ? questions : [];
          return (
            list.find((r) => treeNodeKey(getQuestionCodeValue(r)) === k) || null
          );
        };

        const resolveParentCodeForDraft = (node) => {
          let p = String(node.parentQuestionCode || "").trim();
          if (p) return p;
          const selfCode = String(node.questionCode || "").trim();
          if (!selfCode) return "";
          const flat = getFlatRowByQuestionCode(selfCode);
          if (!flat) return "";
          return String(
            flat.parentQuestionCode ||
              flat.ParentQuestionCode ||
              flat.ParentQuestionCODE ||
              ""
          ).trim();
        };

        const draftNodeByKey = new Map();
        Object.entries(snapshotDraftQuestions).forEach(([key, q]) => {
          draftNodeByKey.set(key, {
            questionCode: q.questionCode || q.QuestionCode || null,
            questionTypeCode: q.questionTypeCode || q.QuestionTypeCode || "",
            srTypeCode: q.srTypeCode || q.SRTypeCode || null,
            parentQuestionCode:
              q.parentQuestionCode || q.ParentQuestionCode || q.ParentQuestionCODE || null,
            questionText: q.questionText || q.QuestionText || "",
            questionAnsText: q.questionAnsText || q.QuestionAnsText || null,
            optionsData: q.optionsData || null,
            displayOrder: q.displayOrder || q.DisplayOrder || q.index || 0,
            isMandatory: q.isMandatory !== undefined ? q.isMandatory : false,
            isConditional: q.isConditional !== undefined ? q.isConditional : false,
            conditionalAnswerValue: q.conditionalAnswerValue || q.ConditionalAnswerValue || null,
            children: [],
            __draftKey: key,
          });
        });

        // Do NOT register draft objects into nodeByKey: that makes the patch pass treat
        // new roots (e.g. key root_2) as existingNode === draft and mark them "patched",
        // so the attach pass never pushes them onto mergedRoots and new questions are lost.
        const mergedRoots = [...visibleRoots];
        const patchedExistingDraftKeys = new Set();
        const isEphemeralDraftKey = (k) =>
          typeof k === "string" &&
          (k.startsWith("root_") ||
            k.startsWith("child_") ||
            k.startsWith("option_child_"));
        draftNodeByKey.forEach((node, key) => {
          const persisted = String(node.questionCode || "").trim();
          // Client-only draft keys must never patch via persisted QuestionCode alone:
          // a stray questionCode on a new root could match a saved node, merge into it,
          // and skip the attach pass so the new root never reaches mergedRoots (edit-mode save).
          const existingNode =
            nodeByKey.get(treeNodeKey(key)) ||
            (!isEphemeralDraftKey(key) && persisted
              ? nodeByKey.get(treeNodeKey(persisted))
              : null);
          if (!existingNode) return;
          patchedExistingDraftKeys.add(key);
          if (node.questionText !== undefined) existingNode.questionText = node.questionText;
          if (node.questionTypeCode !== undefined) existingNode.questionTypeCode = node.questionTypeCode;
          if (node.srTypeCode !== undefined) existingNode.srTypeCode = node.srTypeCode;
          if (node.isMandatory !== undefined) existingNode.isMandatory = node.isMandatory;
          if (node.questionAnsText !== undefined) existingNode.questionAnsText = node.questionAnsText;
          if (node.optionsData !== undefined) existingNode.optionsData = node.optionsData;
          if (node.conditionalAnswerValue !== undefined) {
            existingNode.conditionalAnswerValue = node.conditionalAnswerValue;
          }
        });
        draftNodeByKey.forEach((node, key) => {
          if (patchedExistingDraftKeys.has(key)) return;
          const parentRaw = resolveParentCodeForDraft(node);
          if (!parentRaw) {
            mergedRoots.push(node);
            return;
          }
          const parentNode = nodeByKey.get(treeNodeKey(parentRaw));
          if (parentNode) {
            if (!Array.isArray(parentNode.children)) parentNode.children = [];
            parentNode.children.push(node);
          } else {
            // If parent isn't present in snapshot, keep as root so data is not lost.
            mergedRoots.push(node);
          }
        });

        const applyPersistedCodeDrafts = (nodes) => {
          for (const n of nodes || []) {
            if (!n) continue;
            const code = getQuestionCodeValue(n);
            const d = code && newQuestions[code] ? newQuestions[code] : null;
            if (d && typeof d === "object") {
              if (d.questionText !== undefined && d.questionText !== null) {
                n.questionText = d.questionText;
              }
              if (d.QuestionText !== undefined && d.QuestionText !== null) {
                n.questionText = d.QuestionText;
              }
              if (d.questionTypeCode !== undefined && String(d.questionTypeCode).trim() !== "") {
                n.questionTypeCode = d.questionTypeCode;
              }
              if (d.QuestionTypeCode !== undefined && String(d.QuestionTypeCode).trim() !== "") {
                n.questionTypeCode = d.QuestionTypeCode;
              }
              if (d.srTypeCode !== undefined && String(d.srTypeCode).trim() !== "") {
                n.srTypeCode = d.srTypeCode;
              }
              if (d.SRTypeCode !== undefined && String(d.SRTypeCode).trim() !== "") {
                n.srTypeCode = d.SRTypeCode;
              }
              if (d.questionAnsText !== undefined || d.QuestionAnsText !== undefined) {
                n.questionAnsText = d.questionAnsText ?? d.QuestionAnsText;
              }
              if (d.optionsData !== undefined) {
                n.optionsData = d.optionsData;
              }
              if (d.isMandatory !== undefined) {
                n.isMandatory = d.isMandatory;
              }
              if (d.conditionalAnswerValue !== undefined || d.ConditionalAnswerValue !== undefined) {
                n.conditionalAnswerValue = d.conditionalAnswerValue ?? d.ConditionalAnswerValue;
              }
            }
            if (Array.isArray(n.children) && n.children.length) {
              applyPersistedCodeDrafts(n.children);
            }
          }
        };
        applyPersistedCodeDrafts(mergedRoots);

        const stripDraftMeta = (nodes = []) =>
          (nodes || []).map((n) => {
            const { __draftKey, ...rest } = n;
            return {
              ...rest,
              children: stripDraftMeta(Array.isArray(rest.children) ? rest.children : []),
            };
          });

        const finalTreeForSave = stripDraftMeta(mergedRoots);

        // Do not pre-delete parent codes one-by-one here.
        // The tree-upsert backend performs snapshot sync delete (including recursive child removal),
        // so removing a parent from payload will delete that parent and all descendants safely.

        const resolveDisplayOrder = (node, fallback) => {
          const n = Number(node?.displayOrder ?? node?.DisplayOrder);
          return Number.isFinite(n) && n > 0 ? n : fallback;
        };

        const optionIdToCode = (id) => {
          const s = String(id || "");
          if (!s.startsWith("opt_saved_")) return null;
          const c = s.replace("opt_saved_", "").trim();
          return c || null;
        };

        // Build ChecklistQuestionTreeNode from API checklist rows OR from option UI nodes
        // ({ text, typeCode, id, children, optionsData }) so nested paths like
        // Radio → No → Special Text match add-mode / convertOptionToJson behavior.
        const toTreePayloadNode = (rawNode, displayOrder = 1) => {
          const src = rawNode || {};
          const questionTextNorm = String(
            src.questionText ?? src.QuestionText ?? src.text ?? ""
          ).trim();
          const typeCodeNorm = String(
            src.questionTypeCode ?? src.QuestionTypeCode ?? src.typeCode ?? ""
          ).trim();
          const srTypeCodeNorm = String(
            src.srTypeCode ?? src.SRTypeCode ?? ""
          ).trim();
          const persistedQuestionCode =
            getQuestionCodeValue(src) || optionIdToCode(src.id) || null;
          const ord = resolveDisplayOrder(src, displayOrder);

          const normalizeOptionText = (val) => String(val ?? "").trim().toLowerCase();
          const rawChildList = Array.isArray(src.children) ? src.children : [];

          // Preserve existing option QuestionCode when optionsData nodes lost their transient id
          // (common when question type changes to Dropdown and options are reconstructed by text).
          const existingOptionCodeBuckets = new Map();
          const existingOptionCodeByTextAndOrder = new Map();
          rawChildList.forEach((child) => {
            const code = getQuestionCodeValue(child);
            const txt = normalizeOptionText(child?.questionText || child?.QuestionText || child?.text || "");
            if (!code || !txt) return;
            if (!existingOptionCodeBuckets.has(txt)) existingOptionCodeBuckets.set(txt, []);
            existingOptionCodeBuckets.get(txt).push(code);
            const childOrder = Number(child?.displayOrder ?? child?.DisplayOrder ?? 0);
            if (childOrder > 0) {
              existingOptionCodeByTextAndOrder.set(`${txt}::${childOrder}`, code);
            }
          });
          const consumeExistingOptionCode = (txt, order) => {
            const key = normalizeOptionText(txt);
            const orderNo = Number(order ?? 0);
            if (orderNo > 0) {
              const strictKey = `${key}::${orderNo}`;
              const strictCode = existingOptionCodeByTextAndOrder.get(strictKey);
              if (strictCode) {
                existingOptionCodeByTextAndOrder.delete(strictKey);
                const bucket = existingOptionCodeBuckets.get(key);
                if (bucket && bucket.length > 0) {
                  const idx = bucket.indexOf(strictCode);
                  if (idx >= 0) bucket.splice(idx, 1);
                }
                return strictCode;
              }
            }
            const bucket = existingOptionCodeBuckets.get(key);
            if (!bucket || bucket.length === 0) return null;
            return bucket.shift() || null;
          };

          // Any type that uses the option builder (including Yes/No-style or custom types)
          // stores nested structure in optionsData — always serialize when present.
          const optionChildren =
            Array.isArray(src.optionsData) && src.optionsData.length > 0
              ? src.optionsData
                  .filter((opt) => String(opt?.text || "").trim())
                  .map((opt, idx) => {
                    const node = toTreePayloadNode(opt, idx + 1);
                    if (!node.questionCode) {
                      const preferredOrder = Number(opt?.displayOrder ?? opt?.DisplayOrder ?? idx + 1);
                      const matchedCode = consumeExistingOptionCode(
                        opt?.text || node?.questionText || "",
                        preferredOrder
                      );
                      if (matchedCode) node.questionCode = matchedCode;
                    }
                    return node;
                  })
              : [];
          // When optionsData is the source of truth, drop API children mapped under options
          // (avoids duplicate branches). If there is no optionsData, keep full children.
          const regularChildSources =
            Array.isArray(src.optionsData) && src.optionsData.length > 0
              ? filterChecklistNonOptionChildren(rawChildList, src, questionTypes)
              : rawChildList;

          const regularChildren = regularChildSources
            .filter((c) =>
              String(c?.questionText || c?.QuestionText || c?.text || "").trim()
            )
            .map((child, idx) =>
              toTreePayloadNode(child, resolveDisplayOrder(child, idx + 1))
            );

          // optionsData + node.children can both list the same persisted rows (MC / Radio / etc.).
          // filterChecklistNonOptionChildren misses some cases when QuestionAnsText has commas — then
          // the upsert payload duplicated children and broke parent/child after reload.
          const optionCodesUpper = new Set(
            optionChildren
              .map((c) => String(c?.questionCode || "").trim().toUpperCase())
              .filter(Boolean)
          );
          const regularChildrenDeduped = regularChildren.filter((c) => {
            const cCode = String(c?.questionCode || "").trim().toUpperCase();
            if (!cCode) return true;
            return !optionCodesUpper.has(cCode);
          });
          const mergedChildren = [...optionChildren, ...regularChildrenDeduped];
          let questionAnsText =
            String(src.questionAnsText ?? src.QuestionAnsText ?? "").trim() ||
            (optionChildren.length > 0
              ? optionChildren.map((c) => c.questionText).join(",")
              : "");
          if (isChecklistLevelInputTypeCode(typeCodeNorm)) {
            questionAnsText = String(
              src.levelInputText ?? src.questionAnsText ?? src.QuestionAnsText ?? ""
            ).trim();
          }
          questionAnsText = questionAnsText || null;

          const cond = String(
            src.conditionalAnswerValue ?? src.ConditionalAnswerValue ?? ""
          ).trim();
          const isCond =
            src.isConditional !== undefined
              ? !!src.isConditional
              : src.IsConditional !== undefined
                ? !!src.IsConditional
                : Boolean(cond);

          return {
            questionCode: persistedQuestionCode || null,
            questionTypeCode: typeCodeNorm,
            srTypeCode: srTypeCodeNorm || null,
            parentQuestionCode: null,
            questionText: questionTextNorm,
            questionAnsText: questionAnsText || null,
            displayOrder: ord,
            isMandatory: src.isMandatory !== undefined ? !!src.isMandatory : false,
            isConditional: isCond,
            conditionalAnswerValue: cond || null,
            children: mergedChildren,
          };
        };

        const freshQuestions = finalTreeForSave
          .filter((n) => String(n?.questionText || n?.QuestionText || "").trim())
          .map((node, idx) => toTreePayloadNode(node, idx + 1));

        if (freshQuestions.length === 0) {
          setNewQuestions({});
          setDeletedQuestionCodes(new Set());
          await loadQuestions();
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Template questions refreshed successfully",
            timer: 2000,
            showConfirmButton: false,
          });
          return;
        }

        let treePayload;
        try {
          treePayload = JSON.parse(JSON.stringify(freshQuestions));
        } catch (serErr) {
          console.error("Tree serialization failed:", serErr);
          throw new Error(
            "Could not prepare save data. Refresh the page and try again; if it persists, contact support."
          );
        }
        const saveResponse = await checklistQuestionAPI.upsertQuestionTree({
          templateCode: tcTrim,
          questions: treePayload,
        });
        const isExplicitFailure =
          saveResponse?.success === false ||
          saveResponse?.isSuccess === false ||
          String(saveResponse?.status || "").toLowerCase() === "error";
        if (isExplicitFailure) {
          throw new Error(saveResponse?.message || "Failed to save refreshed checklist tree");
        }

        setNewQuestions({});
        setDeletedQuestionCodes(new Set());
        await loadQuestions();
        Swal.fire({
          icon: "success",
          title: "Success",
          text: `${freshQuestions.length} question(s) saved successfully`,
          timer: 2000,
          showConfirmButton: false,
        });
        return;
      }

      // In edit-mode, when you add a child under an existing saved parent (e.g., A1.3.1 under A1.3),
      // the child's parentQuestionCode is the saved QuestionCode, not a draft-key.
      // The existing buildQuestionTree() only links children where parentQuestionCode matches a draft-key,
      // so these nodes were being ignored and SAVE showed "No new questions to save".
      // Attach-to-parent detection:
      // In edit-mode, new children often have parentQuestionCode set to an existing saved QuestionCODE.
      // The draft tree builder won't include them as roots (because parentQuestionCode is not null),
      // so treat ANY draft node with a parentQuestionCode that doesn't point to another draft key
      // as an "attach under existing parent" candidate.
      const attachToParentKeys = Object.keys(completeNewQuestions).filter((k) => {
        const q = completeNewQuestions[k];
        const p =
          q?.parentQuestionCode || q?.ParentQuestionCode || q?.ParentQuestionCODE || null;
        const parentCode = p ? String(p).trim() : "";
        if (!parentCode) return false;
        // If the parent is another draft node, it will be handled by normal tree save.
        return !completeNewQuestions[parentCode];
      });

      // Resolve a parent identifier (code or text) to a saved QuestionCode.
      // In edit-mode some draft nodes carry the parent as display text (e.g. "Q1", "A1"),
      // so we map it back to the actual QuestionCode before saving.
      const resolveParentQuestionCode = (rawParent) => {
        const raw = String(rawParent ?? "").trim();
        if (!raw) return "";
        const list = Array.isArray(questions) ? questions : [];

        const byCode = list.find((q) => {
          const code = String(q?.questionCode || q?.QuestionCode || q?.QuestionCODE || "").trim();
          return code && code === raw;
        });
        if (byCode) return raw;

        const n = (s) => String(s ?? "").trim().toLowerCase();
        const byText = list.find((q) => {
          const text = q?.questionText || q?.QuestionText || "";
          return n(text) && n(text) === n(raw);
        });
        return String(byText?.questionCode || byText?.QuestionCode || byText?.QuestionCODE || "").trim() || raw;
      };

      // Detect edits/additions under existing saved questions (edit-mode)
      const existingEditedDrafts = Object.entries(newQuestions)
        .filter(([, q]) => {
          if (!q?.__existingParentDraft || !q?.__dirty) return false;
          const dirtyFields = Array.isArray(q?.__dirtyFields) ? q.__dirtyFields : [];
          if (dirtyFields.length === 0) return false;
          return dirtyFields.some(
            (f) => f !== "optionsData" && f !== "__deletedOptionCodes"
          );
        })
        .map(([key, q]) => ({ key, q }));

      // Detect option/child additions under existing saved questions (edit-mode)
      const existingParentDrafts = Object.entries(newQuestions)
        .filter(([, q]) => q?.__existingParentDraft && Array.isArray(q?.optionsData))
        .map(([key, q]) => ({ key, q }));

      // Persist removals for saved options under existing parent questions.
      const deletedOptionCodes = normalizeDeleteCodes(
        Object.values(newQuestions).flatMap((q) =>
          Array.isArray(q?.__deletedOptionCodes) ? q.__deletedOptionCodes : []
        )
      );
      if (deletedOptionCodes.length > 0) {
        const deleteErrors = [];
        for (const code of deletedOptionCodes) {
          try {
            await safeDeleteQuestionByCode(code);
          } catch (delErr) {
            deleteErrors.push(
              `${code}: ${delErr?.response?.data?.message || delErr?.response?.data?.Message || delErr.message || "delete failed"}`
            );
          }
        }
        if (deleteErrors.length > 0) {
          throw new Error(
            `Could not remove ${deleteErrors.length} option(s). Fix and try again, or refresh the page.\n${deleteErrors.slice(0, 8).join("\n")}`
          );
        }
      }

      // Deferred delete for existing questions:
      // remove from backend only when user clicks SAVE.
      const deferredDeletedQuestionCodes = normalizeDeleteCodes([...deletedQuestionCodes]);
      if (deferredDeletedQuestionCodes.length > 0) {
        const deferErrors = [];
        for (const code of deferredDeletedQuestionCodes) {
          try {
            await safeDeleteQuestionByCode(code);
          } catch (delErr) {
            deferErrors.push(
              `${code}: ${delErr?.response?.data?.message || delErr?.response?.data?.Message || delErr.message || "delete failed"}`
            );
          }
        }
        if (deferErrors.length > 0) {
          throw new Error(
            `Could not delete ${deferErrors.length} question(s).\n${deferErrors.slice(0, 8).join("\n")}`
          );
        }
      }

      // Persist direct edits on existing questions (type/text/mandatory/etc.) even when no new question is added.
      const existingByCode = new Map(
        (Array.isArray(questions) ? questions : [])
          .map((item) => {
            const c = getQuestionCodeValue(item);
            return c ? [c.toUpperCase(), item] : null;
          })
          .filter(Boolean)
      );

      const assertApiOk = (resp, actionLabel) => {
        if (
          resp &&
          (resp.success === false ||
            resp.Success === false ||
            String(resp.status || "").toLowerCase() === "error")
        ) {
          throw new Error(
            String(resp.message || resp.Message || `Server rejected ${actionLabel}`)
          );
        }
      };

      let hasExistingFieldEditWork = false;
      const fieldEditErrors = [];
      for (const { key, q } of existingEditedDrafts) {
        const questionCode = String(q?.questionCode || q?.QuestionCode || key || "").trim();
        if (!questionCode || questionCode.startsWith("root_") || questionCode.startsWith("child_")) continue;
        const existing = existingByCode.get(questionCode.toUpperCase());
        const questionText = String(
          q?.questionText || q?.QuestionText || existing?.questionText || existing?.QuestionText || ""
        ).trim();
        const questionTypeCode = String(
          q?.questionTypeCode || q?.QuestionTypeCode || existing?.questionTypeCode || existing?.QuestionTypeCode || ""
        ).trim();
        if (!questionText || !questionTypeCode) continue;

        const parentQuestionCode = String(
          q?.parentQuestionCode ||
            q?.ParentQuestionCode ||
            q?.ParentQuestionCODE ||
            existing?.parentQuestionCode ||
            existing?.ParentQuestionCode ||
            existing?.ParentQuestionCODE ||
            ""
        ).trim();
        const questionAnsTextValue = q?.questionAnsText ?? q?.QuestionAnsText ?? existing?.questionAnsText ?? existing?.QuestionAnsText ?? null;
        const conditionalAnswerValueValue =
          q?.conditionalAnswerValue ?? q?.ConditionalAnswerValue ?? existing?.conditionalAnswerValue ?? existing?.ConditionalAnswerValue ?? null;
        const updatePayload = {
          templateCode,
          questionTypeCode,
          parentQuestionCode: parentQuestionCode || null,
          questionText,
          questionAnsText: questionAnsTextValue,
          displayOrder:
            q?.displayOrder ??
            q?.DisplayOrder ??
            existing?.displayOrder ??
            existing?.DisplayOrder ??
            0,
          isMandatory:
            q?.isMandatory !== undefined
              ? !!q.isMandatory
              : existing?.isMandatory !== undefined
                ? !!existing.isMandatory
                : existing?.IsMandatory !== undefined
                  ? !!existing.IsMandatory
                  : true,
          isConditional:
            q?.isConditional !== undefined
              ? !!q.isConditional
              : existing?.isConditional !== undefined
                ? !!existing.isConditional
                : existing?.IsConditional !== undefined
                  ? !!existing.IsConditional
                  : false,
          conditionalAnswerValue: conditionalAnswerValueValue,
        };
        try {
          const updResp = await checklistQuestionAPI.updateQuestion(questionCode, updatePayload);
          assertApiOk(updResp, `update ${questionCode}`);
          hasExistingFieldEditWork = true;
        } catch (updErr) {
          fieldEditErrors.push(
            `${questionCode}: ${updErr?.response?.data?.message || updErr?.response?.data?.Message || updErr.message || "update failed"}`
          );
        }
      }
      if (fieldEditErrors.length > 0) {
        throw new Error(
          `Could not save ${fieldEditErrors.length} question edit(s).\n${fieldEditErrors.slice(0, 8).join("\n")}`
        );
      }

      const collectSavedOptionSiblingGroups = (opts = [], groups = []) => {
        if (!Array.isArray(opts) || opts.length === 0) return groups;
        const savedCodes = opts
          .map((o) => String(o?.id || ""))
          .filter((id) => id.startsWith("opt_saved_"))
          .map((id) => id.replace("opt_saved_", "").trim())
          .filter(Boolean);
        if (savedCodes.length === opts.length && savedCodes.length > 1) {
          groups.push(savedCodes);
        }
        opts.forEach((o) => collectSavedOptionSiblingGroups(o?.children || [], groups));
        return groups;
      };

      let hasOptionReorderWork = false;
      for (const { q } of existingParentDrafts) {
        const groups = collectSavedOptionSiblingGroups(
          Array.isArray(q?.optionsData) ? q.optionsData : []
        );
        for (const groupCodes of groups) {
          for (let idx = 0; idx < groupCodes.length; idx += 1) {
            const code = groupCodes[idx];
            const existing = existingByCode.get(String(code).toUpperCase());
            if (!existing) continue;
            const currentOrder = Number(existing?.displayOrder ?? existing?.DisplayOrder ?? 0);
            const nextOrder = idx + 1;
            if (currentOrder === nextOrder) continue;
            try {
              const resp = await checklistQuestionAPI.updateQuestion(
                code,
                buildUpdatePayloadFromQuestion(existing, nextOrder)
              );
              assertApiOk(resp, `reorder ${code}`);
              hasOptionReorderWork = true;
            } catch (reErr) {
              throw new Error(
                `Could not save option order for ${code}: ${reErr?.response?.data?.message || reErr?.response?.data?.Message || reErr.message || reErr}`
              );
            }
          }
        }
      }

      const hasNewOptionWork = existingParentDrafts.some(({ q }) => {
        const opts = Array.isArray(q.optionsData) ? q.optionsData : [];
        const hasNewOption = opts.some((o) => !(String(o?.id || "").startsWith("opt_saved_")));
        const hasNewNested = opts.some((o) => {
          const saved = String(o?.id || "").startsWith("opt_saved_");
          if (!saved) return false;
          const kids = Array.isArray(o.children) ? o.children : [];
          const containsNew = (arr) =>
            arr.some((c) => {
              const id = String(c?.id || "");
              if (!id.startsWith("opt_saved_")) return true;
              return Array.isArray(c.children) && containsNew(c.children);
            });
          return kids.length > 0 && containsNew(kids);
        });
        return hasNewOption || hasNewNested;
      });

      // If there are new questions to save, save them using createQuestionTree
      // First, append any new children that should attach under an existing saved parent
      // (example: A1.3.1/A1.3.2 under saved A1.3).
      let hasNewAttachChildWork = false;
      if (attachToParentKeys.length > 0) {
        hasNewAttachChildWork = true;

        const mapQuestionType = (typeCode) =>
          mapChecklistQuestionTypeCodeToName(typeCode, questionTypes);

        const buildDraftNodeForKey = (questionKey) => {
          const q = completeNewQuestions[questionKey];
          if (!q) return null;
          const children = buildQuestionTree(completeNewQuestions, questionKey);
          return {
            questionCode: q.questionCode || q.QuestionCode || null,
            questionTypeCode: q.questionTypeCode || q.QuestionTypeCode || "",
            parentQuestionCode:
              q.parentQuestionCode || q.ParentQuestionCode || q.ParentQuestionCODE || null,
            questionText: q.questionText || q.QuestionText || "",
            questionAnsText: q.questionAnsText || q.QuestionAnsText || null,
            optionsData: q.optionsData || null,
            displayOrder: q.displayOrder || q.DisplayOrder || q.index || 0,
            isMandatory: q.isMandatory !== undefined ? q.isMandatory : true,
            isConditional: q.isConditional !== undefined ? q.isConditional : false,
            conditionalAnswerValue: q.conditionalAnswerValue || q.ConditionalAnswerValue || null,
            children,
          };
        };

        const draftNodeToDto = (node, levelNo = 1) => {
          const dto = {
            questionText: node?.questionText || "",
            questionType: mapQuestionType(node?.questionTypeCode),
            levelNo,
            isMandatory: node?.isMandatory !== undefined ? node.isMandatory : true,
            children: [],
          };

          const childNodes = Array.isArray(node?.children) ? node.children : [];
          if (childNodes.length > 0) {
            dto.children = childNodes.map((c) => draftNodeToDto(c, levelNo + 1));
          }

          // If this new child question is option-based and user added options, persist those too
          // by projecting optionsData into children (same as create flow uses).
          const qtName = dto.questionType || "";
          const isOptionBasedType = isChecklistOptionBasedQuestionTypeName(qtName);
          if (isOptionBasedType && Array.isArray(node?.optionsData) && node.optionsData.length > 0) {
            const optionToDto = (opt, lvl) => ({
              questionText: opt?.text || "",
              questionType: mapQuestionType(opt?.typeCode) || "Label",
              levelNo: lvl,
              isMandatory: opt?.isMandatory !== undefined ? opt.isMandatory : true,
              children: Array.isArray(opt?.children)
                ? opt.children.map((c) => optionToDto(c, lvl + 1))
                : [],
            });
            const optionDtos = node.optionsData.map((o) => optionToDto(o, levelNo + 1));
            dto.children = [...optionDtos, ...dto.children];
          }

          return dto;
        };

        for (const key of attachToParentKeys) {
          const q = completeNewQuestions[key];
          const parentCode =
            q?.parentQuestionCode || q?.ParentQuestionCode || q?.ParentQuestionCODE || null;
          const parentQuestionCode = resolveParentQuestionCode(parentCode);
          if (!parentQuestionCode) continue;

          const node = buildDraftNodeForKey(key);
          if (!node) continue;
          const dto = draftNodeToDto(node, 1);
          if (!dto.questionText || !dto.questionText.trim()) continue;
          const attachResp = await checklistQuestionAPI.saveQuestion(dto, tcTrim, parentQuestionCode);
          assertApiOk(attachResp, `attach child under ${parentQuestionCode}`);
        }

        // Remove attach-children from the normal save map so we don't double-save them.
        attachToParentKeys.forEach((k) => {
          delete completeNewQuestions[k];
        });
      }

      if (Object.keys(completeNewQuestions).length > 0) {
        const questionTree = buildQuestionTree(completeNewQuestions);

        if (questionTree.length > 0) {
          // Generate the JSON structure for Greenlam.Questions
          // This may return an array if there are multiple root questions
          const questionDto = convertToQuestionDtoFormat(questionTree);

          if (questionDto) {
              // Save to CHECKLISTQUESTIONS using the new endpoint
              // If questionDto is an array, save each question separately
              // Otherwise, save the single question
              const questionsToSave = Array.isArray(questionDto) ? questionDto : [questionDto];

              console.log("Attempting to save questionDto(s):", questionDto);
              console.log(`Saving ${questionsToSave.length} question(s)`);

              // Save each question separately (the saveQuestion endpoint expects a single QuestionDto)
              for (let i = 0; i < questionsToSave.length; i++) {
                const singleQuestionDto = questionsToSave[i];
                const rootNode = questionTree[i];
                const parentCodeRaw =
                  rootNode?.parentQuestionCode ||
                  rootNode?.ParentQuestionCode ||
                  rootNode?.ParentQuestionCODE ||
                  null;
                // IMPORTANT: if a parentQuestionCode is present, always pass it through.
                // This is required for edit-mode when adding children/options under an existing saved parent.
                // The backend will attach the new node under that parent.
                const parentQuestionCode =
                  parentCodeRaw && String(parentCodeRaw).trim()
                    ? resolveParentQuestionCode(parentCodeRaw)
                    : null;
                console.log(`Saving question ${i + 1} of ${questionsToSave.length}:`, singleQuestionDto);
                const saveResponse = await checklistQuestionAPI.saveQuestion(
                  singleQuestionDto,
                  tcTrim,
                  parentQuestionCode
                );
                assertApiOk(saveResponse, `save new question ${i + 1}`);
              }

              // Clear newQuestions after successful save
              setNewQuestions({});

              // Reload questions to refresh the tree
              await loadQuestions();

              Swal.fire({
                icon: "success",
                title: "Success",
                text: `${questionsToSave.length} question(s) saved successfully`,
                timer: 2000,
                showConfirmButton: false,
              });
          }
        }
      }

      // Append-only save for new options / option-children under existing saved questions
      if (hasNewOptionWork) {
        const mapQuestionType = (typeCode) =>
          mapChecklistQuestionTypeCodeToName(typeCode, questionTypes);

        const optionToDto = (opt, levelNo = 1) => {
          const dto = {
            questionText: opt?.text || "",
            questionType: mapQuestionType(opt?.typeCode),
            levelNo,
            isMandatory: opt?.isMandatory !== undefined ? opt.isMandatory : true,
            children: [],
          };
          const kids = Array.isArray(opt?.children) ? opt.children : [];
          if (kids.length > 0) {
            dto.children = kids.map((c) => optionToDto(c, levelNo + 1));
          }
          return dto;
        };

        // Save only newly added nodes
        for (const { key: parentKey, q } of existingParentDrafts) {
          const parentQuestionCode = parentKey;
          const opts = Array.isArray(q.optionsData) ? q.optionsData : [];

          for (const opt of opts) {
            const optId = String(opt?.id || "");
            const isSavedOpt = optId.startsWith("opt_saved_");

            if (!isSavedOpt) {
              // New option under existing parent question
              const dto = optionToDto(opt, 1);
              if (dto.questionText && dto.questionText.trim()) {
                const optResp = await checklistQuestionAPI.saveQuestion(dto, tcTrim, parentQuestionCode);
                assertApiOk(optResp, `new option under ${parentQuestionCode}`);
              }
              continue;
            }

            // Existing saved option: save only new nested children under that option code
            const savedOptCode = optId.replace("opt_saved_", "").trim();
            if (!savedOptCode) continue;
            const kids = Array.isArray(opt?.children) ? opt.children : [];
            const newKids = kids.filter((c) => !String(c?.id || "").startsWith("opt_saved_"));
            for (const nk of newKids) {
              const dto = optionToDto(nk, 1);
              if (dto.questionText && dto.questionText.trim()) {
                const nkResp = await checklistQuestionAPI.saveQuestion(dto, tcTrim, savedOptCode);
                assertApiOk(nkResp, `nested option under ${savedOptCode}`);
              }
            }
          }
        }
      }

      // Reload questions to refresh the tree (only if something was saved)
      if (
        Object.keys(completeNewQuestions).length > 0 ||
        hasNewOptionWork ||
        hasOptionReorderWork ||
        hasNewAttachChildWork ||
        hasExistingFieldEditWork ||
        deferredDeletedQuestionCodes.length > 0
      ) {
        setNewQuestions({});
        setDeletedQuestionCodes(new Set());
        await loadQuestions();
        setHasRecentReorderSaved(false);
      } else {
        Swal.fire({
          icon: hasRecentReorderSaved ? "success" : "info",
          title: hasRecentReorderSaved ? "Success" : "Info",
          text: hasRecentReorderSaved
            ? "Question reordering has been saved successfully"
            : "No new questions to save",
          timer: 1800,
          showConfirmButton: false,
        });
        if (hasRecentReorderSaved) setHasRecentReorderSaved(false);
      }
    } catch (error) {
      console.error("Error saving questions:", error);
      try {
        await loadQuestions();
      } catch (reloadErr) {
        console.warn("Reload after save error failed:", reloadErr);
      }
      const apiBody = error?.response?.data;
      const apiMsg =
        (apiBody && (apiBody.message || apiBody.Message || apiBody.title)) ||
        (typeof apiBody === "string" ? apiBody : "") ||
        "";
      Swal.fire({
        icon: "error",
        title: "Error",
        text: String(apiMsg || error.message || "Failed to save questions"),
        confirmButtonText: "OK",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!templateCode?.trim()) {
    return (
      <div className="question-tree-container">
        <div className="alert alert-warning m-3" role="alert">
          <strong>Template required.</strong> Open this page from Checklist Templates with a template code in the URL
          (e.g. <code>?templateCode=...</code>), or go back to select a template.
        </div>
        <div className="m-3">
          <button
            type="button"
            className="btn btn-primary funbtns"
            onClick={() => navigate("/checklist-templates")}
          >
            Go to Checklist Templates
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="question-tree-container">
      <nav aria-label="breadcrumb" className="breadcrumb-custom">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a
              onClick={() =>
                navigate("/checklist-templates", {
                  state: { fromChecklistTree: true, templateCode },
                })
              }
              style={{ cursor: "pointer" }}
            >
              Checklist Templates
            </a>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            {template?.templateName || templateCode} - Questions
          </li>
        </ol>
      </nav>

      <div className="page-header">
        <h1 className="page-title">Question Management</h1>
        <button
          type="button"
          className="btn btn-save-user funbtns checklist-tree-footer-btn checklist-tree-back-btn"
          onClick={() =>
            navigate("/checklist-templates", {
              state: { fromChecklistTree: true, templateCode },
            })
          }
        >
          <ArrowBackIcon className="checklist-tree-btn-icon" fontSize="small" />
          BACK
        </button>
      </div>

      {isCopyDraft && (
        <div
          className="alert alert-info d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3"
          role="status"
        >
          <span>
            Copy draft — template <strong>{templateCode}</strong> (from{" "}
            <strong>{copyFromTemplateCode}</strong>). Questions are not saved until you click Save.
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() =>
              navigate(`/checklist-templates/edit/${encodeURIComponent(templateCode)}`, {
                state: { isCopyDraft: true, copyFromTemplateCode },
              })
            }
          >
            Edit template header
          </button>
        </div>
      )}

      {/* Questions Container */}
      <div className="questions-paper d-flex flex-column">
        {/* New Root Questions */}
        <div style={{ order: 2 }}>
          {Object.keys(newQuestions)
          .filter((key) => {
            // Show all root questions (first question can be shown without type)
            return key.startsWith("root_");
          })
          .map((key) => {
            const newQ = newQuestions[key];
            const parentKey = newQ.questionCode || newQ.QuestionCode || key;
            const hasNewChild = newQuestions[`child_${parentKey}`];

            return (
              <div key={key}>
                <EditableQuestion
                  question={newQ}
                  questionTypes={questionTypes}
                  srTypes={srTypes}
                  level={0}
                  templateCode={templateCode}
                  isNew={true}
                  questionKey={key}
                  newQuestions={newQuestions}
                  onNewQuestionsUpdate={setNewQuestions}
                  onSave={() => {
                    const updated = { ...newQuestions };
                    delete updated[key];
                    setNewQuestions(updated);
                    loadQuestions();
                  }}
                  onDelete={() => {
                    const updated = { ...newQuestions };
                    delete updated[key];
                    // Also remove any children of this question
                    Object.keys(updated).forEach(k => {
                      if (k.startsWith(`child_${key}`) || k.startsWith(`option_child_${key}_`)) {
                        delete updated[k];
                      }
                    });
                    setNewQuestions(updated);
                  }}
                  onAddChild={(parent) => {
                    // Allow adding child questions to new parent questions
                    // For new questions, use the key as identifier
                    // Generate a unique child key to allow multiple children
                    const parentKey = parent.questionCode || parent.QuestionCode || key;
                    
                    // Validate parent has a type selected before allowing child questions (except for Label and Special Text)
                    // First check newQuestions, then check parent object, then check the current question (newQ)
                    const parentQuestion = newQuestions[parentKey] || newQuestions[key] || parent || newQ;
                    const parentTypeCode = parentQuestion?.questionTypeCode || parentQuestion?.QuestionTypeCode || parent?.questionTypeCode || parent?.QuestionTypeCode || newQ?.questionTypeCode || newQ?.QuestionTypeCode;
                    
                    // Allow child questions for Label and Special Text even without question type
                    if ((!parentTypeCode || parentTypeCode.trim() === "") && !isParentLabelOrSpecialText(parentQuestion, parentTypeCode)) {
                      Swal.fire({
                        icon: "warning",
                        title: "Validation Error",
                        text: "Please select a question type for the parent question before adding child questions",
                        confirmButtonText: "OK"
                      });
                      return;
                    }
                    
                    // Validate parent has mandatory dropdown selected
                    const parentIsMandatory = parentQuestion?.isMandatory !== undefined ? parentQuestion.isMandatory : 
                                             (parent?.isMandatory !== undefined ? parent.isMandatory : 
                                             (newQ?.isMandatory !== undefined ? newQ.isMandatory : undefined));
                    if (parentIsMandatory === undefined) {
                      Swal.fire({
                        icon: "warning",
                        title: "Validation Error",
                        text: "Please select mandatory option for the parent question before adding child questions",
                        confirmButtonText: "OK"
                      });
                      return;
                    }
                    
                    // Check if there are existing child questions without types
                    const existingChildKeys = Object.keys(newQuestions).filter(k => {
                      return k.startsWith(`child_${parentKey}_`) || k === `child_${parentKey}`;
                    });
                    
                    if (existingChildKeys.length > 0) {
                      const childQuestionsWithoutType = existingChildKeys.some((childKey) => {
                        const childQuestion = newQuestions[childKey];
                        const hasType = !!(childQuestion?.questionTypeCode || childQuestion?.QuestionTypeCode);
                        return !hasType;
                      });
                      
                      if (childQuestionsWithoutType) {
                        Swal.fire({
                          icon: "warning",
                          title: "Validation Error",
                          text: "Please select a question type for all existing child questions before adding new child questions",
                          confirmButtonText: "OK"
                        });
                        return;
                      }
                      
                      // Check if there are existing child questions without mandatory dropdown
                      const childQuestionsWithoutMandatory = existingChildKeys.some((childKey) => {
                        const childQuestion = newQuestions[childKey];
                        return childQuestion?.isMandatory === undefined;
                      });
                      
                      if (childQuestionsWithoutMandatory) {
                        Swal.fire({
                          icon: "warning",
                          title: "Validation Error",
                          text: "Please select mandatory option for all existing child questions before adding new child questions",
                          confirmButtonText: "OK"
                        });
                        return;
                      }
                    }
                    
                    const timestamp = Date.now();
                    const randomId = Math.random().toString(36).substr(2, 9);
                    const childKey = `child_${parentKey}_${timestamp}_${randomId}`;
                    console.log(`[onAddChild - Root] Adding child question:`, {
                      parentKey,
                      childKey,
                      parent,
                      key,
                      timestamp,
                      randomId
                    });
                    setNewQuestions(prev => {
                      const updated = {
                        ...prev,
                        [childKey]: buildChecklistNewQuestionDraft(questionTypes, {
                          parentQuestionCode: parentKey,
                          level: 1,
                          isMandatory: true,
                        }),
                      };
                      console.log(`[onAddChild - Root] Updated newQuestions keys:`, Object.keys(updated));
                      console.log(`[onAddChild - Root] Child question added:`, updated[childKey]);
                      return updated;
                    });
                  }}
                  parentIndex={newQ.index}
                  node={newQ}
                  nestedChildren={(() => {
                    const childKeys = Object.keys(newQuestions).filter((k) => {
                      if (!(k.startsWith(`child_${parentKey}_`) || k === `child_${parentKey}`)) return false;
                      const question = newQuestions[k];
                      return !!(question.questionTypeCode || question.QuestionTypeCode);
                    });
                    if (childKeys.length === 0) return null;
                    return (
                      <>
                        {childKeys.map((childKey, idx) => {
                          const childQuestion = newQuestions[childKey];
                          return (
                            <div key={childKey}>
                              <EditableQuestion
                                question={childQuestion}
                                questionTypes={questionTypes}
                                srTypes={srTypes}
                                level={1}
                                templateCode={templateCode}
                                isNew={true}
                                questionKey={childKey}
                                newQuestions={newQuestions}
                                onNewQuestionsUpdate={setNewQuestions}
                                onSave={() => {
                                  const updated = { ...newQuestions };
                                  delete updated[childKey];
                                  setNewQuestions(updated);
                                  loadQuestions();
                                }}
                                onDelete={() => {
                                  const updated = { ...newQuestions };
                                  delete updated[childKey];
                                  Object.keys(updated).forEach((k) => {
                                    if (k.startsWith(`child_${childKey}`) || k.startsWith(`option_child_${childKey}_`)) {
                                      delete updated[k];
                                    }
                                  });
                                  setNewQuestions(updated);
                                }}
                                onAddChild={(childParent) => {
                                  const childParentKey = childParent.questionCode || childParent.QuestionCode || childKey;
                                  const childParentQuestion = newQuestions[childParentKey] || newQuestions[childKey] || childParent || childQuestion;
                                  const childParentTypeCode = childParentQuestion?.questionTypeCode || childParentQuestion?.QuestionTypeCode || childParent?.questionTypeCode || childParent?.QuestionTypeCode || childQuestion?.questionTypeCode || childQuestion?.QuestionTypeCode;
                                  if ((!childParentTypeCode || childParentTypeCode.trim() === "") && !isParentLabelOrSpecialText(childParentQuestion, childParentTypeCode)) {
                                    Swal.fire({
                                      icon: "warning",
                                      title: "Validation Error",
                                      text: "Please select a question type for the parent question before adding child questions",
                                      confirmButtonText: "OK",
                                    });
                                    return;
                                  }
                                  const childParentIsMandatory = childParentQuestion?.isMandatory !== undefined ? childParentQuestion.isMandatory : (childParent?.isMandatory !== undefined ? childParent.isMandatory : (childQuestion?.isMandatory !== undefined ? childQuestion.isMandatory : undefined));
                                  if (childParentIsMandatory === undefined) {
                                    Swal.fire({
                                      icon: "warning",
                                      title: "Validation Error",
                                      text: "Please select mandatory option for the parent question before adding child questions",
                                      confirmButtonText: "OK",
                                    });
                                    return;
                                  }
                                  const existingGrandChildKeys = Object.keys(newQuestions).filter((k) => k.startsWith(`child_${childParentKey}_`) || k === `child_${childParentKey}`);
                                  if (existingGrandChildKeys.length > 0) {
                                    const grandChildQuestionsWithoutType = existingGrandChildKeys.some((grandChildKey) => {
                                      const grandChildQuestion = newQuestions[grandChildKey];
                                      return !grandChildQuestion?.questionTypeCode && !grandChildQuestion?.QuestionTypeCode;
                                    });
                                    if (grandChildQuestionsWithoutType) {
                                      Swal.fire({
                                        icon: "warning",
                                        title: "Validation Error",
                                        text: "Please select a question type for all existing child questions before adding new child questions",
                                        confirmButtonText: "OK",
                                      });
                                      return;
                                    }
                                    const grandChildQuestionsWithoutMandatory = existingGrandChildKeys.some((grandChildKey) => newQuestions[grandChildKey]?.isMandatory === undefined);
                                    if (grandChildQuestionsWithoutMandatory) {
                                      Swal.fire({
                                        icon: "warning",
                                        title: "Validation Error",
                                        text: "Please select mandatory option for all existing child questions before adding new child questions",
                                        confirmButtonText: "OK",
                                      });
                                      return;
                                    }
                                  }
                                  const timestamp = Date.now();
                                  const randomId = Math.random().toString(36).substr(2, 9);
                                  const grandChildKey = `child_${childParentKey}_${timestamp}_${randomId}`;
                                  setNewQuestions((prev) => ({
                                    ...prev,
                                    [grandChildKey]: buildChecklistNewQuestionDraft(questionTypes, {
                                      parentQuestionCode: childParentKey,
                                      level: 2,
                                    }),
                                  }));
                                }}
                                parentIndex={newQ.index}
                                childIndex={idx + 1}
                                node={childQuestion}
                              />
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                />
              </div>
            );
          })}
        </div>

        {/* Existing Questions - Render same as new questions (exact same structure, validation, and buttons) */}
        <div style={{ order: 1 }}>
          {questionTree.length > 0 && (
          questionTree
          .filter((node) => !deletedQuestionCodes.has(getQuestionCodeValue(node)))
          .map((node, index) => {
            const nodeKey = node.questionCode || node.QuestionCode;
            const parentKey = nodeKey;
            
            // Render children exactly like new questions - with same structure and validation
            // But filter out options (they're handled inside EditableQuestion)
            const renderChildrenFlat = (children, parentLevel = 0, parentIndexNum = null, parentKeyForChildren = null, parentQuestion = null) => {
              if (!children || children.length === 0) return null;
              const nonOptionChildren = filterChecklistNonOptionChildren(children, parentQuestion, questionTypes)
                .filter((child) => !deletedQuestionCodes.has(getQuestionCodeValue(child)));
              if (nonOptionChildren.length === 0) return null;
              return (
                <>
                  {nonOptionChildren.map((child, childIndex) => {
                    const childKey = child.questionCode || child.QuestionCode || `child_${parentKeyForChildren || nodeKey}_${childIndex}`;
                    const childQuestion = child;
                    const grandNested = renderChildrenFlat(
                      child.children,
                      parentLevel + 1,
                      parentIndexNum,
                      childKey,
                      childQuestion
                    );
                    return (
                      <div key={childKey}>
                        <EditableQuestion
                          question={childQuestion}
                          questionTypes={questionTypes}
                          srTypes={srTypes}
                          level={parentLevel + 1}
                          templateCode={templateCode}
                          isNew={false}
                          parentIndex={parentIndexNum}
                          childIndex={childIndex + 1}
                          questionKey={childKey}
                          newQuestions={newQuestions}
                          onNewQuestionsUpdate={setNewQuestions}
                          onSave={() => {
                            const updated = { ...newQuestions };
                            delete updated[childKey];
                            setNewQuestions(updated);
                            loadQuestions();
                          }}
                          onDelete={() => {
                            const removedCodes = collectQuestionCodesRecursively(childQuestion);
                            if (removedCodes.length > 0) {
                              setDeletedQuestionCodes((prev) => {
                                const next = new Set(prev);
                                removedCodes.forEach((code) => next.add(code));
                                return next;
                              });
                            }
                            const updated = { ...newQuestions };
                            delete updated[childKey];
                            Object.keys(updated).forEach((k) => {
                              if (k.startsWith(`child_${childKey}`) || k.startsWith(`option_child_${childKey}_`)) {
                                delete updated[k];
                              }
                            });
                            setNewQuestions(updated);
                          }}
                          onAddChild={(childParent) => {
                            const childParentKey = childParent.questionCode || childParent.QuestionCode || childKey;
                            const childParentQuestion = newQuestions[childParentKey] || newQuestions[childKey] || childParent || childQuestion;
                            const childParentTypeCode = childParentQuestion?.questionTypeCode || childParentQuestion?.QuestionTypeCode || childParent?.questionTypeCode || childParent?.QuestionTypeCode || childQuestion?.questionTypeCode || childQuestion?.QuestionTypeCode;
                            if ((!childParentTypeCode || childParentTypeCode.trim() === "") && !isParentLabelOrSpecialText(childParentQuestion, childParentTypeCode)) {
                              Swal.fire({
                                icon: "warning",
                                title: "Validation Error",
                                text: "Please select a question type for the parent question before adding child questions",
                                confirmButtonText: "OK",
                              });
                              return;
                            }
                            const childParentIsMandatory = childParentQuestion?.isMandatory !== undefined ? childParentQuestion.isMandatory : (childParent?.isMandatory !== undefined ? childParent.isMandatory : (childQuestion?.isMandatory !== undefined ? childQuestion.isMandatory : undefined));
                            if (childParentIsMandatory === undefined) {
                              Swal.fire({
                                icon: "warning",
                                title: "Validation Error",
                                text: "Please select mandatory option for the parent question before adding child questions",
                                confirmButtonText: "OK",
                              });
                              return;
                            }
                            const existingGrandChildKeys = Object.keys(newQuestions).filter((k) => k.startsWith(`child_${childParentKey}_`) || k === `child_${childParentKey}`);
                            if (existingGrandChildKeys.length > 0) {
                              const grandChildQuestionsWithoutType = existingGrandChildKeys.some((grandChildKey) => {
                                const grandChildQuestion = newQuestions[grandChildKey];
                                return !grandChildQuestion?.questionTypeCode && !grandChildQuestion?.QuestionTypeCode;
                              });
                              if (grandChildQuestionsWithoutType) {
                                Swal.fire({
                                  icon: "warning",
                                  title: "Validation Error",
                                  text: "Please select a question type for all existing child questions before adding new child questions",
                                  confirmButtonText: "OK",
                                });
                                return;
                              }
                              const grandChildQuestionsWithoutMandatory = existingGrandChildKeys.some((grandChildKey) => newQuestions[grandChildKey]?.isMandatory === undefined);
                              if (grandChildQuestionsWithoutMandatory) {
                                Swal.fire({
                                  icon: "warning",
                                  title: "Validation Error",
                                  text: "Please select mandatory option for all existing child questions before adding new child questions",
                                  confirmButtonText: "OK",
                                });
                                return;
                              }
                            }
                            const timestamp = Date.now();
                            const randomId = Math.random().toString(36).substr(2, 9);
                            const newChildKey = `child_${childParentKey}_${timestamp}_${randomId}`;
                            setNewQuestions((prev) => ({
                              ...prev,
                              [newChildKey]: buildChecklistNewQuestionDraft(questionTypes, {
                                parentQuestionCode: childParentKey,
                                level: parentLevel + 2,
                              }),
                            }));
                          }}
                          node={childQuestion}
                          nestedChildren={grandNested}
                          draggable={Boolean(getQuestionCodeValue(childQuestion))}
                          onDragStartBlock={handleDragStartQuestion}
                          onDragOverBlock={handleDragOverQuestion}
                          onDropBlock={handleDropQuestion}
                          isDragOver={dragOverCode === getQuestionCodeValue(childQuestion)}
                        />
                      </div>
                    );
                  })}
                </>
              );
            };

            const nonOptionChildren = filterChecklistNonOptionChildren(node.children || [], node, questionTypes);
            const nestedFromApi = renderChildrenFlat(nonOptionChildren, 0, index + 1, parentKey, node);
            const newChildKeys = Object.keys(newQuestions).filter((k) => {
              if (!(k.startsWith(`child_${parentKey}_`) || k === `child_${parentKey}`)) return false;
              const question = newQuestions[k];
              return !!(question.questionTypeCode || question.QuestionTypeCode);
            });
            const nestedFromDraft =
              newChildKeys.length > 0 ? (
                <>
                  {newChildKeys.map((childKey, idx) => {
                    const childQuestion = newQuestions[childKey];
                    return (
                      <div key={childKey}>
                        <EditableQuestion
                          question={childQuestion}
                          questionTypes={questionTypes}
                          srTypes={srTypes}
                          level={1}
                          templateCode={templateCode}
                          isNew={true}
                          questionKey={childKey}
                          newQuestions={newQuestions}
                          onNewQuestionsUpdate={setNewQuestions}
                          onSave={() => {
                            const updated = { ...newQuestions };
                            delete updated[childKey];
                            setNewQuestions(updated);
                            loadQuestions();
                          }}
                          onDelete={() => {
                            const updated = { ...newQuestions };
                            delete updated[childKey];
                            Object.keys(updated).forEach((k) => {
                              if (k.startsWith(`child_${childKey}`) || k.startsWith(`option_child_${childKey}_`)) {
                                delete updated[k];
                              }
                            });
                            setNewQuestions(updated);
                          }}
                          onAddChild={(childParent) => {
                            const childParentKey = childParent.questionCode || childParent.QuestionCode || childKey;
                            const childParentQuestion = newQuestions[childParentKey] || newQuestions[childKey] || childParent || childQuestion;
                            const childParentTypeCode = childParentQuestion?.questionTypeCode || childParentQuestion?.QuestionTypeCode || childParent?.questionTypeCode || childParent?.QuestionTypeCode || childQuestion?.questionTypeCode || childQuestion?.QuestionTypeCode;
                            if ((!childParentTypeCode || childParentTypeCode.trim() === "") && !isParentLabelOrSpecialText(childParentQuestion, childParentTypeCode)) {
                              Swal.fire({
                                icon: "warning",
                                title: "Validation Error",
                                text: "Please select a question type for the parent question before adding child questions",
                                confirmButtonText: "OK",
                              });
                              return;
                            }
                            const childParentIsMandatory = childParentQuestion?.isMandatory !== undefined ? childParentQuestion.isMandatory : (childParent?.isMandatory !== undefined ? childParent.isMandatory : (childQuestion?.isMandatory !== undefined ? childQuestion.isMandatory : undefined));
                            if (childParentIsMandatory === undefined) {
                              Swal.fire({
                                icon: "warning",
                                title: "Validation Error",
                                text: "Please select mandatory option for the parent question before adding child questions",
                                confirmButtonText: "OK",
                              });
                              return;
                            }
                            const existingGrandChildKeys = Object.keys(newQuestions).filter((k) => k.startsWith(`child_${childParentKey}_`) || k === `child_${childParentKey}`);
                            if (existingGrandChildKeys.length > 0) {
                              const grandChildQuestionsWithoutType = existingGrandChildKeys.some((grandChildKey) => {
                                const grandChildQuestion = newQuestions[grandChildKey];
                                return !grandChildQuestion?.questionTypeCode && !grandChildQuestion?.QuestionTypeCode;
                              });
                              if (grandChildQuestionsWithoutType) {
                                Swal.fire({
                                  icon: "warning",
                                  title: "Validation Error",
                                  text: "Please select a question type for all existing child questions before adding new child questions",
                                  confirmButtonText: "OK",
                                });
                                return;
                              }
                              const grandChildQuestionsWithoutMandatory = existingGrandChildKeys.some((grandChildKey) => newQuestions[grandChildKey]?.isMandatory === undefined);
                              if (grandChildQuestionsWithoutMandatory) {
                                Swal.fire({
                                  icon: "warning",
                                  title: "Validation Error",
                                  text: "Please select mandatory option for all existing child questions before adding new child questions",
                                  confirmButtonText: "OK",
                                });
                                return;
                              }
                            }
                            const timestamp = Date.now();
                            const randomId = Math.random().toString(36).substr(2, 9);
                            const newChildKey = `child_${childParentKey}_${timestamp}_${randomId}`;
                            setNewQuestions((prev) => ({
                              ...prev,
                              [newChildKey]: buildChecklistNewQuestionDraft(questionTypes, {
                                parentQuestionCode: childParentKey,
                                level: 2,
                              }),
                            }));
                          }}
                          parentIndex={index + 1}
                          childIndex={idx + 1}
                          node={childQuestion}
                        />
                      </div>
                    );
                  })}
                </>
              ) : null;
            const nestedChildrenCombined =
              nestedFromApi || nestedFromDraft ? (
                <>
                  {nestedFromApi}
                  {nestedFromDraft}
                </>
              ) : null;

            return (
              <div key={nodeKey}>
                <EditableQuestion
                  question={node}
                  questionTypes={questionTypes}
                  srTypes={srTypes}
                  level={0}
                  templateCode={templateCode}
                  isNew={false}
                  parentIndex={index + 1}
                  questionKey={nodeKey}
                  newQuestions={newQuestions}
                  onNewQuestionsUpdate={setNewQuestions}
                  onSave={() => {
                    const updated = { ...newQuestions };
                    delete updated[nodeKey];
                    setNewQuestions(updated);
                    loadQuestions();
                  }}
                  onDelete={() => {
                    const removedCodes = collectQuestionCodesRecursively(node);
                    if (removedCodes.length > 0) {
                      setDeletedQuestionCodes((prev) => {
                        const next = new Set(prev);
                        removedCodes.forEach((code) => next.add(code));
                        return next;
                      });
                    }
                    const updated = { ...newQuestions };
                    delete updated[nodeKey];
                    Object.keys(updated).forEach((k) => {
                      if (k.startsWith(`child_${nodeKey}`) || k.startsWith(`option_child_${nodeKey}_`)) {
                        delete updated[k];
                      }
                    });
                    setNewQuestions(updated);
                  }}
                  onAddChild={(parent) => {
                    const parentKeyForChild = parent.questionCode || parent.QuestionCode || nodeKey;
                    const parentQuestion = newQuestions[parentKeyForChild] || newQuestions[nodeKey] || parent || node;
                    const parentTypeCode = parentQuestion?.questionTypeCode || parentQuestion?.QuestionTypeCode || parent?.questionTypeCode || parent?.QuestionTypeCode || node?.questionTypeCode || node?.QuestionTypeCode;
                    if ((!parentTypeCode || parentTypeCode.trim() === "") && !isParentLabelOrSpecialText(parentQuestion, parentTypeCode)) {
                      Swal.fire({
                        icon: "warning",
                        title: "Validation Error",
                        text: "Please select a question type for the parent question before adding child questions",
                        confirmButtonText: "OK",
                      });
                      return;
                    }
                    const parentIsMandatory = parentQuestion?.isMandatory !== undefined ? parentQuestion.isMandatory : (parent?.isMandatory !== undefined ? parent.isMandatory : (node?.isMandatory !== undefined ? node.isMandatory : undefined));
                    if (parentIsMandatory === undefined) {
                      Swal.fire({
                        icon: "warning",
                        title: "Validation Error",
                        text: "Please select mandatory option for the parent question before adding child questions",
                        confirmButtonText: "OK",
                      });
                      return;
                    }
                    const existingChildKeys = Object.keys(newQuestions).filter((k) => k.startsWith(`child_${parentKeyForChild}_`) || k === `child_${parentKeyForChild}`);
                    if (existingChildKeys.length > 0) {
                      const childQuestionsWithoutType = existingChildKeys.some((ck) => {
                        const cq = newQuestions[ck];
                        return !cq?.questionTypeCode && !cq?.QuestionTypeCode;
                      });
                      if (childQuestionsWithoutType) {
                        Swal.fire({
                          icon: "warning",
                          title: "Validation Error",
                          text: "Please select a question type for all existing child questions before adding new child questions",
                          confirmButtonText: "OK",
                        });
                        return;
                      }
                      const childQuestionsWithoutMandatory = existingChildKeys.some((ck) => newQuestions[ck]?.isMandatory === undefined);
                      if (childQuestionsWithoutMandatory) {
                        Swal.fire({
                          icon: "warning",
                          title: "Validation Error",
                          text: "Please select mandatory option for all existing child questions before adding new child questions",
                          confirmButtonText: "OK",
                        });
                        return;
                      }
                    }
                    const timestamp = Date.now();
                    const randomId = Math.random().toString(36).substr(2, 9);
                    const childKey = `child_${parentKeyForChild}_${timestamp}_${randomId}`;
                    setNewQuestions((prev) => ({
                      ...prev,
                      [childKey]: buildChecklistNewQuestionDraft(questionTypes, {
                        parentQuestionCode: parentKeyForChild,
                        level: 1,
                        isMandatory: true,
                      }),
                    }));
                  }}
                  node={node}
                  nestedChildren={nestedChildrenCombined}
                  draggable={Boolean(getQuestionCodeValue(node))}
                  onDragStartBlock={handleDragStartQuestion}
                  onDragOverBlock={handleDragOverQuestion}
                  onDropBlock={handleDropQuestion}
                  isDragOver={dragOverCode === getQuestionCodeValue(node)}
                />
              </div>
            );
          })
        )}
        </div>

        {/* Add Root Question Button */}
        {questionTree.length === 0 &&
          Object.keys(newQuestions).filter((key) => key.startsWith("root_")).length === 0 && (
            <div className="empty-state">
              <p className="empty-state-text">No questions found</p>
            </div>
          )}

      </div>

      {/* Action Buttons */}
      <div className="save-button-container d-flex gap-2 justify-content-center">
        <button
          type="button"
          className="btn btn-secondary funbtns"
          onClick={logNewQuestionsState}
          hidden
          style={{ display: "none", backgroundColor: "#6c757d", borderColor: "#6c757d" }}
        >
          DEBUG: Log State
        </button>
        <button
          type="button"
          className="btn btn-save-user funbtns checklist-tree-footer-btn checklist-tree-add-action-btn"
          onClick={handleAddRootQuestion}
        >
          <AddIcon className="checklist-tree-btn-icon" fontSize="small" />
          CLICK TO ADD
        </button>
        <button
          type="button"
          className="btn btn-save-user funbtns checklist-tree-footer-btn"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          <SaveIcon className="checklist-tree-btn-icon" fontSize="small" />
          {isSubmitting ? "SAVING..." : "SAVE"}
        </button>
      </div>
    </div>
  );
};

export default ChecklistQuestionTreeManagement;
